/**
 * English translations for the Conductor UI.
 * Key naming convention: page/component.section.key
 */
const en = {
  observation: {
    "checks_empty": "No check records yet.",
    "stop_quota": "Automatic stop allowance",
    "check_cost": "Check costs",

    "title": "Read-only observation and questions",
    "expand": "Show overview",
    "collapse": "Back to conversation",
    "boundary": "Manual questions in this conversation are read-only: they do not edit code or redispatch the original task. Automatic supervision follows the existing grant.",
    "placeholder": "Ask about progress, evidence, or what remains unverified.",
    "open_source": "Open the original task",
    "loading": "Reading the original task status…",
    "load_failed": "The original task status could not be loaded. This does not mean the task has ended.",
    "no_source": "The linked task is unavailable; its status is unverified.",
    "refresh": "Refresh status",
    "refresh_note": "Refreshing does not call a model. Sent questions incur normal model usage.",
    "controls": "Supervision records and controls",
    "current": "Current work",
    "reported": "Reported results",
    "next": "Next step",
    "status_source": "Source: execution status",
    "report_source": "Source: latest executor report excerpt; not accepted",
    "no_report": "No visible result report yet. The outcome is unverified.",
    "next_answer_question": "Waiting for your answer before continuing.",
    "next_review_approval": "Waiting for you to review the pending proposal.",
    "next_not_reported": "The next step has not been reported. Ask in the read-only discussion.",
    "questions": "What would you like to know?",
    "pause_checks": "Pause checks",
    "resume_checks": "Resume checks",
    "pause_note": "Pausing prevents new checks. Checks already in progress may still incur costs.",
    "pause_interventions_note": "Pausing interventions continues read-only checks and may incur costs. End supervision to stop checks."
},

  long_content: {
    notice: 'Long message ({count} characters). Displaying plain text; the full original is preserved.',
    view: 'View original in pages',
    summary: 'Back to summary',
    copy: 'Copy full original',
    previous: 'Previous',
    next: 'Next',
    page: 'Page {page} / {pages}',
    copied: 'Full original copied',
    copy_failed: 'Copy failed. Allow clipboard access in your browser and retry.',
    truncated: 'Showing only the first {count} characters.',
    paged_notice: 'Long content ({count} characters), shown in pages.',
    page_nav: 'Document pages',
    toc: 'Jump to chapter',
  },

  // ── Common / Shared ──────────────────────────────────────────────
  common: {
    cancel: 'Cancel',
    copy: 'Copy',
    copied: 'Copied!',
    loading: 'Loading...',
    save: 'Save',
    delete: 'Delete',
    remove: 'Remove',
    approve: 'Approve',
    reject: 'Reject',
    export: 'Export',
    error: 'Error',
    or: 'or',
    from: 'from',
    on: 'on',
    saving: 'Saving...',
    conductor: 'Conductor',
    wecom: 'WeCom',
    web: 'Web',
    close: 'Close',
    close_aria: 'Close',
    unlimited: 'Unlimited',
  },

  // ── Login Page ───────────────────────────────────────────────────
  login: {
    title: 'Conductor',
    subtitle: 'AI-Powered Development Assistant',
    username_label: 'Username / Email / Phone',
    password_label: 'Password',
    username_placeholder: 'Username / Email / Phone',
    password_placeholder: 'Enter your password',
    signing_in: 'Signing in...',
    sign_in: 'Sign In',
    sign_in_wecom: 'Sign in with WeCom',
    username_required: 'Username is required',
    password_required: 'Password is required',
    wecom_no_account: 'No account linked to this WeCom identity. Please log in with username/password first.',
    wecom_login_failed: 'WeCom login failed: {error}',
    wecom_qr_failed: 'Failed to load WeCom QR login. Please try again.',
    error_invalid_credentials: 'Wrong username or password',
    error_account_locked: 'Account locked due to repeated login failures, please contact your administrator',
    forgot_link: 'Forgot password?',
    register_entry: 'New user? Register',
    register_code_label: 'Invite code',
    register_code_placeholder: 'Enter your invite code',
    register_code_required: 'Invite code is required',
    register_continue: 'Continue',
  },

  // ── Forgot / Reset Password ──────────────────────────────────────
  forgotPassword: {
    title: 'Reset Password',
    subtitle_choose: 'Choose a registered method to receive reset instructions',
    tab_email: 'Email',
    tab_phone: 'Phone',
    back_to_login: '← Back to login',
    email_label: 'Email address',
    email_placeholder: 'you@example.com',
    email_hint: 'We will send a reset link to this email',
    send_link: 'Send reset link',
    phone_subtitle: 'Reset via SMS verification code',
    phone_label: 'Phone number',
    phone_placeholder: 'Enter your registered phone number',
    code_label: 'Verification code',
    code_placeholder: '6-digit code',
    send_code: 'Get code',
    resend_countdown: 'Resend ({seconds}s)',
    new_password_label: 'New password',
    new_password_placeholder: 'At least 8 chars, with letters and digits',
    confirm_password_label: 'Confirm new password',
    confirm_password_placeholder: 'Re-enter the new password',
    submit_phone: 'Reset password',
    email_sent_title: 'Check your email',
    email_sent_sub: 'If the account exists, we have sent reset instructions',
    email_sent_body: 'A reset link has been sent to {email}. Please click it within 30 minutes to finish resetting your password.',
    resend: "Didn't get it? Resend",
    err_email_required: 'Please enter your email address',
    err_phone_required: 'Please enter your phone number',
    err_code_required: 'Please enter the verification code',
    err_password_policy: 'Password must be at least 8 chars and contain both letters and digits',
    err_passwords_mismatch: 'The two passwords do not match',
    generic_error: 'Operation failed, please try again later',
    reset_title: 'Set a new password',
    reset_sub: 'Set a new password for your account',
    reset_hint: 'Mix upper/lower case letters, digits and symbols',
    reset_submit: 'Confirm reset',
    reset_missing_token: 'This reset link is invalid or expired (valid for 30 minutes). Please request a password reset again.',
    reset_request_again: 'Request a new reset link',
    reset_success: 'Password reset. Please log in with your new password',
  },

  // ── Chat Page ────────────────────────────────────────────────────
  chat: {
    // P-COND-DF0BDCAF2C0A ST3: upload failed, composer restored.
    upload_failed_restored: 'Upload failed: {detail}. Your message was put back in the input box — review it and send again.',
    header: {
      conductor: 'Conductor',
      dispatch_approvals: 'Dispatch Approvals',
      collab_approvals: 'Collab Approvals',
      approvals: 'Approvals',
      admin: 'Admin',
      admin_panel: 'Admin Panel',
      logout: 'Logout',
      manage_menu: 'Manage',
      user_manual: 'User Manual',
      skill_center: "Skill Center",
      desktop_download: 'Desktop Client',
    },
    status: {
      connected: 'Connected',
      connecting: 'Connecting...',
      disconnected: 'Disconnected',
      reconnect: 'Reconnect',
    },
    toggle: {
      archived: 'Archived',
      active: 'Active',
    },
    refresh_title: 'Refresh conversations',
    show_active: 'Show active conversations',
    show_archived: 'Show archived conversations',
    no_projects: 'You are not assigned to any project yet. Please contact an admin to add you to a project.',
    toggle_sidebar: 'Toggle sidebar',
    // SP-U2 first-run empty-state guide
    empty_title: 'Welcome! No conversations yet',
    empty_hint: 'Start by creating your first conversation to begin working with your project agent.',
    empty_cta: 'Create your first conversation',
    empty_secondary: 'Tip: invite teammates or complete your collaboration profile in workspace settings.',
    start_new_conversation: 'New Conversation',
    // P-COND-F4E1C7B13B15 ST2 — zero-project guide card (ChatPage main area).
    // Copy mirrors the approved design asset l1-empty-project-guide.html.
    zero_project: {
      title: 'No projects yet',
      sub: 'Every conversation belongs to a project. Create a project first, then you can start conversations inside it.',
      step_1: 'Click "Create project" below and fill in the project name and code location.',
      step_2: 'Pick a workspace mode (dev / collaboration) and a complexity tier — the workspace is generated from them.',
      step_3: 'Open the project once it is created; its project channel posts a getting-started note.',
      cta: 'Create project',
      new_conversation: 'New Conversation',
      hint: 'A project must exist before a conversation can be created.',
    },
    projects_unavailable: {
      title: 'Could not load your projects',
      sub: 'The project list failed to load (one automatic retry already happened). Check your connection and try again.',
      retry: 'Reload',
    },
    collab: {
      welcome_title: 'Welcome to the Collaboration Workspace',
      welcome_text: 'Drop in your documents and describe what you need. The agent helps you draft, organize, and archive your department\u2019s work.',
      tasks_heading: 'Try one of these tasks:',
      task_1: 'Draft a market analysis report',
      task_2: 'Organize and archive department standards',
      task_3: 'Turn meeting notes into an action plan',
      cta: 'Start a collaboration session',
      secondary_hint: 'Collaboration mode: read/write your department\u2019s files, read-only across departments.',
    },
    dev: {
      tasks_heading: 'Try one of these tasks:',
      task_1: 'Dev & test: build features, fix bugs, write tests',
      task_2: 'Product & design: break down requirements, page mockups',
      task_3: 'Ops & deploy: log triage, release & rollback',
    },
    error: {
      unknown: 'Unknown error',
      create_failed: 'Failed to create conversation: {detail}',
      delete_failed: 'Failed to delete conversation: {detail}',
      archive_failed: 'Failed to archive conversation: {detail}',
      unarchive_failed: 'Failed to unarchive conversation: {detail}',
      retry: 'Retry',
      dismiss_aria: 'Dismiss',
    },
    streaming: {
      startup_starting: 'Starting…',
      startup_preparing: 'Preparing task…',
      startup_waiting: 'Waiting for AI response…',
      startup_long: 'Still processing…',
      startup_elapsed: 'Waiting {s}s',
      startup_starting_detail: 'Sending your request. Processing status will appear shortly.',
      startup_preparing_detail: 'Your request is being processed. The first response will appear here.',
      startup_long_detail: 'This is taking longer than expected. You can keep waiting or use Stop below.',
      call_count: "Calls",
      state_done: "Confirmed complete",
      state_error: "Execution failed",
      state_running: "Running",
      state_unknown: "Result unconfirmed",
      reason_cancelled: "Cancelled; result unavailable",
      reason_timeout: "Timed out; result unavailable",
      reason_truncated: "Stream truncated; result unavailable",
      reason_missing_result: "Execution result unavailable",
      exit_code: "Exit code {code}",
      skill_read: "Observed skill read: {name} (file read; completion not certified)",
      skills_unconfirmed: "Skill reading: unconfirmed",
      thinking_aria: 'Agent is thinking',
      // AISS-240 long-task progress line. {m}/{s} = minutes / zero-padded
      // seconds; elapsed_short is used for the sub-minute case.
      elapsed: 'Running {m}m {s}s',
      elapsed_short: 'Running {s}s',
      idle: 'Last activity {m}m {s}s ago',
      idle_short: 'Last activity {s}s ago',
      progress_aria: 'Task in progress',
      // Live reasoning strip (P-COND-257F4A39271B). {n} = reasoning char count.
      reasoning_live: 'Thinking…',
      reasoning_done: 'Reasoning',
      reasoning_chars: ' ({n} chars)',
      reasoning_toggle_aria: 'Toggle reasoning',
      // Thinking hint (P-COND-177E21301650), shown only after the backend's
      // `thinking_status` frame. {s} = whole seconds since that frame.
      thinking_elapsed: 'AI is thinking ({s}s)',
    },
  },

  // ── Stage-0 Comprehension Card (workspace-modes SP-U2) ──────────
  stage0: {
    badge: 'Stage 0',
    title: 'Understanding your existing project',
    body: 'The agent is reverse-comprehending your existing codebase to understand its structure and intent before requirements gathering begins. Review its summary in the conversation. When the understanding looks correct, confirm to continue to requirements.',
    confirm: 'Understanding is correct',
    confirm_hint: 'Advance to Stage 1 (User Requirements)',
    correct: 'Not quite right',
    correct_hint: 'Send the agent corrective feedback in the chat input',
  },

  // ── Conversation List ────────────────────────────────────────────
  conv_list: {
    // NO leading '+' — the glyph is supplied by the component
    // (ConversationList `.l2-new-conv-btn`). Contract Pack: the two must not
    // both carry it, or the button renders "+ + New Conversation".
    new_conversation: 'New Conversation',
    all_projects: 'All Projects',
    back_to_projects: 'Back to all projects',
    // L1 project row (`.proj-card`) — counts and status wording.
    // Both counts carry a {count} placeholder: "6 个项目" and "6 projects"
    // order their parts differently, and building them as `{n} {t(...)}` in
    // the component would hard-code Chinese word order into the TSX.
    projects_count: '{count} projects',
    proj_conv_count: '{count} conversations',
    proj_no_convs: 'No conversations yet',
    proj_status_running: 'Running',
    proj_status_awaiting: 'Needs answer',
    proj_status_dirty: 'Changes',
    proj_status_synced: 'Synced',
    // Hover / a11y description for the `.proj-org` chip. The chip itself shows
    // only the org name (data); on its own it is an unlabelled grey pill, so
    // the title says what the name means.
    proj_org_title: 'Organization: {name}',
    // L2 header workspace-mode chip (`.l2-mode-tag`). Derived from
    // project.mode_paths (AD-63) — only dev / collab exist here. 「统筹」
    // (operation) is a per-CONVERSATION dev_submode and must never appear on a
    // project-level chip.
    mode_dev: 'Dev',
    mode_collab: 'Collab',
    mode_dev_title: 'Dev mode: this project workspace is for branch development',
    mode_collab_title: 'Collab mode: this project workspace is a cross-team knowledge workspace',
    // Read-only favourite indicator on a conversation row (`.conv-fav-heart`,
    // the same heart the filter panel draws). The heart is not a control;
    // favourite/unfavourite lives in the ⋮ menu.
    favorited: 'Favorited',
    project_channel: 'Project Channel',
    project_channel_sub: 'Announcements & notices',
    project_channel_unread: 'Unread announcements',
    search_placeholder: 'Search ID / branch / title / content',
    search_clear: 'Clear search',
    // L2 conversation section heading + the collapsible filter disclosure
    recent_conversations: 'Recent',
    filters_toggle: 'Filters',
    loading: 'Loading conversations...',
    loading_more: 'Loading more...',
    empty_archived: 'No archived conversations',
    empty_active: 'No conversations found',
    // L1 zero-project note (parent-supplied `hasNoProjects`)
    empty_no_projects: 'No projects yet.',
    empty_no_projects_note: 'The project layer offers no "New Conversation" entry — a conversation always belongs to a specific project.',
    new_conversation_preview: 'New conversation',
    branched_from: 'Branched from {title}',
    branched_from_title: 'Go to the source conversation',
    branch_count: '{count} branches',
    supervision_count: '{count} supervisors',
    branch_toggle_title: 'Show / hide branch conversations',
    start_new_title: 'Start a new conversation',
    archive_title: 'Archive conversation',
    restore_title: 'Restore conversation',
    delete_title: 'Delete conversation',
    edit_theme_title: 'Double-click to edit theme',
    delete_confirm: 'Permanently delete this conversation and all its messages? This cannot be undone.',
    archived_badge: 'Archived',
    branch: 'Branch',
    link: 'Conv ID',
    git_status_clean: 'Clean · nothing to commit',
    git_status_pending: 'Uncommitted changes',
    git_status_committed: 'Committed (local) · Plan-3 normal',
    agent_running: 'Running · agent is processing',
    agent_awaiting_user: 'Waiting for your answer · a question needs you',
    favorite_title: 'Favorite conversation',
    unfavorite_title: 'Unfavorite',
    favorite_filter: 'Favorites',
    unknown_project: 'Unknown Project',
    date_filter_label: 'Date range',
    date_all: 'All',
    date_7d: '7 days',
    date_30d: '30 days',
    date_90d: '90 days',
    show_more_convs: 'Show more ({count})',
    show_less_convs: 'Show less',
    view_all_project: 'View all',
    menu_open: 'Actions',
    menu_group_conversation: 'Conversation',
    menu_group_git: 'Git',
    menu_rename: 'Rename theme',
    menu_copy_id: 'Copy conversation ID',
    menu_copy_branch: 'Copy branch name',
    menu_favorite: 'Favorite',
    menu_unfavorite: 'Unfavorite',
    menu_archive: 'Archive',
    menu_unarchive: 'Restore',
    menu_delete: 'Delete',
    menu_push: 'Push',
    menu_merge: 'Merge',
    menu_push_disabled_hint: 'No changes',
    menu_merge_disabled_hint: 'Push first',
    menu_merge_master_hint: 'Push promotes master; no merge',
    menu_branch_new: 'Branch a new conversation from here',
    menu_branch_new_hint: 'Carry this context into a separate task',
    menu_execution: 'View execution status',
    menu_execution_hint: 'See progress, ask why, suggest changes',
  },

  // ── Branch-from-conversation dialog (CS-T9, P-COND-82309B809347) ──
  branch_dialog: {
    title: 'Branch a new conversation from here',
    intro: 'Start another topic with this background.',
    loading: 'Reading the source conversation…',
    creating: 'Creating the branch conversation…',
    background_label: 'Source context',
    source_label: 'Source',
    cutoff_label: 'Context up to',
    cutoff_value: '{time}, latest saved message',
    brings_label: 'Includes',
    brings_value: 'History summary (if any) + recent turns + source reference',
    storage_label: 'Stored',
    storage_value: 'On the server; available on any device',
    pill_ready: 'Context ready',
    pill_empty: 'No history yet',
    pill_missing: 'Summary not included',
    recent_toggle: 'Show recent records ({count})',
    role_user: 'User',
    role_assistant: 'Agent',
    frozen_note: 'The server builds the context at the cutoff when the branch is created; later messages in the source are not added.',
    empty_title: 'The source conversation has no saved messages yet',
    empty_body: 'The new conversation only inherits its settings (project, mode, code branch).',
    denied: 'Cannot read the source conversation; it may have been deleted or access changed. Nothing was created.',
    load_failed: 'Failed to read the source conversation: {detail}',
    retry: 'Retry',
    shared_workspace: 'Both conversations share the same code workspace (same branch). Take care before editing code in parallel.',
    no_autostart: 'Enter the task after creation; the source task is not continued automatically.',
    confirm: 'Create branch conversation',
    confirm_empty: 'Create with settings only',
    cancel: 'Cancel',
    create_failed: 'Create failed: {detail}',
    summary_unavailable_title: 'Branch created · history summary not included',
    summary_omitted: {
      none_available: 'The source has no history summary yet; recent turns and the source reference are included.',
      after_cutoff: 'The existing summary is newer than the cutoff and is not included; recent turns and the source reference are included.',
      targeted_visibility: 'The source group chat has messages visible only to some members, so no history summary is included — only recent turns you can see.',
      read_failed: 'The context could not be read; only the source reference is included. Open the source to review its records.',
    },
    open_branch: 'Open branch conversation',
  },

  // ── Supervision (CS-T11) ───────────────────────────────────────────
  supervision: {
    loading: 'Loading supervision settings…',
    level: { observe: 'Observe', guard: 'Guard', managed: 'Managed correction', extended: 'Extended scheduling' },
    level_desc: {
      observe: 'Review on events and write conclusions; never stop or modify the task.',
      guard: 'Observe, and request a stop under agreed conditions. You decide adjustments and continuation.',
      managed: 'Stop, revise requirements, create a continuation, dispatch, verify, keep supervising.',
      extended: 'Managed correction plus choosing allowed models and read-only research subtasks.',
    },
    action: {
      feedback: 'Compose and send back feedback',
      stop: 'Request stopping the current run (this run only)',
      continue_dispatch: 'Revise requirements and create a continuation conversation',
      verify: 'Verify the result after the continuation ends',
      select_model: 'Choose an allowed model',
      readonly_research: 'Schedule read-only research subtasks',
    },
    action_kind: { feedback: 'Feedback', stop: 'Stop request', continue_dispatch: 'Continuation', verify: 'Verification' },
    action_state: {
      requested: 'Requested',
      stop_requested: 'Stop requested, awaiting confirmation',
      stop_unconfirmed: 'Stop unconfirmed',
      stopped: 'Exit confirmed',
      dispatched: 'Dispatched',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    status: { active: 'Active', paused: 'Auto-intervention paused', ended: 'Ended', revoked: 'Revoked' },
    wake: { events_all: 'Wake on end, failure, waiting for me, or long no-progress', end_or_fail: 'Wake only on end or failure' },
    form: {
      title: 'Authorize a supervisor conversation',
      intro: 'A separate supervisor wakes on selected events; it does not monitor every second.',
      level_label: 'Grant level',
      actions_title: 'Permissions (can be unticked, never beyond the level)',
      always_on: 'View progress, ask, compose feedback',
      auto_continue: 'Continuation skips a second approval (same project, branch and goal)',
      auto_continue_hint: 'Requires approval rights on this project; refused otherwise.',
      auto_continue_needs_continue: 'Tick "Revise requirements and create a continuation conversation" first.',
      gate_forbidden: 'This project requires approval and you have no approval right, so "skip second approval" cannot be enabled. It has been unticked — you can submit again.',
      scope_note: 'Deploys, data changes and similar operations cannot be granted; project rules apply. You cannot grant more than your own rights.',
      max_interventions: 'Max automatic corrections',
      expiry: 'Expires after',
      expiry_hours: '{hours} hours',
      budget: 'Total cost soft cap (USD, supervisor + all continuations)',
      budget_invalid: 'Enter an amount above 0 with at most 6 decimals',
      wake: 'When to wake the supervisor',
      budget_note: 'Based on observable cost; unknown cost counts at the reserved amount. Notification delay may overshoot slightly — not a hard cap.',
      submit: 'Enable {level}',
      submitting: 'Enabling…',
      cancel: 'Cancel',
    },
    board: {
      title_live: '{level} in progress',
      title_ended: '{level} (ended)',
      lineage: 'Task relationship',
      node_a: 'A Original task',
      node_a_sub: 'Supervised task',
      node_b: 'B Supervisor',
      node_c: 'C Continuation',
      node_c_sub: 'Current run',
      interventions: 'Automatic corrections',
      interventions_value: '{used} / {max} used',
      spend: 'Total cost (supervisor + continuation)',
      expires: 'Valid until {time}',
      auto_continue_on: 'Continuation skips second approval',
      timeline: 'Timeline',
      timeline_empty: 'No interventions yet.',
      pause: 'Pause auto-intervention',
      resume: 'Resume auto-intervention',
      revoke: 'End supervision',
      stop_all: 'Stop the whole task',
      regrant: 'Authorize again',
      refresh: 'Refresh',
      open_supervisor: 'Open supervisor conversation',
      stop_all_note: '"Stop the whole task" is your action; the supervisor will not treat it as a failure and restart.',
    },
    confirm: {
      revoke_title: 'End supervision?',
      revoke_body: 'The supervisor stops scheduling; the current run is not stopped and unfinished corrections are cancelled.',
      revoke_ok: 'End supervision',
      stop_all_title: 'Stop the whole task?',
      stop_all_body: 'Requests stopping the current run (this run only) and revokes supervision. The stop is verified and never shown as stopped before confirmation; existing work is not rolled back.',
      stop_all_ok: 'Stop the whole task',
      cancel: 'Cancel',
    },
    recovery: {
      title: 'Stop and continuation',
      stopped_body: 'Exit confirmed. Existing work is kept; nothing was rolled back.',
      unconfirmed_body: 'Stop not yet confirmed — the original run may still be writing to the same workspace. Verification continues; no continuation starts yet.',
      pending_body: 'Stop requested, waiting for the executor to confirm.',
      other_body: 'This stop request is closed.',
      recheck: 'Verify stop again',
      rechecking: 'Verifying…',
      create_continuation: 'Create continuation',
      continue_blocked: 'The original run and the continuation share one branch workspace; a continuation can only be created after the original run has confirmed its exit.',
      continue_via_supervisor: 'Continuations are created by the supervisor: this opens the supervisor conversation. One correction creates at most one continuation.',
      open_continuation: 'Open continuation',
      handoff_title: 'Handoff package',
    },
    handoff: {
      none: 'None',
      original_goal: 'Original goal',
      approved_scope: 'Approved scope',
      stop_reason: 'Stop reason',
      stop_evidence: 'Stop evidence',
      done: 'Done',
      not_done: 'Not done',
      unverified: 'Unverified',
      commits: 'Commits',
      uncommitted_files: 'Uncommitted files',
      untracked_files: 'Untracked files',
      unknown_outcome_ops: 'Operations with unknown outcome',
      new_requirements: 'New requirements',
      acceptance: 'Acceptance criteria',
      remaining_interventions: 'Remaining corrections',
      remaining_budget_usd: 'Remaining budget',
      source_conversation_id: 'Source conversation',
    },
    role: { supervisor: 'Supervisor', continuation: 'Continuation', supervisor_title: 'Supervisor conversation (managed correction)', continuation_title: 'Continuation conversation' },
    errors: {
      grant_level_not_open: 'The "{level}" level is not open yet (staged rollout). Please choose another level.',
      supervision_exists: 'This conversation already has a live supervision — refreshed.',
      supervision_not_live: 'Supervision has ended or was revoked — refreshed.',
      supervision_not_owner: 'Only the conversation owner can view and configure supervision.',
      version_conflict: 'Updated elsewhere — please refresh.',
      load_failed: 'Failed to load supervision settings.',
      create_failed: 'Failed to enable: {detail}',
      action_failed: 'Action failed: {detail}',
    },
  },

  // ── Chat Container ──────────────────────────────────────────────
  chat_container: {
    title_menu_open: 'Conversation actions',
    branch_status: {
      active: 'Not pushed',
      pushed: 'Pushed · awaiting merge',
      pushed_final: 'Pushed',
      merged: 'Merged',
    },
    mode_badge: {
      operation: 'Operation',
      operation_tip: 'Operation (a dev sub-mode): triage stale branches, clean up worktrees/clones, and merge content — not new feature development.',
      branch_tip: 'Feature branch of this conversation',
    },
    readonly_channel: 'This project channel is read-only — it only shows system announcements and scheduled-job results. Start a new conversation to assign work.',
    readonly_forked: 'This conversation is now read-only. Continue in the forked conversation.',
    branch_notice: 'This conversation shares branch {branch} with the source conversation. You can keep developing here; changes are committed to the same branch. Avoid editing the same files in both.',
    branch_notice_no_branch: 'This conversation shares the working directory with the source conversation. You can keep developing here; avoid editing the same files in both.',
    branch_notice_dismiss: 'Dismiss',
    ask_failed: 'Could not ask: {detail}',
    welcome_title: 'Welcome to Conductor',
    welcome_text: 'Select a conversation from the sidebar or create a new one to get started.',
    loading_messages: 'Loading messages...',
    share: 'Share',
    share_with_team: 'Share with team',
    share_sheet_title: 'Share',
    copy_conversation_id: 'Copy conversation ID',
    copy_conversation_id_sub: 'Send it to a teammate to find this conversation',
    share_with_team_sub: 'Convert to a team conversation',
    members_sub: 'View and manage members',
    export_sub: 'Choose a format',
    files: 'Files',
    export_ellipsis: 'Export\u2026',
    members_count: 'Members ({count})',
    members_title: 'Members',
    owner: 'Owner',
    member: 'Member',
    select_user: 'Select user to add...',
    adding: 'Adding...',
    add: 'Add',
    failed_remove_member: 'Failed to remove member.',
    failed_add_member: 'Failed to add member.',
    share_dialog_title: 'Share with Team',
    share_dialog_body: 'This will allow you to add team members to this conversation. All past messages will be visible to anyone you add. This cannot be undone.',
    convert_and_share: 'Convert and Share',
    export_conversation: 'Export conversation',
    toggle_member_list: 'Toggle member list',
  },

  // ── Input Area ──────────────────────────────────────────────────
  input: {
    plusMenu: {
      title: "Add to this message",
      aria: "Add to this message",
      back: "Back",
      close: "Close",
      files: "Add files",
      filesSub: "Upload or pick from the relay",
      skills: "Skills",
      skillsSub: "Choose how the task is done",
      skillsSelected: "Selected /{slug}",
      skillsTitle: "Choose a skill",
      mcp: "Connections",
      mcpTag: "MCP",
      mcpSub: "{usable} usable · {needs} need action",
      mcpLoading: "Loading connections…",
      sessionGroup: "This conversation",
      review: "Proposal review",
    },
    // P-COND-8CE6F1D21E1D: per-conversation proposal review setting + composer chip.
    reviewSetting: {
      title: "Proposal review",
      subtitle: "This conversation only · applies from the next task",
      follow: "Follow project",
      followDesc: "Current default: {label}",
      off: "No automatic review",
      offDesc: "Proposals go straight to you for approval",
      A: "Same-model review",
      ADesc: "The model that ran the task checks the proposal again",
      B: "Independent-model review",
      BDesc: "Another model checks the proposal",
      model: "Review model",
      modelAuto: "Automatic",
      modelUnavailable: "{model} (currently unavailable)",
      note: "Automatic review does not replace your approval.",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      saveFailed: "Save failed, nothing changed. Please try again later",
      readonlyOwner: "Only the conversation creator can change this",
      readonlyTier: "Your permission level cannot change this",
      readonlyRunning: "A task is running and is reviewed with the setting it started with; you can change this after it ends",
      chip: "Review: {label}",
      dotLabel: "Proposal review is set for this conversation",
    },
    send: 'Send',
    cancel: 'Cancel',
    // The in-flight button is "Stop", not "Cancel": finished work is not undone.
    stop: 'Stop',
    attach_files: 'Attach files',
    cancel_response: 'Cancel response',
    // Placeholder while a native_loop run has the textarea unlocked + failure copy.
    supplement_hint: 'Append info for agent\u2026',
    supplement_failed: 'Failed to send the supplement: {detail}',
    // Explanatory title while the supplement button is greyed out awaiting acceptance.
    supplement_pending_hint: 'The previous supplement has not been accepted by the agent yet; you can append more once it is',
    // Mid-run supplement notice under the input box (P-COND-D7200DBE0134; moved from the former Feedback tab).
    supplement_notice_claude_stream: 'Claude receives your note at a later step without interrupting the running tool. A receipt does not mean it was adopted; notes sent near the end may not take effect.',
    supplement_notice_claude: 'This run is not accepting notes yet (still starting, or not supported here). You can send after it ends.',
    supplement_notice_unavailable: 'This run is not accepting notes. You can send after it ends.',
    supplement_notice_pending: 'Checking whether this run can accept notes…',
    supplement_notice_reason_codex_supplement_disabled: 'Live notes are not enabled for this run. You can send after it ends.',
    supplement_notice_reason_codex_path_not_enabled: 'This execution path cannot receive live notes. You can send after it ends.',
    supplement_notice_reason_codex_run_not_steerable: 'This run cannot receive live notes. You can send after it ends.',
    // Draft saved by the former Feedback tab: one-time restore into the input box.
    quote_chip: 'Quote',
    quote_chip_ask: 'Ask',
    quote_remove: 'Remove quote',
    quote_bar_label: '{count} quoted passage(s)',
    supplement_quote_blocked: 'Quotes are pending — send them after this run finishes',
    quote_truncated: 'Truncated to {max} chars',
    quote_note_add: 'Add note',
    quote_note_edit: 'Edit note',
    quote_note_placeholder: 'Your note on this passage… (Enter to save · Shift+Enter for newline · Esc to cancel)',
    quote_note_prefix: 'Note: ',
    branch_context_label: 'Branch context',
    branch_context_ask_label: 'Asked about',
    branch_context_remove: 'Remove branch context',
    // Backend 409: the slot is still occupied by an unconsumed supplement.
    supplement_conflict: 'The previous supplement has not been accepted yet \u2014 please try again shortly',
    send_message: 'Send message (Enter)',
    message_input: 'Message input',
    planning: 'Planning',
    coding: 'Coding',
    // P-COND-056A6CA445F9 (3tier) — mode-toggle tooltips (replace two hard-coded English strings)
    planning_title: 'Current: Planning mode (project writes limited to planning/docs/testing; actions on your computer are not restricted; /force has no effect in this mode) · click to switch to Coding',
    coding_title: 'Current: Coding mode · click to switch to Planning',
    system_default: 'Default Model',
    model_locked: 'Model locked to the default by your administrator',
    // Title on a RED provider pill, i.e. health_state = 'invalid'
    // (P-COND-33ACB5C7F124 ST-3; narrowed by P-COND-1F526B8A2BA5 ST3). This
    // string used to serve BOTH "revoked" and "throttled", so it could only say
    // the vague "currently unavailable". The backend now distinguishes them, so
    // this one names the real remedy; throttling uses `provider_cooling` below.
    // The pill itself stays clickable.
    provider_unavailable:
      '{name} credentials have expired (every account needs to re-authenticate or get a new key) \u2014 please switch to another provider',
    // A completely different remedy from `provider_unavailable`: a rate limit
    // carries a TTL and clears itself, the user need do nothing, and the send
    // button is NOT disabled. Showing the "expired" copy here used to send
    // people off to re-authenticate for a condition that heals in ~30 minutes.
    provider_cooling:
      '{name} is rate-limited (every account has hit its limit) \u2014 it will recover automatically, or switch to another provider',
    // Escape hatch next to the composer warning: the health map is a mount-time
    // snapshot, so a user who just topped up must be able to re-check WITHOUT
    // reloading the page. This is what makes gating the send button safe.
    model_status_unavailable: 'Unavailable',
    model_status_unsettled: 'Task needs review',
    model_status_cooling: 'Cooling down',
    model_status_unknown: 'Status unconfirmed',
    model_status_title: 'Model status',
    model_details_show: 'Details',
    model_details_hide: 'Hide details',
    model_details_view: 'View reason',
    model_details_no_reason: 'No detailed reason is available. Refresh to check.',
    model_fallback: 'Sending will try {model}',
    provider_health_refresh: 'Refresh',
    // Busy labels while a refresh is in flight (double-click guard). The Codex
    // conversation lock runs a re-check; everything else is a plain re-read.
    provider_health_refreshing: 'Refreshing\u2026',
    provider_health_rechecking: 'Checking\u2026',
    // Codex conversation-lock re-check outcomes (non-200 / pending). A 200 shows the server message as-is.
    codex_recheck_pending: 'Check still in progress; result not yet confirmed. Please refresh later',
    codex_recheck_busy: 'The conversation is running; try again later',
    codex_recheck_cooldown: 'Checked just now; please try again later',
    codex_recheck_forbidden: 'You do not have access to check this conversation',
    codex_recheck_unconfirmed: 'Check result not yet confirmed; please refresh later',
    placeholder: 'Type your message... (Shift+Enter for new line)',
    collab_placeholder: 'Describe your collaboration task, e.g. draft a market analysis report\u2026 (Shift+Enter for new line)',

    file_too_large: '{name} exceeds the {max} size limit',
    max_files_exceeded: 'Maximum {max} files allowed. Please remove some files first.',
    file_type_archive: '{name}: archives are not supported. Please extract it and upload the files inside.',
    file_type_unsupported: '{name}: this file type is not supported',
    video_too_long: '{name} is longer than {minutes} minutes — trim it and upload again',
    video_badge: '▶ Video',
    video_privacy_hint: 'The AI will see this video — do not record passwords or other sensitive information. Limit {size} / {minutes} min.',
    uploading_percent: 'Uploading {percent}%',
    upload_cancel: 'Cancel upload',
    total_size_exceeded: 'Total size exceeds {max}. Please remove some files first.',
    effort: {
      label: 'Effort',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      extra: 'Extra',
      max: 'Max',
      default_badge: 'Default',
      default_hint: 'Default High',
    },
    length: {
      label: 'Length',
      standard: 'Standard',
      long: 'Long',
      max: 'Max',
      default_badge: 'Default',
      default_hint: 'Default Standard',
    },
    thinking: {
      label: 'Thinking',
      on: 'On',
      off: 'Off',
      desc: 'Enable extended reasoning',
      tooltip: 'Thinking \u2014 can think for more complex tasks',
    },
    tooltip: {
      supervisor: 'Prepend /supervisor to messages (use supervisor agent)',
      orchestrator: 'Prepend /orchestrator to messages (multi-component build \u2014 requires plan)',
      long: 'Prepend /long to messages (mark as long-running)',
      force: 'Prepend /force to messages (bypass dispatch gate)',
      model: 'Select model for this message. \u2018System Default\u2019 respects project settings and global defaults.',
      options: 'Options',
    },
  },

  // ── Message ─────────────────────────────────────────────────────
  message: {
    role: {
      you: 'You',
      conductor: 'Conductor',
      system: 'System',
    },
    source: {
      wecom: 'WeCom',
      web: 'Web',
    },
    assistant_typing: 'Assistant is typing',
    responding_to: 'Responding to {name}',
    failed_load_image: 'Failed to load image',
    loading_image: 'Loading image...',
    start_implementation: 'Start Implementation',
    starting: 'Starting...',
    retry: 'Retry',
    download_failed: 'Download failed',
    send_failed: 'Send failed',
    resend: 'Resend',
    resending: 'Sending\u2026',
    resend_capped: 'Retried several times and it still failed \u2014 resending is stopped. Rephrase the request or reset the session before trying again.',
    more_actions: 'More actions',
    menu_doc: '\uD83D\uDCC4 Generate file (doc)',
    menu_feedback: '\uD83D\uDC1E Report a problem',
    menu_multi_review: '🔍 Multi-model review',
    menu_quote: 'Quote this',
    menu_branch: 'Branch a new conversation from here',
    menu_branch_scope: 'Same project · same branch ({branch})',
    menu_branch_scope_no_branch: 'Same project · same branch',
    branch_failed: 'Could not create: {detail}',
    branch_error_forbidden: 'no access to the source conversation',
    branch_error_invalid: 'invalid branch request',
    branch_error_unknown: 'please try again later',
    branch_seed_label: 'Branch context · from #{id}',
    branch_seed_expand: 'Expand',
    branch_seed_collapse: 'Collapse',
  },

  // ── Multi-model review (third-party audit of a proposal) ─────
  multiReview: {
    title: 'Multi-model review',
    subtitle:
      'Pick 1–{max} models to check the code read-only and give independent opinions; they never modify files and never approve on your behalf.',
    loading: 'Loading available models…',
    no_targets: 'Deep review is disabled or no model is available — contact your administrator.',
    // P-COND-368144D76579 — ReviewTargetsResponse.unavailable_reason
    unavailable_disabled: 'Deep review is not enabled in this environment. An administrator must turn it on before models can be picked; the proposal can still be approved as usual.',
    unavailable_no_project: 'This conversation is not linked to a project, so no review model can be picked.',
    unavailable_no_authorized_model: 'No review model is authorized for this project — ask an administrator to check the model authorization.',
    unavailable_error: 'Failed to load the model list, so availability is unknown. Please try again later.',
    no_credentials: 'No model has a usable credential — configure one in Admin first.',
    unavailable: 'Unavailable',
    selected_count: 'Selected {n} / {max}',
    remaining_today: '{n} left today',
    cap_reached: 'You can pick at most {max} models',
    readonly_note: 'A review never modifies any file. ',
    billing_note: 'billed by actual usage',
    cost_cap: ', capped at about ${x} per model',
    footer_end: '.',
    cancel: 'Cancel',
    start: 'Start review',
    running: 'Reviewing…',
    load_failed: 'Failed to load the model list',
    run_failed: 'Review failed',
    // Running (window may be closed)
    bg_title: 'The review is running in the background',
    bg_body: 'You can close this window and keep working — the result will appear in the conversation.',
    in_progress: 'Running…',
    close_window: 'Close window',
    // Per-model failures
    failed_title: '{n} model(s) did not complete',
    failed_tag: 'Failed',
    retry: 'Retry',
    close: 'Close',
    reason_no_credential: 'No usable credential',
    reason_empty_content: 'The model returned empty content',
    reason_unknown: 'Unknown reason',
    // Transport-level error classification
    err_timeout:
      'The review exceeded the client wait limit and the request was aborted; it may still be running — the result will arrive later as a system message.',
    err_credential: 'Invalid credential or insufficient permission — check the model credentials in Admin.',
    err_quota: 'Your daily review count or organization budget is exhausted — try again later.',
    all_failed_note: 'Review complete — results recorded in the conversation.',
    // Background deep review (read-only child tasks, ST10)
    agent_pending_title: 'Deep review running in the background',
    agent_pending_body: 'The review runs read-only and may take several minutes; the result appears in the conversation as a card once every model has finished or timed out. You can close this window.',
    cancel_agent: 'Stop review',
    pending_bar: 'Multi-model review in progress',
    pending_bar_count: 'Multi-model review in progress · {count} models',
    pending_cancel: 'Stop review',
    cancel_done: 'Stop requested — waiting for confirmation.',
    cancel_failed: 'Could not submit the stop request',
    // Live review status (P-COND-6B1B3E09FD79 ST2)
    elapsed_sec: '{s}s elapsed',
    elapsed_min: '{m}m {s}s elapsed',
    elapsed_hour: '{h}h {m}m elapsed',
    live_queued: 'Queued',
    live_running: 'Running',
    live_completed: 'Done',
    live_failed: 'Failed',
    live_stop_pending: 'Stop pending',
    live_stopped: 'Stopped',
    live_last_running: 'Last known: running',
    live_view_process: 'View process →',
    // Review ops panel + compact strip (P-COND-158AE9229655, design mmreview-stop-panel-v2)
    strip_title: 'Reviewing · {done} / {total} models done',
    strip_stop_pending: 'Stop pending · {n} models',
    strip_stop_failed: 'Stop request failed · retry',
    strip_stopped: 'Review ended · {done} done / {stopped} stopped',
    strip_completed: 'Review complete · {done} / {total}',
    strip_unavailable: 'Reviewing · status unavailable',
    strip_view: 'View review ↓',
    strip_awaiting_proposal: 'Review ended · waiting for the proposal',
    strip_awaiting_approval: 'Proposal awaiting approval',
    auto_strip_running: 'Independent review in progress',
    auto_strip_stopped: 'Independent review stopped · the proposal will be submitted directly',
    panel_awaiting_proposal: 'Review ended, waiting for the proposal to be submitted',
    panel_awaiting_approval: 'Proposal awaiting approval (see the proposal\'s "Independent review" row for the verdict)',
    auto_panel_running: 'Independent review in progress',
    auto_panel_stopped: 'Stopped — the proposal will be submitted directly',
    note_awaiting_proposal: 'The proposal will appear in the conversation once submitted.',
    note_awaiting_approval: 'This closes by itself once the approval is settled.',
    auto_note_running: 'Another model is checking the proposal; stopping submits the proposal directly without waiting for the review.',
    auto_note_stopped: 'The independent review was stopped; this revision was not reviewed, please judge it yourself.',
    panel_running: 'Multi-model review in progress',
    panel_stop_pending: 'Stop requested, waiting for confirmation',
    panel_stop_failed: 'Could not submit the stop request',
    panel_stopped: 'This review has ended',
    panel_completed: 'Review complete',
    panel_unavailable: 'Status temporarily unavailable',
    panel_note: 'Stopping only affects this review; finished results are kept.',
    note_stop_pending: 'Not every model has confirmed the stop yet; finished results are kept and you can still view the process.',
    note_stop_failed: 'Stop not confirmed. Retry, or open the process to see what is happening.',
    note_stopped: 'Unfinished models were confirmed stopped; finished results stay in the conversation.',
    note_completed: 'The results are shown in the conversation.',
    note_unavailable: 'Cannot fetch the latest status right now; showing the last known state. You can still stop the review.',
    stop: 'Stop review',
    stop_waiting: 'Waiting for confirmation',
    stop_retry: 'Retry stop',
    details_summary: 'Model progress and process',
    // Review result card (ST3)
    result_title: 'Multi-model review results',
    result_view_process: 'View investigation ›',
    result_expand: 'Show full text',
    result_collapse: 'Collapse',
    status_approved: 'Approved',
    status_issues: 'Changes requested',
    status_unknown: 'Inconclusive',
    status_timeout: 'Timed out',
    status_cancelled: 'Stopped',
    status_cancel_unconfirmed: 'Stop not confirmed',
    status_failed: 'Incomplete',
    // Result card v2 summary line + child back link (P-COND-DEF54F26332F ST3)
    summary_models: '{n} models',
    summary_issues: '{n} request changes',
    finding_direction: 'Direction {n}',
    finding_blocker: 'Blocker {n}',
    finding_suggest: 'Suggestion {n}',
    summary_approved: '{n} approved',
    summary_incomplete: '{n} incomplete',
    summary_unknown: '{n} inconclusive',
    role_label: 'Multi-model review',
    back_to_parent: '← Back to original conversation',
    back_to_parent_titled: '← Back to original conversation · {title}',
  },

  // ── Feedback (in-conversation report → Observability Center) ─────
  feedback: {
    title: 'Report a problem',
    kv_project: 'Project',
    kv_conversation: 'Conversation',
    kv_user: 'User',
    placeholder: 'Describe the problem you ran into (optional)',
    hint: 'On submit, the last 2 rounds of this conversation are attached for ops / the Observability Center to investigate.',
    cancel: 'Cancel',
    submit: 'Submit',
    submitting: 'Submitting\u2026',
    submitted: 'Submitted',
    submit_error: 'Failed to submit. Please try again.',
  },

  // ── Message List ────────────────────────────────────────────────
  message_list: {
    load_more: 'Load more messages',
    loading: 'Loading',
    empty: 'No messages yet. Start the conversation!',
    assistant_typing: 'Assistant is typing',
    release: 'Release',
    pull_to_refresh: 'Pull to refresh',
  },

  // ── Streaming Progress ──────────────────────────────────────────
  progress: {
    starting: 'Starting',
    connection_lost: 'Connection lost \u2014 waiting to reconnect',
    last_prefix: 'Last:',
    completed: 'Completed in {seconds}s | {summary}',
    elapsed: '{seconds}s elapsed',
    processing: 'Processing',
    // Post-stop retained progress record banner (ChatContainer::TaskStoppedBanner).
    stopped_at: 'Task stopped at {time}',
    stopped_expand: 'click to expand the completed record',
    stopped_collapse: 'click to collapse the completed record',
    stopped_tools: 'Tools called',
    stopped_text: 'Content produced',
  },

  // ── Mid-run supplement status tray (ChatContainer::SupplementTray) ──
  // ── Execution panel (CS-T10, P-COND-82309B809347) ─────────────────
  // ── Managed supervision v2 (V2-B): ledger / read-only quick questions ──
  supervision_v2: {
    workbench: {
      "title": "Managed workbench",
      "back": "Back to conversation",
      "no_records": "No records yet. This does not mean work is complete.",
      "activity": "Progress while you were away",
      "notifications": "Notification history",
      "expires": "Decide by {time}",
      "affected": "Affected tasks: {tasks}",
      "choose": "Choose an option",
      "recommended": "Recommended; explicit selection required",
      "confirm_decision": "Confirm selection",
      "decision_note": "Your choice updates this contract’s decision. Recommendations are never selected automatically.",
      "fallbacks": "Approved alternatives",
      "no_fallbacks": "No approved alternatives yet.",
      "contract_version": "Contract version {version}",
      "evidence_note": "Review current criteria. A pass for an old version does not verify the current output.",
      "criterion": "Acceptance criterion",
      "verdict": "Current verdict",
      "basis": "Evidence for this round",
      "stale_note": "The output or contract changed. Verify again.",
      "basis_versions": "Contract {contract} · Attempt {attempt} · Output {output}",
      "grant": "Supervision status",
      "expires_label": "Authorization expires",
      "execution": "Execution state (task ledger)",
      "last_check": "Latest check",
      "pause_note": "Pausing intervention continues read-only checks and may incur costs. Ending supervision does not stop the task.",
      "resume": "Resume intervention",
      "pause": "Pause intervention",
      "end": "End supervision",
      "stop": "Stop entire task",
      "end_confirm": "End checks and automatic intervention? The original task will continue.",
      "stop_confirm": "Request a stop and revoke supervision? The stop must be verified. Existing work is preserved.",
      "confirm": "Confirm",
      "cancel": "Cancel",
      "test_deploy": "Deploy to test environment",
      "deploy_note": "Only the registered test environment authorized in the delegation card is used. Unknown outcomes are not retried. Production is excluded.",
      "stop_requested": "Stop requested. Check the receipt; execution may still be running until confirmed.",
      "see_evidence": "Review acceptance evidence",
      "see_output": "View output records",
      "see_decisions": "Review pending decisions",
      "accepted": "Accepted outputs",
      "pending": "Needs your decision",
      "goal": "Delivery goal",
      "no_goal": "Delivery goal not confirmed",
      "receipt": {
            "delivered": "Delivered",
            "failed": "Failed",
            "unknown": "Delivery unknown",
            "intent": "Preparing",
            "deferred": "Deferred"
      },
      "acceptance": {
            "passed": "Passed",
            "failed": "Failed",
            "partial": "Partially passed",
            "unverified": "Not verified",
            "stale": "Stale · verify again",
            "exception_accepted": "Accepted by exception"
      },
      "execution_state": {
            "in_progress": "In progress",
            "idle": "No tasks running"
      },
      "deploy": {
            "queued": "Test deployment queued. Continue browsing and refresh later for the receipt.",
            "succeeded": "Test deployment completed; page and business acceptance are still required.",
            "failed": "Test deployment failed. Check deployment records.",
            "unknown": "Deployment outcome unknown. The request is retained and will not be retried automatically."
      }
},
    loading: 'Loading…',
    refresh: 'Refresh',
    load_failed: 'Failed to load: {detail}',
    refreshing: 'Refreshing…',
    stale_note: 'Still showing data read {time}; it may be out of date.',
    action_failed: 'Action failed: {detail}',
    submitting: 'Submitting…',
    updated_at: 'Ledger updated {time}',
    read_at: 'read {time}',
    model_supervisor: 'Supervisor model',
    model_executor: 'Executor model',
    model_default: 'Project default',
    contract: 'Contract v{version}',
    no_contract: 'No contract yet',
    scope: 'Allowed scope: {items}',
    acceptance: '{count} acceptance item(s)',
    tasks: 'Tasks ({count})',
    decisions: 'Needs your decision ({count})',
    decisions_note: 'The recommended option is only highlighted; it becomes your decision after you click an option and confirm. Open decisions block only related actions.',
    recommended: 'Recommended',
    not_approved_until_click: 'Not decided yet: click an option',
    confirm_choice: 'Confirm choice',
    decision_stale: 'This decision was updated or already answered; refreshed.',
    decision_status: { pending: 'Pending', answered: 'Decided', expired: 'Expired', superseded: 'Superseded' },
    followups: 'Follow-ups ({count})',
    owner: { supervisor: 'Supervisor', executor: 'Executor', user: 'Needs you' },
    last_verdict: 'Previous round judgement',
    no_verdict: 'No judgement yet',
    guidance_label: 'Guidance for the supervisor',
    guidance_placeholder: 'e.g. do not touch the database yet',
    guidance_send: 'Send guidance',
    guidance_sent: 'Recorded; the supervisor reads it next round',
    open_guidance_label: 'Guidance when opening (optional)',
    open_note: 'The supervisor starts its first review automatically; no need to type "start". Missing goal/scope/acceptance become decisions for you.',
    upgrade: 'Upgrade to managed',
    quick_title: 'Quick look (read-only, never wakes the model)',
    quick: { doing: 'Doing now', done: 'Done so far', next: 'Next step', stuck: 'Stuck?', decide: 'What to decide', explain: 'Explain plan' },
    facts: 'Facts',
    judgement: 'Previous round judgement',
    unknowns: 'Unknown',
    readonly_note: 'Read-only answer from the recorded ledger and execution status; no action is taken.',
    task_status: { pending: 'Pending', ready: 'Ready', running: 'Running', awaiting_acceptance: 'Awaiting acceptance', rework: 'Rework', accepted: 'Accepted', blocked: 'Blocked', cancelled: 'Cancelled' },
    acceptance_status: { not_started: 'Acceptance not started', pending: 'Acceptance pending', passed: 'Accepted', failed: 'Acceptance failed', needs_user: 'Acceptance needs you', exception_accepted: 'Accepted by exception' },
    // V2-F page integration
    axis: { grant: 'Grant', supervisor: 'Supervisor', executor: 'Execution', acceptance: 'Acceptance' },
    grant_state: { valid: 'Valid', paused: 'Paused', expired: 'Expired', ended: 'Ended', revoked: 'Revoked' },
    expires: 'Expires {time}',
    supervisor_state: { waiting: 'No check yet', running: 'Checking', completed: 'Last round done', failed: 'Last round failed', cancelled: 'Last round cancelled', unknown: 'Unknown' },
    executor_state: { idle: 'Idle', preparing: 'Preparing', running: 'Running', awaiting_user: 'Waiting for your answer', awaiting_approval: 'Waiting for plan approval', failed: 'Last run failed', unknown: 'Unknown (read failed)' },
    verdict: { not_started: 'Not started', pending: 'Pending', passed: 'All passed', failed: 'Some failed', needs_user: 'Needs you', exception_accepted: 'Includes exceptions (not a test pass)' },
    verdict_counts: 'Passed {done}/{total}',
    budget_title: 'Spend and budget',
    budget_total: 'Managed budget',
    budget_settled: 'Settled',
    budget_pending: 'Pending settlement',
    budget_pending_value: '{amount} (cost unconfirmed; held at its reservation, never counted as 0)',
    budget_pending_none: 'None',
    budget_reserved: 'Reserved in flight',
    budget_remaining: 'Remaining',
    budget_pre_takeover: 'Original task cost before takeover',
    budget_pre_takeover_note: ' (listed separately, not in the managed budget)',
    budget_counters: 'Counts',
    counter_interventions: 'Interventions {value}',
    counter_stops: 'Stops {value}',
    counter_auto: 'Auto dispatches {value}',
    unknown_cost: 'Unknown',
    views_label: 'Managed ledger views',
    view: { tasks: 'Tasks ({count})', followups: 'Follow-ups ({count})', interventions: 'Interventions ({count})', contract: 'Agreement ({count})' },
    view_empty: 'Nothing recorded yet',
    output_version: 'Output v{version}',
    link_task: 'Task: {name}',
    link_followup: 'Follow-up: {name}',
    link_action: 'Intervention: {kind}',
    link_contract: 'Agreement v{version}',
    followup_status: { open: 'Open', closed: 'Closed', cancelled: 'Cancelled' },
    next_step: 'Next: {text}',
    close_condition: 'Close when: {text}',
    action_kind: { feedback: 'Guidance', stop: 'Stop', continue_dispatch: 'Continuation', verify: 'Check' },
    caller: { legacy_unknown: 'Source not recorded', user: 'By you', supervisor: 'By supervisor', system: 'By platform' },
    delivery_label: 'Delivery: ',
    adoption_label: 'Adoption: ',
    delivery: { pending: 'Pending', sending: 'Sending', received: 'Received', rejected: 'Rejected', unknown: 'Unknown (not auto-resent)' },
    adoption: { unconfirmed: 'Unconfirmed (received is not adopted)', adopted: 'Adopted', partial: 'Partly adopted', not_adopted: 'Not adopted' },
    not_tracked: 'n/a',
    dispatch_ref: 'Run {id}',
    contract_current: 'In effect',
    contract_history: 'Earlier version',
    guidance_chat_note: 'This note goes to the supervisor; to add input to the running task directly, use the chat box below.',
    // V2-F′ phase-2 autonomy views (SupervisionAutonomy*.tsx). Closed switch shows closed_* only.
    autonomy: {
      toggle: 'Open managed workbench',
      toggle_note: 'Advances by the delegation card and execution plan while you are away; loads when expanded.',
      closed_title: 'Phase-2 autonomous progress is not open yet',
      closed_body: 'Long-running autonomous progress is not enabled on this platform yet. The phase-1 ledger, decisions and stop controls above are unaffected.',
      not_managed_title: 'Phase-2 autonomous progress is only available for managed-level supervision',
      not_managed_body: 'This supervision is not on a managed level (split quota), so phase-2 autonomous progress is unavailable. Phase-1 features above are unaffected.',
      loading: 'Loading autonomy data…',
      load_failed: 'Load failed: {detail}',
      source_failed: 'Load failed',
      retry: 'Retry',
      refresh: 'Refresh',
      read_only_note: 'The grant has ended; view only.',
      views_label: 'Autonomy views',
      view: { overview: 'Results', commission: 'Delegation', map: 'Task map', decisions: 'Decisions', evidence: 'Evidence', control: 'Spend & control' },
      overall: { empty: 'No tasks yet', in_progress: 'Still in progress', partial: 'Partially done (not all done)', all_blocked: 'All blocked — needs you', complete: 'All done' },
      group: { awaiting_decision: 'Awaiting decision', blocked: 'Blocked', completed: 'Completed', partial: 'Partial', unverified: 'Unverified', in_progress: 'In progress', not_started: 'Not started' },
      group_count: '{label} ({count})',
      exception: 'Accepted by exception (not a test pass)',
      evidence_refs: 'Evidence {refs}',
      no_evidence: 'No evidence yet',
      task_cost: 'Cost USD {usd}',
      task_cost_unknown: 'Cost pending settlement',
      unknown_cost_n: 'plus {count} pending settlement',
      endpoint: { label: 'Delivery end-point: {kind}', none: 'No delivery end-point set', not_required: 'No end-point check needed', verified: 'End-point verified', unverified: 'End-point unverified (not delivered)' },
      endpoint_title: 'Delivery end-point',
      endpoint_kind: { preview: 'Preview', test_env: 'Test environment', release_candidate: 'Release candidate' },
      decisions_pending: '{count} pending decisions',
      card_none: 'No delegation card yet. Fill in and confirm one before leaving.',
      card_title: 'Delegation card v{version} (contract v{contract})',
      card_created: 'Confirmed {time}',
      delivery_note: 'Delivery note',
      forbidden_items: 'Forbidden items',
      tech_principles: 'Technical principles',
      allow_test_deploy: 'Allow deploying to the test environment',
      allow_test_deploy_yes: 'Allowed (registered test environment only, never production)',
      allow_test_deploy_no: 'Not allowed',
      test_environment: 'Test environment',
      notify_channels: 'Notification channels',
      channel: { in_app: 'In-app', wecom: 'WeCom', email: 'Email' },
      change_note: 'Change note',
      fallbacks: 'Pre-authorised fallbacks ({count})',
      fallback_row: 'If "{condition}" then "{action}"',
      fallback_status: { active: 'Active', superseded: 'Superseded', revoked: 'Revoked' },
      fallback_tasks: 'Tasks: {tasks}',
      fallback_add: 'Add fallback',
      fallback_remove: 'Remove',
      fallback_rule_key: 'Rule key',
      fallback_condition_key: 'Condition key',
      fallback_condition: 'Condition',
      fallback_action: 'Action',
      fallback_task_ids: 'Task IDs (comma-separated, optional)',
      none: 'None',
      edit_card: 'Edit delegation card',
      create_card: 'Fill in delegation card',
      save_card: 'Confirm and save as a new version',
      cancel: 'Cancel',
      saving: 'Saving…',
      saved: 'Saved as v{version}',
      lines_hint: 'One item per line',
      card_stale: 'The delegation card was updated to a newer version. Refresh and edit the latest version (your draft was not submitted).',
      refresh_latest: 'Refresh to latest',
      plan_none: 'No execution plan yet (the supervisor submits one first, or you can).',
      plan_title: 'Execution plan v{version} · {status}',
      plan_status: { active: 'Active', superseded: 'Superseded', rejected: 'Rejected' },
      plan_kinds: 'Revision kinds: {kinds}',
      revision_kind: { initial: 'Initial', split: 'Split', reorder: 'Reorder', add_verification: 'Add verification', rework: 'Rework' },
      node_kind: { work: 'Work', verification: 'Verification', rework: 'Rework' },
      plan_reason: 'Reason: {reason}',
      plan_rejected: 'Rejected {time}: {reason}',
      plan_latest: 'Latest version v{version}',
      depends_on: 'Depends on: {keys}',
      no_deps: 'No dependencies',
      acceptance_ids: 'Acceptance items: {ids}',
      node_state_unknown: 'No task derived yet',
      revise_plan: 'Submit a plan revision',
      revise_kinds: 'Revision kinds',
      revise_reason: 'Revision reason (required)',
      revise_nodes: 'Nodes (JSON array: node_key / title / kind / goal_ref / acceptance_ids / depends_on)',
      revise_nodes_invalid: 'Nodes are not a valid JSON array',
      revise_nodes_count: 'Node count must be between 1 and {max} (currently {count})',
      revise_node_not_object: 'Node {index} is not an object',
      revise_node_missing: 'Node {index} is missing fields: {fields}',
      revise_node_unknown: 'Node {index} has unknown fields: {fields}',
      revise_need_contract: 'No current contract version; a revision cannot be submitted.',
      submit_plan: 'Submit revision',
      plan_submitted: 'Submitted v{version}',
      plan_stale: 'The plan has a newer version (latest v{version}). Refresh and revise the latest version.',
      plan_inflight: 'The revision touches in-flight actions that must be reconciled first: {ids}',
      reject_plan: 'Reject this version',
      reject_reason: 'Reject reason',
      confirm_reject: 'Confirm reject',
      rejected_ok: 'Rejected. No new dispatch follows; in-flight actions are not cancelled and need reconciling: {ids}',
      rejected_ok_none: 'Rejected. No new dispatch follows; no in-flight actions.',
      plan_not_active: 'This version is no longer active and cannot be rejected. Please refresh.',
      decisions_empty: 'No pending decisions.',
      decision_expires: 'Due {time}',
      decision_tasks: 'Affects: {tasks}',
      decisions_answer_note: 'Answer in the decision card of the ledger above; the recommendation is never preselected and nothing is approved without a click.',
      evidence_note: 'Completed counts only passed or exception-accepted; conclusions invalidated by a new output version return to unverified.',
      cost_title: 'Spend',
      cost_total: 'Recorded cost USD {usd}',
      cost_budget: 'Budget USD {budget} · settled USD {spent}',
      cost_unknown: '{count} actions pending settlement (unknown cost is never 0)',
      readiness_title: 'Before you leave',
      ready_yes: 'Ready to leave: every blocking item is satisfied',
      ready_no: 'Not ready: blocking items remain',
      blocking: 'Blocking',
      hint_only: 'Hint',
      remedy: 'Fix: {remedy}',
      readiness_key: { card: 'Delegation card', card_basis: 'Card basis', contract: 'Contract', grant: 'Managed grant', budget: 'Budget', deadline: 'Grant deadline', test_env: 'Test environment', channels: 'Notification channels', pending_decisions: 'Pending decisions', plan: 'Execution plan' },
      control_note: 'Stopping the task and ending supervision use the phase-1 controls above; this page offers no second stop entry.',
    },
  },

  execution_panel: {
    tab_status: "Execution",
    tab_supervision: "Supervision",
    technical: "Technical and delivery details",
    step_hint: "Select a step for details",
    step_input: "Execution input",
    step_output: "Result excerpt",
    step_exit: "Exit code {code}",
    step_title: "Step details",
    step_enlarge: "Expand reader",
    step_copy: "Copy input",
    step_copied: "Copied",
    step_select_copy: "Selected. Copy manually.",
    step_excerpt_note: "Excerpts only. A completed step does not mean the task is complete.",
    step_truncated: "Some content was omitted. Expanding the reader does not restore it.",
    step_pending: "Running. No result received yet.",
    step_no_result: "No result available; success is unconfirmed.",
    step_withheld: "Content withheld: sensitive reads, file contents or unsupported output.",
    step_expired: "This detail expired or was not collected.",
    step_forbidden: "Only the conversation owner can read inputs and results.",
    step_unavailable: "Details unavailable. Try expanding again later.",
    step_not_collected: "No expandable detail was collected",
    step_retained: "Currently reading (outside recent records)",
    action_command: "Run command",
    action_read: "Read file",
    action_grep: "Search contents",
    action_glob: "Find files",
    step_running: "Running",
    step_done: "Done",
    step_error: "Failed",
    step_reason_cancelled: "This step was cancelled.",
    step_reason_timeout: "This step timed out.",
    step_reason_missing_result: "Execution ended without a result for this step.",
    step_reason_other: "Tool status is not a task acceptance conclusion.",
    read_file_label: "File read",
    search_label: "Search criteria",
    file_body_hidden: "File contents are not shown.",
    withheld_sensitive: "This command involves sensitive content; command and result are not shown.",
    withheld_output_sensitive: "The result contains sensitive content and is not shown.",
    withheld_file_content: "This command outputs file contents; the result is not shown.",
    withheld_oversize: "The result is too large to show.",
    load_more: "Load more",
    turn_start: "New round started",
    actions_label: "Actions",
    level_closed: " · Not open yet",
    level_unavailable: "Available grant levels could not be loaded. Creation is disabled.",
    advanced: "Advanced settings",
    more_actions: "More actions",
    check_waiting: "Waiting for a check",
    check_running: "Checking",
    check_completed: "Check execution completed",
    check_failed: "Check execution failed",
    check_cancelled: "Check cancelled",
    check_unknown: "Check outcome unconfirmed",
    check_event_note: "Checks wake on configured events, not continuously.",
    check_open_conclusion: "The supervisor has replied. Open its conversation to read the conclusion.",
    check_no_conclusion: "No confirmed check conclusion yet.",
    title: 'Execution',
    heading: 'How far has this task got?',
    close: 'Close execution panel',
    back: 'Back to conversation',
    refresh: 'Refresh',
    fresh_at: 'Data as of {time}',
    loading: 'Loading execution status…',
    error_title: 'Cannot read execution status right now',
    error_body: 'No current status was received. This does not mean the task has ended.',
    retry: 'Retry',
    stale_notice: 'Out of date · last updated {time}. The old record is kept below; the current state is unverified.',
    empty_title: 'This task has not started',
    empty_body: 'No execution record and no pending feedback.',
    state_label: 'Execution',
    delivery_label: 'Delivery',
    state_idle: 'Nothing running',
    state_idle_ended: 'This round has ended',
    state_preparing: 'Preparing',
    state_running: 'Running',
    state_awaiting_user: 'Waiting for your answer',
    state_awaiting_approval: 'Waiting for approval',
    state_failed: 'Failed',
    state_unknown: 'Status unverified',
    unknown_note: 'The executor state cannot be read right now. The task may still be running — do not treat it as finished.',
    delivery_none: 'No delivery record',
    delivery_active: 'Branch in progress · not pushed',
    delivery_pushed: 'Pushed · awaiting merge',
    delivery_merged: 'Merged',
    delivery_git_pending: 'Uncommitted changes',
    delivery_git_committed: 'Committed · not pushed',
    delivery_git_clean: 'No uncommitted changes',
    started_at: 'Started {time}',
    heartbeat: 'Last heartbeat {time}',
    heartbeat_none: 'No heartbeat',
    heartbeat_hint: 'A heartbeat only means the process is alive, not that it made progress',
    progress: 'Last progress {time}',
    progress_none: 'No progress recorded',
    waiting_title: 'Your decision is needed',
    waiting_body: '{count} question(s) are waiting for your answer — reply in the main conversation.',
    approval_body: 'A proposal is waiting for approval — handle it in the main conversation.',
    failed_body: 'This execution ended in failure. Look into the cause before deciding the next step.',
    delivered_title: 'One more step to handle',
    delivered_body: 'There are changes that are not pushed and not live yet.',
    milestones_title: 'Recent records (newest first)',
    milestones_empty: 'No execution records yet.',
    observation_hint: 'Records come from the execution trace; a step without a record has not been shown to pass.',
    observe: 'Learn more',
    observe_hint: 'Open a linked observation conversation — read-only discussion that does not affect the main run',
    observe_creating: 'Creating observation conversation…',
    observe_failed: 'Could not create the observation conversation: {detail}',
    observe_title_prefix: 'Observe: ',
    observe_untitled: 'Untitled conversation',
    supervision_title: 'Ask a supervisor to review',
    err_run_mismatch: 'The run has changed — please refresh',
    err_expected_run_id_required: 'The current run id is missing — refresh and try again',
    err_not_supported: 'This run does not accept mid-run supplements',
    err_forbidden: 'You do not have permission to control this task. Viewing progress is not the same as changing it.',
    err_unknown_outcome: 'Delivery unknown — do not send it again',
    err_other_generic: 'Send failed, please try again later',
  },

  supplement: {
    claude_received: 'Claude received it; verify the execution result',
    rejected_note: 'The executor did not accept this supplement. Its text is retained for copying.',
    status_unknown_codex: 'Delivery status unknown',
    status_unknown: 'Acceptance unconfirmed',
    unknown_note: 'Delivery is uncertain. Do not resend automatically. Resolution requires the old execution to have exited.',
    end: 'End this task',
    continue: 'Acknowledge risk and continue',
    continue_note: 'Decision recorded. Send a new message to continue; the uncertain input will not be replayed.',
    text_only: 'Mid-task supplements support text only. Keep attachments for the next message.',
    title: 'Supplements for this task ({count})',
    status_pending: 'Pending',
    status_accepted: 'Accepted {time}',
    status_undelivered: 'Not delivered',
    pending_note: 'Waiting for the executor receipt; another supplement can be sent after this one is confirmed.',
    undelivered_note: 'The task ended before this one was picked up (text kept so you can copy and resend)',
  },

  // ── Export Modal ────────────────────────────────────────────────
  export_modal: {
    title_conversation: 'Export Conversation',
    title_proposal: 'Export Proposal',
    include_roles: 'Include roles',
    role_user: 'User',
    role_assistant: 'Assistant',
    role_system: 'System',
    last_n_messages: 'Last N messages',
    all_messages: 'All messages',
    more_options: 'More options',
    format: 'Format',
    format_md: 'Markdown (.md)',
    format_html: 'HTML (.html)',
    format_pdf: 'PDF (.pdf)',
    format_docx: 'Word (.docx)',
    downloading: 'Downloading...',
    download: 'Download',
    export_failed: 'Export failed. Please try again.',
    close: 'Close',
  },

  // ── Conversation Files Panel (P-COND-490FE79403B8 ST2) ──────────
  files_panel: {
    video_unplayable: 'Your browser cannot play this video — download it to view.',
    title: 'Conversation files',
    close: 'Close',
    back: 'Back',
    group_agent: 'Agent generated',
    group_uploaded: 'Uploaded by me',
    group_design: 'Designs',
    group_planning: 'Planning docs',
    group_documents: 'Project docs',
    empty: 'No files in this conversation yet',
    loading: 'Loading…',
    load_error: 'Failed to load files',
    download: 'Download',
    open: 'Open',
    close_preview: 'Close preview',
    preview_error: 'Failed to load preview',
    preview_unsupported: 'Preview is not available for this type — download to view',
    preview_too_large: 'File is too large to preview — download to view',
    group_project_all: 'Project files',
    project_all_hint: 'Not limited to this conversation · collapsed',
    group_my_dept: 'My department',
    group_company_shared: 'Company shared',
    no_dept_hint: 'You have not joined a department — only company-shared files are shown',
    other_depts_hidden: 'Other departments\' folders are not shown here',
    remote_unreachable: 'Remote host unreachable — cannot list project files',
    folder_empty: 'This folder is empty or does not exist',
    view_rendered: 'Rendered',
    view_source: 'Source',
    view_table: 'Table',
    view_layout: 'Layout',
    sheet_tabs: 'Sheets',
    sheet_empty: 'This sheet is empty',
    sheet_truncated: 'Showing the first {rows} of {total} rows; download for the full content',
    sheet_truncated_cols: 'Showing the first {cols} of {total} columns; download for the full content',
    maximize: 'Maximize',
    restore: 'Restore',
    open_new_window: 'Open in new window',
    back_to_list: 'Back to list',
    link_back: 'Back to',
    link_outside_scope: 'Outside the folders this panel can browse (docs/, planning/ …)',
    link_not_found: 'The linked file does not exist or has moved',
    sort_by: 'Sort',
    sort_modified: 'Modified',
    sort_name: 'Name',
    sort_group: 'Sort by',
    view_by: 'View',
    view_name: 'File name',
    view_thumb: 'Name + thumbnail',
    view_menu: 'View',
    load_more: 'Load more ({n} left)',
    converting: 'Converting to PDF preview…',
    annotate: 'Annotate & add to message',
    annotate_title: 'Annotate image',
    annotate_pen: 'Freehand',
    annotate_rect: 'Rectangle',
    annotate_ellipse: 'Ellipse',
    annotate_arrow: 'Arrow',
    annotate_text: 'Text',
    annotate_text_placeholder: 'Type, press Enter to confirm',
    annotate_color: 'Color',
    annotate_undo: 'Undo',
    annotate_clear: 'Clear',
    annotate_note_placeholder: 'Add a note (optional)',
    annotate_cancel: 'Cancel',
    annotate_done: 'Add to message',
    annotate_load_error: 'Image failed to load; cannot annotate',
    quote_lines: 'Quote lines {start}–{end}',
    quote_truncated: '…(truncated, {n} lines total)',
    annotate_page: 'Annotate page',
    annotate_current_page: 'Annotate page {n} / {total}',
    page_label: 'p.{n}',
    pdf_loading: 'Loading PDF…',
    preview_office_too_large: 'File exceeds 20 MB and cannot be previewed; please download it',
    preview_office_timeout: 'Conversion timed out; retry later or download the file',
    preview_office_failed: 'The file may be corrupt or encrypted; please download it',
    annotate_short: 'Annotate {n}/{total}',
    annotate_visible: 'Annotate view',
    snapshot_busy: 'Capturing…',
    snapshot_failed: 'Capture failed, retry',
    quote_selection: 'Quote selection',
    screen_label: 'screen {n}',
  },

  // ── New Conversation Dialog ─────────────────────────────────────
  new_conv: {
    title: 'New Conversation',
    project: 'Project',
    base_branch: 'Base Branch',
    loading_branches: 'Loading branches...',
    branch_hint: 'Develop new features on a feat branch; master is for ops / hotfixes only.',
    branch_hint_local: 'Select the branch to work on; commits go directly to it via worktree routing. Prefer a feat branch for new features.',
    // P-COND-CA40B4848921 ST3 — per git-mode base-branch label + hint.
    base_branch_with_mode: 'Base Branch ({mode})',
    branch_mode_self_integration: 'self-managed · integration line',
    branch_mode_self_feature: 'self-managed · feature branch',
    branch_mode_self_release: 'self-managed · release line',
    branch_mode_cloud: 'cloud repo',
    branch_hint_self_integration: 'Every conversation commits straight onto this shared line. /push lands back on {branch}; /merge releases {branch} to master.',
    branch_hint_self_feature: 'Commits go to this feature branch. /push lands back on it; /merge promotes it into the integration line {integration}.',
    branch_hint_self_feature_direct: 'Commits go to this feature branch. /push lands back on it; /merge promotes it into the release line master.',
    branch_hint_self_release: 'Commits go straight to the release line; after /push it is final, no /merge needed.',
    branch_hint_cloud: 'A dedicated branch is created for this conversation; after /push, open an MR in Codeup.',
    branch_search_placeholder: 'Search branches...',
    selected: 'Selected',
    // Dev sub-mode selector (P-COND-AB7B8345E64D)
    dev_submode: 'Mode',
    dev_submode_branch: 'Branch dev',
    dev_submode_operation: 'Operation',
    dev_submode_branch_hint: 'Normal feature development on the selected branch.',
    dev_submode_operation_hint: 'A coordination agent that organizes scattered branches, cleans up, and merges content — it does not develop new features.',
    collab_submode: 'Focus',
    collab_submode_general: 'General',
    collab_submode_planning: 'Planning',
    collab_submode_design: 'Design',
    collab_submode_production: 'Documents',
    collab_submode_general_hint: 'General purpose: text editing, contract review, announcements, workflows. All skills available on demand.',
    collab_submode_planning_hint: 'Content creation & research: market research, social media intelligence, copywriting.',
    collab_submode_design_hint: 'Visual design & brand: illustrations, layout, VI guidelines, image generation.',
    collab_submode_production_hint: 'Document production: PPT presentations, Word reports, Excel spreadsheets.',
    // Fork lineage (方案3 clone reuse) — source selector labels
    source_label: 'Based on',
    source_branch: 'Base branch (clone)',
    source_conversation: 'Existing conversation (uncommitted)',
    // Forkable conversation dropdown (Req 3)
    fork_conv_placeholder: 'Select a source conversation',
    fork_conv_empty: 'No reusable conversations',
    fork_conv_loading: 'Loading conversations...',
    fork_status_pending: 'Uncommitted',
    fork_status_committed: 'Committed',
    fork_status_clean: 'Unmodified',
    fork_readonly_warning: 'The new conversation inherits the source conversation\u2019s uncommitted code changes; the source conversation becomes read-only and can no longer commit code.',
    // Create new branch (Req 5/6)
    create_branch: '\uFF0B New branch',
    create_branch_title: 'Create a new branch',
    create_branch_base: 'Base branch',
    create_branch_name: 'New branch name',
    create_branch_name_placeholder: 'e.g. feat_login_redesign',
    create_branch_name_hint: 'The branch name must contain \u201Cfeat\u201D. e.g. feat_login_redesign',
    create_branch_submit: 'Create',
    create_branch_creating: 'Creating...',
    create_branch_error: 'Failed to create branch',
    group_chat: 'Group Chat',
    group_hint: 'Multi-user conversation. Members can toggle agent mode individually.',
    add_members: 'Add Members',
    members_later: 'Members can be added after creation.',
    loading_users: 'Loading users...',
    sync_wecom: 'Sync with WeCom',
    wecom_sync_hint: 'Messages will sync between Web and WeCom. Only one WeCom conversation per project.',
    web_only_hint: 'Web-only conversation (no WeCom sync).',
    create: 'Create',
    // SP-U2 workspace mode selector (shown only for mode-enabled projects)
    workspace_mode: 'Workspace Mode',
    mode_unset: 'None (unbound)',
    mode_dev: 'Dev (development workspace)',
    mode_collab: 'Collab (collaboration workspace)',
    mode_hint: 'Bind this conversation to a workspace mode. Leave unset to keep it mode-unbound.',
    default_provider: 'Default (project setting)',
    no_token: ' (no token configured)',
  },

  // ── Continuation Dialog ─────────────────────────────────────────
  continuation: {
    title: 'Continue Conversation?',
    start_fresh: 'Start Fresh',
    continue: 'Continue',
  },

  // ── Code Block ──────────────────────────────────────────────────
  code_block: {
    copy_title: 'Copy to clipboard',
  },

  // ── Continuation Dialog ─────────────────────────────────────────
  continuation_dialog: {
    title: 'Continue Conversation?',
    start_fresh: 'Start Fresh',
    continue: 'Continue',
    close: 'Close dialog',
    aria_label: 'Continuation prompt',
  },

  // ── Project Selector ────────────────────────────────────────────
  project_selector: {
    all_projects: 'All Projects',
    select_project: 'Select project',
  },

  // ── Approval Page ───────────────────────────────────────────────
  approval: {
    header: 'Template Approvals',
    nav_admin: 'Admin',
    nav_chat: 'Chat',
    tabs: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      all: 'All',
    },
    stats: {
      approved_today: 'Approved Today',
      rejected_today: 'Rejected Today',
      total: 'Total',
    },
    empty: 'No proposals match this filter.',
    table: {
      title: 'Title',
      type: 'Type',
      action: 'Action',
      source: 'Source',
      status: 'Status',
      date: 'Date',
    },
    sections: {
      summary: 'Summary',
      target_file: 'Target File',
      agent_reasoning: 'Agent Reasoning',
      challenge_review: 'Challenge Review',
      changes: 'Changes',
      proposed_content: 'Proposed Content',
      current_content: 'Current Content',
      admin_note: 'Admin Note',
    },
    admin_note_placeholder: 'Admin note (optional)',
    badges: {
      fast_track: 'fast-track',
      large: 'large',
    },
    operation_failed: 'Operation failed',
  },

  // ── Org Shell Navigation ─────────────────────────────────────────
  orgNav: {
    section: {
      platform: 'Platform',
      current_org: 'Current Org',
      settings: 'Settings',
      account: 'Account',
    },
    nav: {
      all_orgs: 'All Organizations',
      platform_settings: 'Platform Settings',
      overview: 'Overview',
      usage: 'Usage',
      subscription: 'Subscription',
      members: 'Members',
      projects: 'Projects',
      audit: 'Audit Log',
      coming_soon: 'Coming soon',
      org_settings: 'Org Settings',
      profile: 'Profile',
      back_to_chat: 'Back to Chat',
      my_devices: 'My Devices',
      skill_center: "Skill Center",
      desktop_grants: 'Desktop Access Approvals',
      org_skills: 'Skills',
      desktop_audit: 'Desktop Audit Log',
    },
    role: {
      platform_admin: 'Platform Admin',
      owner: 'Owner',
      admin: 'Admin',
      member: 'Member',
    },
    aria: {
      open_menu: 'Open menu',
      close_menu: 'Close menu',
      user_menu: 'User menu',
      main_nav: 'Main navigation',
      current_org: 'Current organization',
      select_org: 'Select organization',
    },
    logged_out: 'Not logged in',
  },

  // ── Admin Shell Navigation ───────────────────────────────────────
  adminNav: {
    org_management: 'Org Management',
    pricing: 'Subscription Pricing',
  },

  // ── Subscription Pricing config (P-COND-LRT ST3) ────────────────────
  pricing: {
    crumb: '配置 / Subscription Pricing',
    title: 'Subscription Pricing',
    sub: 'Edit subscription prices per tier × period (normalized-USD, full-period price; blank = not purchasable). Takes effect immediately, no restart.',
    loading: 'Loading…',
    error_load: 'Failed to load subscription pricing',
    error_save: 'Save failed',
    saved: 'Saved',
    save: 'Save',
    saving: 'Saving…',
    reset: 'Reset',
    tier_col: 'Tier',
    not_purchasable: 'Not purchasable',
    invalid_positive: 'Price must be positive or blank',
    period: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semiannual',
      annual: 'Annual',
    },
  },

  // ── Plan-tier quota config (P-COND-LRT) ─────────────────────────────
  planQuota: {
    crumb: '配置 / Plan Quota',
    title: 'Plan Quota Config',
    sub: 'Configure each plan tier\'s monthly internal usage ceiling (enforcement basis).',
    info: 'These are INTERNAL usage ceilings (normalized-USD cost basis) for quota gating and overage control. They are NOT shown to end users — users only see qualitative tier differences and the RMB price. Blank (null) = unlimited.',
    loading: 'Loading…',
    error_load: 'Failed to load plan quotas',
    error_save: 'Save failed',
    saved: 'Saved',
    save: 'Save',
    saving: 'Saving…',
    reset: 'Reset',
    tier_col: 'Plan Tier',
    limit_col: 'Monthly Ceiling',
    note_col: 'Note',
    unit: 'norm-USD',
    null_unlimited: 'null · unlimited',
    unlimited_placeholder: 'Unlimited',
    invalid_positive: 'Ceiling must be positive or blank',
    reload_note: 'Takes effect immediately (config hot-reload, no restart). Changes are audit-logged.',
    tier: {
      internal: 'internal',
      trial: 'trial',
      standard: 'standard',
      enterprise: 'enterprise',
    },
    desc: {
      internal: 'Internal accounts, never billed',
      trial: 'Default registration-trial quota',
      standard: 'Small / mid teams',
      enterprise: 'Large teams',
    },
  },

  // ── Model pricing standards (P-COND-LRT) ────────────────────────────
  modelPricing: {
    crumb: '配置 / Model Pricing',
    title: 'Model Pricing Standards',
    sub: 'Configure each provider\'s per-model token unit prices (USD / 1M tokens) and cost-normalization coefficients.',
    loading: 'Loading…',
    error_load: 'Failed to load model pricing',
    error_save: 'Save failed',
    saved: 'Saved',
    save: 'Save all pricing',
    saving: 'Saving…',
    reset: 'Reset',
    coefficients_title: 'Cost normalization coefficients',
    param_col: 'Parameter',
    value_col: 'Value',
    model_col: 'Model',
    invalid_nonneg: 'All values must be non-negative numbers',
    reload_note: 'Takes effect on hot-reload (lru_cache invalidates on mtime), no code change or restart. Changes are audit-logged.',
    field: {
      input: 'input',
      output: 'output',
      cache_write: 'cache_write',
      cache_read: 'cache_read',
    },
  },

  // ── Org Subscription management (P-COND-LRT) ────────────────────────
  orgSub: {
    title: 'Subscription',
    lead: 'View your current plan, renew / extend, and browse order history. Currently a sandbox test-payment environment.',
    loading: 'Loading…',
    error_load: 'Failed to load subscription',
    retry: 'Retry',
    current_plan: 'Current Plan',
    active_badge: 'Active',
    k_tier: 'Plan Tier',
    k_period: 'Subscription Period',
    k_price: 'Current Plan Price',
    k_expiry: 'Expiry Date',
    list_price_note: 'Shown as the plan list price; the prorated differential on a mid-cycle upgrade is settled at checkout.',
    compare_renew: 'Compare & Renew',
    sandbox_badge: 'Sandbox Pay',
    sandbox_note: '⚠ This is a sandbox / test payment — no real charge occurs. This notice will be removed once a real payment gateway is integrated.',
    order_history: 'Order History',
    no_orders: 'No orders yet.',
    pay_success: 'Payment succeeded — subscription updated.',
    pay_failed: 'Payment did not complete.',
    pay_error: 'Payment request failed. Please try again.',
    cmp: {
      feature: 'Feature',
      standard: 'Standard',
      enterprise: 'Enterprise',
      projects: 'Manageable Projects',
      projects_std: 'Up to 5',
      projects_ent: 'Unlimited',
      seats: 'Member Seats',
      seats_std: '10 seats',
      seats_ent: 'Unlimited',
      concurrency: 'Dispatch Concurrency',
      concurrency_std: 'Standard',
      concurrency_ent: 'High-concurrency priority queue',
      token_quota: 'Token Quota',
      token_quota_std: 'Standard monthly quota',
      token_quota_ent: 'Enterprise token quota = 4X Standard',
      support: 'Support',
      support_std: 'Ticket support',
      support_ent: 'Dedicated contact + priority response',
      price: 'Subscription Price',
    },
    form: {
      tier: 'Plan Tier',
      period: 'Subscription Period',
      pay: 'Proceed to Pay',
      paying: 'Processing…',
      order_amount: 'Amount Due',
    },
    tier: {
      internal: 'Internal',
      trial: 'Trial',
      standard: 'Standard',
      enterprise: 'Enterprise',
    },
    period: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semiannual',
      annual: 'Annual',
    },
    status: {
      paid: 'Paid',
      pending: 'Pending',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    order: {
      id: 'Order No.',
      tier: 'Plan',
      amount: 'Amount',
      period: 'Period',
      status: 'Status',
      time: 'Time',
    },
  },

  // ── Org Pages (batch A) ─────────────────────────────────────────────
  org: {
    overview: {
      crumb_org: 'Organization',
      crumb_overview: 'Overview',
      loading: 'Loading...',
      title_fallback: 'Loading...',
      desc: 'Organization dashboard — key metrics and recent activity.',
      error_prefix: 'Failed to load:',
      retry: 'Retry',
      kpi: {
        members: 'Members',
        projects: 'Projects',
        monthly_points: 'Monthly Points (Used / Quota)',
        monthly_ratio: 'Monthly Usage Ratio',
        monthly_cost: 'This Month Cost',
      },
      project_tooltip: 'Only shows projects you can access',
      points_unit: ' pt',
      points_unlimited: ' pt · Unlimited',
      ratio_empty_title: 'No monthly quota set for this organization',
      ratio_empty: 'Unlimited / No quota',
      quota_section: {
        title: 'Quota Usage',
        sub: 'Quota in points · Monthly = plan total, Daily/Weekly = throttle guards (hidden when unlimited)',
      },
      activity_section: {
        title: 'Recent Activity',
        sub: 'Key events in the last 7 days · Full log in Audit page',
      },
      audit_unavailable: 'Audit API not yet available',
      audit_unavailable_desc: 'The backend audit-log GET route will be wired in a future tier.',
      no_activity: 'No activity in the last 7 days',
      tier_today: 'Today',
      tier_week: 'This Week',
      tier_month: 'This Month',
    },
    usage: {
      crumb_usage: 'Usage',
      title: 'Usage / Token Usage',
      desc: 'Per-member token consumption and cost by month.',
      error_prefix: 'Failed to load:',
      retry: 'Retry',
      period_label: 'Month',
      kpi: {
        monthly_points: 'Monthly Points (Used / Quota)',
        monthly_ratio: 'Monthly Usage Ratio',
        member_count: 'Members',
        monthly_cost: 'This Month Cost (USD)',
      },
      points_unlimited: ' pt · Unlimited',
      ratio_unlimited: 'Unlimited / No quota',
      quota_section: {
        title: 'Quota Usage',
        sub: 'Quota in points · Monthly = plan total, Daily/Weekly = throttle guards (hidden when unlimited)',
      },
      detail_section: {
        title: 'Member Usage Details',
        sub_period: 'Period {period}',
        sub_default: 'Aggregated by user',
      },
      table: {
        user: 'User',
        model: 'Model',
        input: 'Input',
        output: 'Output',
        cache_create: 'Cache Create',
        cache_read: 'Cache Read',
        cost_usd: 'Cost (USD)',
        points: 'Points',
      },
      model_label: 'Model',
      model_filter_all: 'All Models',
      no_data: 'No usage data for this month',
    },
    members: {
      crumb_members: 'Members',
      title: 'Organization Members',
      desc: 'Manage members and roles. Owner protection: at least 1 owner required; transfer before removing.',
      loading: 'Loading...',
      error_prefix: 'Failed to load:',
      retry: 'Retry',
      search_placeholder: 'Search username / email / department / title\u2026',
      search_aria: 'Search members',
      role_filter_aria: 'Role filter',
      role_all: 'All Roles',
      role_admin: 'Admin',
      role_member: 'Member',
      status_filter_aria: 'Status filter',
      status_all: 'All Statuses',
      status_joined: 'Joined',
      status_pending: 'Pending',
      add_member: '+ Add Member',
      table: {
        account: 'Account',
        role: 'Role',
        department: 'Department',
        title_col: 'Title',
        manager: 'Manager',
        status: 'Status',
        joined_at: 'Joined At',
        actions_aria: 'Actions',
      },
      role_label: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
      },
      status_label: {
        joined: 'Joined',
        pending: 'Pending',
      },
      owner_remove_title: 'Owner cannot be removed \u2014 transfer ownership first',
      resending: 'Sending\u2026',
      resend: 'Resend Invite',
      revoking: 'Revoking\u2026',
      revoke: 'Revoke',
      edit_role: 'Edit Role',
      edit_info: 'Edit Info',
      remove: 'Remove',
      more: 'More ▾',
      less: 'Less ▴',
      empty: {
        unavailable: 'Member list temporarily unavailable',
        unavailable_desc: 'The backend GET /api/v1/orgs/{org_id}/members route will be wired in a future tier.',
        no_members: 'No members yet',
        no_members_desc: 'Use "+ Add Member" to invite the first member.',
        no_match: 'No matching members',
        no_match_desc: 'Try adjusting the filters.',
      },
      error: {
        resend_not_available: 'Resend not yet available',
        no_permission: 'Insufficient permissions',
        resend_failed: 'Resend failed, please try again',
        revoke_not_found: 'Invitation no longer exists',
        revoke_failed: 'Revoke failed, please try again',
        revoke_confirm: 'Are you sure you want to revoke this invitation? This cannot be undone.',
      },
      departments: {
        section_title: 'Department Management',
        section_hint:
          'Departments = registered departments ∪ departments in use by members. After adding a department, collab projects auto-create the domains/<dept>/ directory.',
        add_btn: '+ Add Department',
        empty: 'No departments yet. Click "+ Add Department" to register the first one.',
        loading: 'Loading departments…',
        load_failed: 'Failed to load departments',
        modal_title: 'Add Department',
        name_label: 'Department Name',
        name_placeholder: 'e.g. Finance',
        name_hint: 'Non-empty, ≤100 chars; cannot duplicate an existing department.',
        submit: 'Create & Select',
        submitting: 'Creating…',
        cancel: 'Cancel',
        error_exists: 'Department already exists',
        error_invalid: 'Department name must be non-empty and at most 100 characters',
        error_generic: 'Create failed, please try again',
        select_unrestricted: '(No specific department / all departments)',
        select_add: '+ Add department…',
        select_hint:
          'Must be chosen from the list, no free text. Blank = no specific department (under the default tier, equivalent to access to all department directories).',
        // P-COND-3F91C4A8D2E5 ST3 — department removal entry (chip ×).
        remove_aria: 'Remove department {name}',
        remove_title: 'Remove this department',
        rename_btn: 'Rename',
        rename_aria: 'Rename department {name}',
        rename_title: 'Rename department',
        rename_label: 'New name',
        rename_hint: 'Renaming works by department ID; the old name in members, invitations and project grants is updated too.',
        rename_submit: 'Save',
        rename_submitting: 'Saving…',
        rename_same: 'The new name equals the current name',
        rename_exists: 'A department with this name already exists',
        rename_forbidden: 'Only org admins can rename departments',
        rename_failed: 'Rename failed, please retry',
        rename_reverted: 'Rename failed on the collab file side; everything was restored to the old name.',
        rename_partial: 'Rename was only partly applied and has been audited; an admin must repair it manually.',
        rename_done: 'Renamed "{old}" to "{name}"',
        remove_confirm:
          'Remove department "{name}"?\n\n' +
          '· {members} member(s) will become department-less (the members themselves are kept)\n' +
          '· {invitations} pending invitation(s) will have their department cleared\n' +
          '· {projects} project(s) will lose this department\u2019s authorization\n' +
          '· Directories and files are KEPT — nothing is deleted\n\n' +
          'Member department assignment cannot be restored from the UI.',
        remove_impact_failed:
          'Impact preview failed, removal cancelled: {error}',
        remove_failed: 'Failed to remove department, please try again',
        // P-COND-05EF0D660DAA ST3 — department visibility pause / resume list.
        list_label: 'Departments',
        pause_hint:
          "Pausing a department hides this org's collab projects from its members — they can neither see nor select them. Nothing on disk is deleted; resuming restores the original authorization path unchanged. A member granted access individually on a project keeps it: individual grants always prevail over a department pause.",
        paused_badge: 'Paused',
        pause_btn: 'Pause',
        resume_btn: 'Resume',
        toggling: 'Working…',
        pause_aria: 'Pause department {name}',
        resume_aria: 'Resume department {name}',
        pause_failed: 'Failed to pause department, please try again',
        resume_failed: 'Failed to resume department, please try again',
        toggle_not_found:
          'That department no longer exists, or you lack permission to manage this org',
        // Second confirmation before a pause (design annotation: "Clicking
        // Pause raises a confirmation showing the affected headcount and the
        // project list."). Resume is purely restorative — one click, no
        // confirm. Members and projects only: a pause does NOT touch pending
        // invitations, so naming them here would state something untrue.
        pause_confirm:
          'Pause department "{name}"?\n\n' +
          "· {members} member(s) will no longer see or select this org's collab projects\n" +
          '· {projects} collab project(s) affected: {project_list}\n' +
          '· Nothing on disk is deleted; resuming restores the original authorization path unchanged\n' +
          '· Anyone granted access individually on a project keeps it — individual grants prevail',
        pause_confirm_projects_none: 'none',
        pause_confirm_projects_sep: ', ',
        pause_confirm_projects_more: ' and {count} more',
        pause_impact_failed: 'Impact preview failed, pause cancelled: {error}',
      },
    },
    projects: {
      crumb_projects: 'Projects',
      title: 'Organization Projects',
      desc: 'All projects in this organization. Click a project name to manage member permissions.',
      loading: 'Loading...',
      error_prefix: 'Failed to load:',
      retry: 'Retry',
      search_placeholder: 'Search projects\u2026',
      search_aria: 'Search projects',
      status_filter_aria: 'Status filter',
      create_btn: '+ New Project',
      create_btn_title: 'Create org-scoped project',
      create_btn_no_perm: 'Requires org admin or platform admin permission',
      create_btn_at_cap: 'Project cap reached ({count}); contact a platform admin to raise the quota',
      status: {
        all: 'All Statuses',
        active: 'Active',
        archived: 'Archived',
        planning: 'Planning',
        suspended: 'Suspended',
      },
      table: {
        project: 'Project',
        access_method: 'Access Method',
        status: 'Status',
        approval: 'Approval',
        created_at: 'Created At',
        actions_aria: 'Actions',
      },
      access_method_label: {
        local: 'Local (local)',
        ssh_agent: 'SSH Agent',
        api: 'API Tunnel',
        docker: 'Docker',
        ssh_docker: 'SSH + Docker',
        desktop: 'My computer (Desktop)',
      },
      approval: {
        required: 'Approval Required',
        direct: 'Direct',
      },
      detail_link: 'Details',
      empty: {
        title: 'No projects found',
        no_projects: 'This organization has no projects.',
        no_match: 'Try adjusting your filters.',
        no_permission: 'Projects in this organization are visible only to administrators.',
      },
    },
    components: {
      quota: {
        unlimited_label: ' \u00b7 Unlimited / No quota',
        over_label: 'Over quota {pct}%',
        subscription_period: 'Subscription Period',
        current_period_start: 'Current Period Start',
        subscription_expires: 'Subscription Expires',
        period: {
          monthly: 'Monthly',
          quarterly: 'Quarterly',
          semiannual: 'Semi-annual',
          annual: 'Annual',
        },
      },
    },
    audit: {
      title: 'Audit Log',
      breadcrumb: 'Audit Log',
      desc: 'Immutable record of all key events within the organization (P1: 8 categories · all retained ≥1 year).',
      filter: {
        region_label: 'Audit Filters',
        action_type: 'Event Type',
        actor: 'Performed By',
        actor_placeholder: 'Name / Email',
        target: 'Target',
        target_placeholder: 'Target project / user',
        time_range: 'Time Range',
      },
      action_options: {
        all: 'All (8 categories)',
      },
      time_options: {
        day1: 'Last 24 hours',
        day7: 'Last 7 days',
        day30: 'Last 30 days',
        custom: 'Custom…',
      },
      table: {
        time: 'Time (+08:00)',
        event: 'Event',
        actor: 'Performed By',
        target: 'Target',
        details: 'Details',
      },
      empty: {
        filtered_title: 'No records match the current filters',
        no_records_title: 'No audit records in this time range',
        filtered_desc: 'Try adjusting the event type or time range.',
        unavailable_title: 'Audit Log API Coming Soon',
        unavailable_desc: 'The backend GET /api/admin/audit route will be wired in a future tier.',
        forbidden_title: 'You do not have permission to view the audit log for this organization',
        forbidden_desc_admin: 'Backend returned 403 — please check your session permissions.',
        forbidden_desc_member: 'Audit logs are only available to organization administrators. Contact the organization owner for access.',
      },
      error_prefix: 'Load failed: {msg}',
      pager: {
        showing: 'Showing {start}-{end} / Total {total}',
        zero: 'Total 0',
        prev: '‹ Previous',
        next: 'Next ›',
      },
    },
    settings: {
      title: 'Organization Settings',
      breadcrumb: 'Settings',
      error_prefix: 'Load failed: {msg}',
      retry: 'Retry',
      readonly_banner: 'You are viewing this page as an organization member. Editing requires organization admin permissions.',
      section: {
        ownership: {
          title: '⚠️ Ownership Management',
          sub: 'Owner operations are highly sensitive and require WeCom IM two-factor verification. Visible to current owner only.',
          transfer_btn: 'Transfer Ownership…',
        },
        basic: {
          title: 'Basic Info',
          name_label: 'Organization Name',
          slug_label: 'Slug (URL)',
          slug_hint: 'Slug cannot be changed after creation',
          desc_label: 'Description',
          desc_placeholder: '(none)',
          edit_btn: 'Edit',
          edit_title_enabled: 'Edit form will be wired in T6c (EditOrgModal)',
          edit_title_disabled: 'Platform admin permission required',
        },
        quota: {
          title: 'Quota Limits',
          loading: 'Loading quota info…',
          unlim: 'Unlimited',
          kpi_tooltip: 'This metric requires platform admin permission',
          label_plan: 'Plan',
          label_status: 'Status',
          label_trial_ends: 'Trial Ends',
          label_providers: 'Allowed Model Providers',
          kpi_members: 'Members',
          kpi_projects: 'Projects',
          kpi_monthly_budget: 'Monthly Token Budget',
          kpi_member_limit: 'Member Limit',
          kpi_project_limit: 'Project Limit',
          edit_quota_btn: 'Edit Quota',
          edit_quota_contact: 'Edit Quota (Contact Platform Admin)',
          edit_quota_title_enabled: 'Edit Quota',
          edit_quota_title_disabled: 'Contact Platform Admin',
          locked_title: 'Platform admin permission required to view commercial config',
          locked_desc: 'Plan / status / token limits and other fields are currently only available to platform admins.',
        },
        subscribe: {
          title: 'Self-serve Subscribe / Upgrade',
          stub_hint:
            'Sandbox payment (demo): this is a payment STUB — confirming does NOT charge you. A real payment gateway is pending.',
          tier_label: 'Target plan',
          period_label: 'Billing period',
          period_monthly: 'Monthly',
          period_quarterly: 'Quarterly',
          period_half_year: 'Semiannual',
          period_yearly: 'Annual',
          create_intent_btn: 'Checkout',
          amount_label: 'Amount due',
          session_label: 'Checkout session',
          first_purchase: 'First purchase',
          upgrade: 'Upgrade (prorated differential)',
          sandbox_pay_btn: 'Pay now (sandbox)',
          cancel_btn: 'Cancel',
          subscribed: 'Subscribed',
          expires: 'expires',
        },
        // P-COND-4E646C4E5E39 Sub-Task 4 — cross-org project reference grants.
        // The Chinese side is the design contract's verbatim copy; this is its
        // faithful English rendering (same key set, same placeholders).
        grants: {
          incoming_title: 'Organizations We Have Authorized',
          incoming_hint:
            'These organizations may reference our projects read-only. Revoking takes effect immediately — the mounted directory disappears from their containers.',
          outgoing_title: 'Access Requests We Have Sent',
          outgoing_hint:
            'Once approved, our organization admins can pick the other side\u2019s projects under “Reference Other Projects (read-only)” on a project page.',
          // Status labels read from opposite ends: "incoming" is the grantor\u2019s
          // view, "outgoing" the applicant\u2019s — so one `approved` row reads
          // "Authorized" on one card and "Access granted" on the other.
          tag_in_approved: 'Authorized',
          tag_in_pending: 'Awaiting your confirmation',
          tag_in_rejected: 'Rejected',
          tag_in_revoked: 'Revoked',
          tag_out_approved: 'Access granted',
          tag_out_pending: 'Awaiting their confirmation',
          tag_out_rejected: 'Rejected',
          tag_out_revoked: 'Revoked',
          meta_in_pending:
            '{date} requested · by {who} · reason: {reason}',
          meta_in_approved: '{date} approved by {who}',
          meta_in_closed: '{date} closed',
          meta_out_approved: '{date} approved',
          meta_out_pending:
            '{date} submitted, waiting for their admin to confirm',
          meta_out_closed: '{date} closed',
          revoke_btn: 'Revoke',
          approve_btn: 'Approve',
          reject_btn: 'Reject',
          view_refs_btn: 'View references',
          apply_btn: '＋ Request access to another organization',
          confirm_revoke:
            'Revoke the authorization granted to “{name}”? The mounted directory disappears from their containers immediately.',
          processing: 'Processing…',
          loading: 'Loading…',
          unknown_org: '(unknown organization)',
          no_reason: '(none given)',
          error_prefix: 'Failed to load grants: {msg}',
          incoming_empty_title: 'No organizations authorized yet',
          incoming_empty_desc:
            'When another organization requests access, the request appears here for you to confirm.',
          outgoing_empty_title: 'No access requests sent yet',
          outgoing_empty_desc:
            'Ask the other organization\u2019s admin for their slug, then use the button below to send a request.',
        },
        danger: {
          title: 'Danger Zone',
          sub: 'After deactivation the organization enters an isolated state. Can be restored by platform admins.',
          deactivate_btn: 'Deactivate Organization…',
        },
      },
      loading: 'Loading…',
    },
    profile: {
      title: 'Profile',
      breadcrumb_account: 'Account',
      breadcrumb_profile: 'Profile',
      error_prefix: 'Load failed: {msg}',
      retry: 'Retry',
      loading: 'Loading…',
      basic_info: {
        title: 'Basic Info',
        name_label: 'Name',
        username_label: 'Username',
        user_id_label: 'user_id',
        email_label: 'Email',
        phone_label: 'Phone',
        wecom_label: 'WeCom ID (wecom_userid)',
        role_label: 'Global Role',
        save_btn: 'Save',
        save_success: 'Saved',
        badge_platform_admin: 'Platform Admin',
        badge_member: 'Regular User',
      },
      contact: {
        verified: '✓ Verified',
        not_set: '— (not set)',
        change: 'Change',
        add: 'Add',
        step1: 'Step 1: Enter the new address and send a code',
        step2: 'Step 2: Enter the code you received',
        email_placeholder: 'Enter new email address',
        phone_placeholder: 'Enter new phone number',
        send_code: 'Send code',
        code_hint: 'The code is sent to the new address and valid for 10 minutes',
        resend: 'Resend',
        resend_countdown: 'Resend ({n}s)',
        code_placeholder: '6-digit code',
        confirm: 'Confirm change',
      },
      password: {
        title: 'Change Password',
        current_label: 'Current Password',
        new_label: 'New Password',
        confirm_label: 'Confirm New Password',
        submit: 'Update Password',
        success: 'Password updated',
        mismatch: 'The two new passwords do not match',
        policy_hint: 'At least 8 characters, must contain both letters and digits',
      },
      memberships: {
        title: 'Organization Memberships',
        col_org: 'Organization',
        col_slug: 'Slug',
        col_role: 'Role',
        col_created: 'Created At',
        empty_title: 'Not a member of any organization',
        empty_desc: 'Visit "All Organizations" or contact a platform admin to join.',
        error_title: 'Organization list temporarily unavailable',
        error_desc: 'Please try again later or check your network connection to the backend.',
      },
    },
    projectDetail: {
      loading: 'Loading…',
      not_found: {
        breadcrumb_projects: 'Projects',
        breadcrumb_not_found: 'Not Found',
        title: 'Project Not Found',
        desc: 'This project may have been deactivated or does not belong to this organization.',
        back_btn: '← Back to Project List',
      },
      error_prefix: 'Load failed: {msg}',
      retry: 'Retry',
      desc_fallback: 'Project Details — member permissions, quota, danger operations.',
      kpi: {
        members: 'Members',
        // Collab-only: the number of access.yaml `users:` entries — a collab
        // project's admission list lives there, not in project_members
        // (which is always empty for collab).
        authorized_users: 'Authorized Individuals',
        approval_mode: 'Approval Mode',
        status: 'Status',
        default_tier: 'Default Permission Tier',
        approval_required: 'Approval Required',
        direct_execute: 'Direct Execute',
        tier_tooltip: 'Non-platform-admin callers cannot see this field',
      },
      basic: {
        title: 'Basic Info',
        sub: 'The access method of an existing project cannot be changed.',
        name_label: 'Project Name',
        name_aria: 'Project Name (read-only)',
        method_label: 'Access Method',
        method_aria: 'Access Method (read-only)',
        path_label: 'Project Path',
        path_aria: 'Project Path (read-only)',
        ssh_label: 'SSH Host',
        ssh_aria: 'SSH Host (read-only)',
        approval_label: 'Requires Approval',
        approval_aria: 'Requires Approval (read-only)',
        approval_yes: 'Yes — Two-phase dispatch gate',
        approval_no: 'No — Direct execute',
        created_label: 'Created At',
        created_aria: 'Created At (read-only)',
      },
      members: {
        title: 'Member Management',
        sub: 'Permission tiers control what Claude agents can do (read_only → code_changes → full_autonomy). read_only may only use Planning mode.',
        add_btn: '+ Add Project Member',
        col_username: 'Username',
        col_role: 'Project Role',
        col_tier: 'Permission Tier',
        col_can_select_model: 'Can select model?',
        can_select_title: 'Allow this member to freely pick the model in new sessions/messages',
        can_select_locked: 'Admins can always select (locked)',
        // col_deny_access / deny_access_* removed with the 成员管理 table's
        // deny column (P-COND-9C4E17B0D2A5 ST3): its gate was nested inside
        // isDevLike so it never reached a real collab project. The capability
        // now lives in the directory-permission editor's `users:` rows.
        role_owner: 'Owner',
        role_member: 'Member',
        edit_btn: 'Edit',
        edit_title: 'Edit role and permission tier',
        remove_btn: 'Remove',
        removing: 'Removing…',
        remove_title_owner: 'Project owner cannot be removed — transfer ownership first',
        remove_title_member: 'Remove this member (soft delete)',
        confirm_remove: 'Are you sure you want to remove "{name}" from this project? This is a soft delete and can be re-added.',
        empty_title: 'No project members yet',
        empty_desc: 'This project has no members yet. Click "+ Add Project Member" to add from organization members.',
        error_title: 'Member list temporarily unavailable',
        dismiss: 'Dismiss',
        owner_protect: 'Cannot remove "{name}": this member is the project owner, please transfer ownership first.',
      },
      quota: {
        title: 'Quota',
        sub: 'Project-level quota inherits from the organization level (Phase 3 v1.1).',
        default_model: 'Default Model',
        model_not_set: 'Not configured',
        model_tooltip: 'Not configured — inherits global default',
        cost_title: 'Current Month Cost',
        cost_not_set: 'Not configured',
        cost_tooltip: 'Project-level cost aggregation not yet exposed — only org-level month_to_date_cost available',
        approval_mode: 'Approval Mode',
        two_phase: 'Two-phase dispatch gate',
        direct: 'Direct execute',
      },
      settings: {
        title: 'Project Settings',
        sub: 'Editable project configuration — description, default permission tier, default model, status and approval mode.',
        error_prefix: 'Save failed: {msg}',
        saved: 'Settings saved.',
        save_btn: 'Save Settings',
        saving: 'Saving…',
        save_title_enabled: 'Save project settings',
        save_title_disabled: 'No changes — modify any field to save',
        desc_label: 'Description',
        tier_label: 'Default Permission Tier',
        // P-COND-056A6CA445F9 (3tier) — pinned disabled option for a stored legacy tier
        tier_legacy_option: 'legacy tier (retired, effective as read_only)',
        model_label: 'Default Model',
        model_default_option: 'Leave blank to use global default',
        model_select_default_label: 'Project-Level Model Select Default',
        model_select_default_inherit: 'Follow org/member default (fail-open)',
        model_select_default_allow: 'Allow (project default: can select model)',
        model_select_default_deny: 'Deny (project default: cannot select model)',
        provider_label: 'Model Provider Config',
        provider_section_sub:
          'Choose the model providers and default models available to this project. Both org and project admins can configure this.',
        provider_platform_default: 'Platform default',
        provider_default_radio: 'Default provider',
        provider_not_in_org: 'Not authorized',
        provider_none_error: 'Select at least one usable provider.',
        review_mode_label: 'Review Mode',
        review_mode_sub: 'Whether and how a proposal is reviewed automatically before it reaches you for approval. Affects this project only.',
        review_mode_opt_inherit: 'Follow platform default',
        review_mode_opt_inherit_help: 'Uses the platform admin\'s global setting; follows it when the platform changes',
        review_mode_opt_A: 'Author self-check',
        review_mode_opt_A_help: 'The same model that wrote the proposal re-checks it in its session: fast, but the same viewpoint',
        review_mode_opt_B: 'Cross-model review',
        review_mode_opt_B_help: 'A model from another family or a higher tier reviews the full text; falls back to author self-check when no reviewer is available',
        review_mode_opt_off: 'No review',
        review_mode_opt_off_help: 'Proposals skip automatic review, are marked "not reviewed (project setting)" and go straight to you for approval',
        review_mode_effective: 'Effective: {mode} (source: {source})',
        review_mode_src_project: 'project setting',
        review_mode_src_admin: 'platform setting',
        review_mode_src_env: 'platform default',
        review_mode_src_collab: 'collab project',
        provider_hint:
          'Checked = provider is usable for this project (new-session provider picker lists only checked ones); the default-model initial value is the platform admin\u2019s "platform default" and can be overridden here per project; default provider = the provider used for auto tasks and the new-session initial selection. architecture_mode/backend are auto-derived from the chosen model.',
        status_label: 'Status',
        approval_label: 'Requires Approval',
        approval_yes: 'Yes — Two-phase dispatch gate',
        approval_no: 'No — Direct execute',
        planning_label: 'Planning Folder',
        max_turns_label: 'Max Turns',
        max_turns_placeholder: 'Leave blank to use global default',
        collab_scope_note: 'Collab project — only applicable settings shown',
        git_card_title: 'Git Lifecycle & Management Mode',
        git_card_sub:
          'Configure whether this project uses the Git lifecycle, and the branch management mode for remote projects.',
        git_label: 'Git Lifecycle',
        git_mode_label: 'Management Mode',
        git_mode_b: 'Mode B — Codeup managed',
        git_mode_a: 'Mode A — Self-managed',
        git_help_toggle: 'What are these settings? Click to expand',
        git_help_enabled:
          'Git lifecycle ON: each dispatch works in an isolated per-conversation clone; changes are git-tracked and revertible in one step.',
        git_help_disabled:
          'Git lifecycle OFF: dispatches edit the remote project workspace directly — no branch, no snapshot. Switch back anytime to quickly undo the mechanisms above.',
        git_help_mode_b:
          'Mode B (Codeup managed): every dispatch auto-creates a feat_YYYYMMDD branch and pushes it to Codeup — suited to team workflows needing PR / review.',
        git_help_mode_a:
          'Mode A (self-managed): conversations commit straight onto the selected base branch and never auto-create a feat_ branch; multiple conversations share one line — suited to projects that manage branches themselves (e.g. thegym). You can still create a feat_ base branch manually; with an integration line set, a feature branch /merge goes into the integration line first, and the integration line /merge releases to master.',
        git_branch_default_label: 'Default Branch',
        git_branch_default_placeholder: 'Leave blank to default to master',
        git_branch_default_hint:
          'When set, used as the fallback branch for operation (统筹) mode sessions and self-managed (方式A) sessions. Leave blank → master.',
        git_integration_branch_label: 'Integration line (optional)',
        git_integration_branch_placeholder: 'e.g. feat_main; leave blank for none',
        git_integration_branch_hint:
          'When set, promotion is two-level: a feature branch /merge goes into the integration line, and the integration line /merge releases to master; operation sessions never auto-advance master. Blank → a feature branch /merge goes straight to master.',
        git_ready_title_ready: 'Repository ready',
        git_ready_title_not_ready: 'Repository not ready — the setting was NOT applied',
        git_ready_title_failed: 'Preparation failed — the setting was NOT applied',
        git_ready_title_check_ok: 'Check passed: grey steps will be done automatically on enable',
        git_ready_title_check_blocked: 'Check failed: resolve the red items first',
        git_ready_title_skipped: 'No repository preparation needed (not a self-managed Mode A remote project)',
        git_ready_not_run: 'Not run',
        git_ready_changes_label: 'Changes already made on the host:',
        git_ready_changes_none: 'None',
        git_ready_suggest_label: 'Suggested directories to ignore (kept out of the repo):',
        git_ready_ignore_continue: 'Ignore these and continue',
        git_ready_keep_label: 'Keep in the repository: {dir} (looks like source code)',
        git_ready_check_btn: 'Check readiness',
        git_ready_checking: 'Checking…',
        git_ready_saving_hint: 'Preparing the repository on the host — this can take about 1 minute, please keep this page open…',
        git_ready_timeout: 'Request timed out: the host may still be working. Click "Check readiness" later to confirm.',
        git_ready_step_path: 'Project directory',
        git_ready_step_repo_root: 'Repository location',
        git_ready_step_preflight: 'Dry run',
        git_ready_step_host_only_tracked: 'Managed files',
        git_ready_step_push_target: 'Push target',
        git_ready_step_ignore: 'Ignore rules',
        git_ready_step_baseline: 'Initial commit',
        git_ready_step_branch: 'Default branch',
        git_ready_step_origin: 'Backup repository',
        git_ready_step_rules: 'Rule files',
        modes_label: 'Enable Collab/Dev Modes',
        modes_off_hint: 'Disabled — the project uses a single workspace root (current behavior).',
        modes_dev_hint: 'Dev mode root (dev): {path}',
        modes_locked_hint: 'Enabled (cannot be changed once configured)',
        modes_path_error: 'Cannot derive workspace paths: the project path must be absolute.',
        ip_label: 'IP Whitelist',
        scripts_label: 'Allowed Scripts',
        scripts_placeholder: 'One script path per line, leave blank for no restriction',
        skill_overrides_label: 'Skill Tier Overrides',
        extra_mounts_label: 'Extra Mounts',
        enabled: 'Enabled',
        disabled: 'Disabled',
      },
      danger: {
        title: 'Danger Operations',
        sub: 'Transfer ownership or deactivate the project — only project owners, org admins, or platform admins can execute.',
        transfer_title: 'Transfer Project Ownership',
        transfer_sub: 'Transfer ownership to another active member of this organization (enter their user_id).',
        transfer_btn: 'Transfer Ownership…',
        processing: 'Processing…',
        deactivate_title: 'Deactivate Project',
        deactivate_sub: 'Soft delete — project history is retained, but members will lose access. Contact platform admin to restore.',
        deactivate_btn: 'Deactivate Project',
        dismiss: 'Dismiss',
        transfer_prompt: 'Transfer project ownership: enter the user_id of the new owner (must be an active member of this organization).',
        user_id_empty: 'user_id cannot be empty.',
        transfer_success: 'Project ownership transferred.',
        deactivate_confirm: 'Are you sure you want to deactivate project "{name}"? This is a soft delete — project history is retained, but members will lose access. Contact platform admin to restore.',
        deactivate_success: 'Project deactivated.',
        deactivate_forbidden: 'Deactivating projects is currently restricted to platform admins (no org-level deactivation route yet).',
      },
      selfService: {
        panel_title: 'Knowledge Self-Service',
        panel_sub: 'Upload raw spec documents to add industry skills, department SOPs, or workflow definitions to the collab workspace.',
        skill_card_title: 'Industry Skill',
        skill_card_sub: 'Add industry-specific capabilities (contract review, budget calc, recruiting JD…).',
        skill_target: '→ .claude/skills/<slug>/SKILL.md',
        skill_add_btn: '+ Add Skill',
        skill_field_slug: 'Skill Identifier (slug)',
        skill_field_slug_placeholder: 'e.g. contract-review',
        skill_field_hint: 'Additional Notes (hint)',
        skill_field_hint_placeholder: 'Optional — extra context for the conversion agent',
        skill_field_file: 'Spec Document',
        skill_dialog_title: 'Add Industry Skill',
        skill_uploading: 'Uploading…',
        skill_upload_btn: 'Upload & Convert',
        sop_card_title: 'Department SOP',
        sop_card_sub: 'Archive department standards / policy documents (PDF / DOCX / MD, classified P1).',
        sop_target: '→ domains/<dept>/standards/',
        sop_add_btn: '+ Upload SOP',
        sop_field_dept: 'Department',
        sop_field_dept_placeholder: 'Select department',
        sop_field_file: 'SOP Document',
        sop_dialog_title: 'Upload Department SOP',
        sop_uploading: 'Uploading…',
        sop_upload_btn: 'Upload',
        sop_list_label: 'Archived files for',
        sop_empty: 'No SOPs archived yet.',
        workflow_card_title: 'Workflow',
        workflow_card_sub: 'Define process flows (goal / trigger / steps / owner).',
        workflow_target: '→ workflows/<name>.md',
        workflow_add_btn: '+ Define Workflow',
        workflow_field_name: 'Workflow Name',
        workflow_field_name_placeholder: 'e.g. monthly-close',
        workflow_field_hint: 'Additional Notes (hint)',
        workflow_field_hint_placeholder: 'Optional — extra context for the conversion agent',
        workflow_field_file: 'Spec Document',
        workflow_dialog_title: 'Define Workflow',
        workflow_uploading: 'Uploading…',
        workflow_upload_btn: 'Upload & Convert',
        badge_ready: 'Ready',
        badge_converting: 'Converting',
        converting_hint: 'Converting — the agent is writing the file. Refresh in a moment.',
        btn_retry: 'Retry',
        btn_delete: 'Delete',
        btn_download: 'Download',
        btn_cancel: 'Cancel',
        empty_skills: 'No skills yet.',
        empty_workflows: 'No workflows yet.',
        error_prefix: 'Error: {msg}',
        delete_confirm: 'Delete skill "{slug}"? This cannot be undone.',
        usage_help_toggle: 'When to use which? (Skill · SOP · Workflow scenarios)',
        usage_skill_label: 'Industry Skill',
        usage_skill:
          'A reusable capability/methodology. Once converted, the agent auto-loads it and reuses it across conversations (e.g. contract review, budgeting, recruiting JDs).',
        usage_sop_label: 'Department SOP',
        usage_sop:
          'A department\'s authoritative policy/standard original — archived only (PDF/DOCX/MD, not converted) for the agent to consult and follow.',
        usage_workflow_label: 'Workflow',
        usage_workflow:
          'A multi-step process for one task (goal / trigger / steps / owner), converted into workflows/<name>.md.',
        admin_only_badge: 'Org admins only',
        loading: 'Loading…',
        file_picker_default: 'Choose file…',
        load_failed: 'Load failed',
        upload_failed: 'Upload failed',
        download_failed: 'Download failed',
        retry_failed: 'Retry failed',
        delete_failed: 'Delete failed',
        validate_slug_empty: 'Slug cannot be empty',
        validate_select_spec: 'Please select a spec document',
        validate_select_dept: 'Please select a department',
        validate_select_sop: 'Please select an SOP document',
        validate_name_empty: 'Name cannot be empty',
      },
      badge: {
        collab: 'Collab Project',
        dev: 'Dev Project',
      },
      enter_conversation: {
        cta: 'Enter Conversation',
        cta_aria: 'Open chat for this project',
      },
      accessYaml: {
        // P-COND-CDD515B7ACD4 — this card governs ONLY which directories a
        // subject may write once already inside the workspace (the PATH
        // authorization chain). "Who can see this project" is a separate
        // chain, owned by the deptVisibility block below. The old
        // "Department Permissions" title conflated the two and is what caused
        // the incident.
        title: 'Directory Permissions (access.yaml)',
        sub: 'Set which directories each department or member may write / must not touch AFTER they are already inside this project workspace. This does NOT decide who can see the project — visibility is configured under "Department Visibility" below. On save, the format is validated and a consistency check is run against ACCESS.md (conflicts are rejected).',
        loading: 'Loading…',
        warnings_label: 'Notice:',
        no_file: 'No access.yaml file yet; it will be created on save.',
        save_btn: 'Save Department Permissions',
        saving: 'Saving…',
        saved: 'Department permissions saved',
        conflict_access_md: 'Configuration conflicts with ACCESS.md; save rejected',
        conflict_stale: 'The file was modified by someone else; please reload before saving',
        invalid: 'Invalid access.yaml format (check YAML syntax and tier values)',
      },
      // P-COND-CDD515B7ACD4 — department visibility grants
      // (projects.department_tiers). Orthogonal to the access.yaml card above:
      // this decides WHO SEES the project in their sidebar, access.yaml
      // decides which directories they may write once inside.
      deptVisibility: {
        title: 'Department Access',
        sub: 'Permission level and directory grants managed together — one row per department.',
        col_dept: 'Department',
        col_level: 'Permission Level',
        col_allow: 'Writable Directories',
        col_deny: 'Blocked Directories',
        col_model_select: 'Model Selection',
        model_select_inherit: 'Inherit project default',
        model_select_allow: 'Allow',
        model_select_deny: 'Deny',
        col_status: 'Status',
        mode_hidden: 'Not visible',
        level_read_only: 'Read-only',
        level_code_changes: 'Editable',
        legacy_tier_label: 'Advanced tier — adjust it in access.yaml',
        hint_not_visible: 'Permissions do not apply while the project is not visible',
        scope_floor: 'Shared areas only',
        scope_all: 'All writable',
        scope_specific: 'Specific directories',
        scope_floor_note:
          'Shared areas only (domains/shared, workflows, domains/_personal are writable); the department\'s own directory is not',
        scope_all_note:
          'All writable (the entire collab workspace, incl. every department directory and org-knowledge.md; agent rules, the permission notes and audit records excepted)',
        hint_allow_paths: 'Pick writable directories',
        hint_allow_paths_empty: 'Leave empty = nothing writable (fail-closed)',
        hint_deny_paths: 'Pick blocked directories',
        hint_deny_paths_note: 'Blocked directories apply to BOTH reads and writes, and outrank writable directories',
        hint_readonly_no_paths: 'Read-only has no write tools; writable directories do not apply',
        empty: 'This organization has no departments registered yet — assign departments on the Members page first.',
        save_btn: 'Save Department Permissions',
        saving: 'Saving…',
        saved: 'Department permissions saved',
        badge_granted: 'Visible',
        badge_paused: 'Department paused',
        unknown_hint: 'Removed from the organization; the grant is kept and never auto-cleaned',
        cases_title: 'Permission Reference & Examples',
        case1_title: 'Editable · all writable',
        case1_effect:
          'Agent has write tools and can write the whole collab workspace (incl. every department directory and org-knowledge.md); agent rules, permission notes and audit records excepted',
        case1_note: 'For: ops, engineering, teams needing full project access',
        case2_title: 'Editable · specific writable directories',
        case2_effect: 'Writes limited to the listed directories; the rest stay readable. Empty = nothing writable',
        case2_note: 'For: departments that only maintain their own directories',
        case3_title: 'Editable + blocked directories',
        case3_effect: 'All writable, but blocked directories reject reads and writes alike (protected files are never writable)',
        case3_note: 'The two columns are independent and freely combined; blocked wins',
        case4_title: 'Read-only + blocked directories',
        case4_effect: 'No write tools; blocked directories reject even reads, the rest stay readable',
        case4_note: 'Every level except "not visible" can set blocked directories',
        template_title: 'Permission template',
        template_current: 'Current: {name}',
        template_custom: 'Custom (directories set per department)',
        template_a_name: 'A · Department collaboration',
        template_a_default: 'A · Department collaboration (default)',
        template_a_desc: 'Edit own department working files; other departments read-only',
        template_b_name: 'B · Department isolation',
        template_b_desc: 'Edit own department working files; other departments not readable by default',
        template_sub: 'Pick a template; use "Adjust" for exceptions and "Custom directories" only for special cases',
        template_hidden_label: 'Hidden from all departments',
        template_shared_label: 'Readable by all departments',
        template_pick_dept: '+ Choose departments',
        template_none_selected: 'None selected',
        template_picker_ok: 'OK',
        template_apply_btn: 'Apply to departments',
        template_apply_hint: 'Switching A/B updates the template-following rows below at once; "Apply to departments" makes the other rows follow too. Takes effect after "Save department permissions". Standards edits still need approval',
        template_base_dropped_hidden: 'B does not use "Hidden from all departments": the list ({list}) stops applying once saved; switch back to A to restore it (before saving)',
        template_base_dropped_shared: 'A does not use "Readable by all" or extra reads: the list ({list}) stops applying once saved; switch back to B to restore it (before saving)',
        template_dirty: 'Unsaved',
        template_dialog_title: 'Apply template {base} to departments? ({count} visible departments will follow it)',
        template_dialog_follow: 'These departments will follow the template: {list}',
        template_dialog_replaced: 'Will be replaced: the custom directories of {list}. To keep them, switch that row back to "Custom directories" after saving',
        template_dialog_invisible: 'Not-visible departments ({list}) will not be opened',
        template_dialog_open_invisible: 'Also open {count} not-visible departments as "Can edit · follows template"',
        template_dialog_open_invisible_hint: 'Will open: {list}. Each row can still be set to read-only or not visible before saving',
        template_dialog_paused: 'Paused departments ({list}) will not be opened',
        template_dialog_shared_readonly: 'The public area becomes read-only unless departments or people are selected',
        template_dialog_convert: 'Switch {count} "personal read-only" members to follow their department (recommended)',
        template_dialog_convert_hint: 'If unchecked: these {count} people stay read-only and cannot write their department',
        template_dialog_cancel: 'Cancel',
        template_dialog_confirm: 'Fill draft',
        template_col_scope: 'Writable scope',
        template_col_others: 'Other departments',
        template_scope_dept: 'Own department working files',
        template_scope_public: 'public area',
        template_scope_public_yes: 'editable',
        template_scope_public_no: 'read-only',
        template_scope_follow: 'follows template',
        template_scope_readonly: 'Read-only, cannot write any file',
        template_to_custom: 'Custom writable dirs',
        template_to_follow: 'Follow template',
        template_others_all: 'All readable',
        template_others_all_except: 'All readable except {list}',
        template_others_none: 'Other departments not readable',
        template_others_some: 'Not readable; readable: {list}',
        template_others_inherited: 'Other departments readable (from template): ',
        template_custom_write: 'Custom writable dirs',
        template_adjust_global_hint: 'This edits the project template and affects every department that follows it',
        template_adjust: 'Adjust',
        template_adjust_title: '{dept} · other departments',
        template_adjust_hide_self: 'Hide this department from all departments',
        template_adjust_shared_self: 'This department is readable by all departments',
        template_adjust_extra_read: 'This department may additionally read:',
        template_adjust_a_also_hidden: 'Other hidden departments (unchecking will unhide for all departments)',
        template_people_title: 'Personal exceptions · {count}',
        template_people_readonly: 'personal read-only',
        template_people_editable: 'personal editable',
        template_people_full: 'personal full access',
        template_people_custom: 'personal custom directories',
        template_people_custom_note: '(public-area setting does not apply)',
        template_people_denied: 'access denied',
        template_people_to_inherit: 'Follow department',
        template_people_keep: 'Kept',
        template_people_no_dept: 'No department',
        template_people_pending: 'Personal changes are saved together with "Save department permissions"',
        template_save_users_failed: 'Department permissions saved, but personal settings failed to save: {error}',
        shared_write_label: 'Public area editable by',
        shared_write_none: 'Read-only (default)',
        shared_write_selected: 'Selected departments / people',
        shared_write_all: 'All editable departments',
        shared_write_depts: 'Departments',
        shared_write_users: 'People',
        shared_write_pick_dept: '+ Choose departments',
        shared_write_pick_user: '+ Choose people',
        shared_write_hint_none: 'Public area = shared materials (shared) and workflows. Read-only for everyone (except admins and the project owner).',
        shared_write_hint_selected: 'Only the selected departments and people can edit the public area; others can still read. A read-only department cannot write even if selected; individually selected people are not limited by their department tier.',
        shared_write_hint_all: 'Every "editable" department can edit the public area; read-only departments can only read.',
        shared_write_hint_empty: 'No department or person selected: after saving, the public area stays read-only for everyone',
        shared_write_pick_dept_title: 'Departments that can edit the public area',
        shared_write_pick_user_title: 'People who can edit the public area',
        shared_write_pick_dept_hint: 'Just tick department names — no directories needed.',
        shared_write_pick_user_hint: 'Selected people need no separate personal entry; their department permissions are kept.',
        shared_write_pick_empty: 'Nothing to choose',
      },
      orgTier: {
        label: 'Default org member permission',
        deny: 'Deny (not visible to org members)',
        read_only: 'Read-only',
        code_changes: 'Editable (read/write code)',
        hint: 'Change takes effect immediately for all org members without explicit grants.',
      },
      audit_jump: {
        title: 'Audit Log',
        empty_title: 'View Project Audit',
        empty_desc: 'Complete member/permission/dispatch events within the project are aggregated to the organization audit log page.',
        goto_btn: 'Go to Audit Log →',
      },
      status: {
        active: 'Active',
        archived: 'Archived',
        planning: 'Planning',
        suspended: 'Suspended',
      },
      lifecycle: {
        title: 'Lifecycle Management',
        sub: 'Pausing rejects new dispatches; deletion enters a 30-day recycle window and is fully restorable.',
        status_active: 'Active',
        status_paused: 'Paused',
        status_pending_delete: 'Pending Delete',
        error_close: 'Close',
        processing: 'Processing…',
        pending_banner: 'Pending deletion — {days}d {hours}h left (permanently purged after {deadline}).',
        pending_expired: 'Pending deletion — grace period expired; purge imminent.',
        restore_title: 'Restore Project',
        restore_sub: 'Currently pending deletion. Restore to resume normal use.',
        restore_btn: 'Restore',
        pause_row_title_active: 'Pause Project',
        pause_row_title_paused: 'Resume Project',
        pause_row_sub_active: 'Currently active. Pausing rejects new dispatches.',
        pause_row_sub_paused: 'Currently paused. Resume to accept dispatches again.',
        pause_btn: 'Pause',
        resume_btn: 'Resume',
        delete_title: 'Delete Project (restorable)',
        delete_sub: 'Enters a 30-day recycle window; permanently purged if not restored.',
        delete_btn: 'Delete',
        confirm_delete: 'Delete project "{name}"? It will enter a 30-day recycle window and can be restored anytime.',
      },
      sharedReadonly: {
        title: 'Reference Other Projects (read-only)',
        sub_pre: 'Mount another project read-only under ',
        sub_code: '/workspace/shared/',
        sub_post:
          ' for this project to reference. Eligible sources are this organization\u2019s projects plus projects from any organization that has granted access to this organization.',
        error_close: 'Close',
        select_label: 'Select a project to reference',
        select_placeholder_empty: 'No referenceable projects available',
        select_placeholder: 'Please select a project…',
        // "name (Org Display Name · slug)" — the org qualifier is not
        // decoration: a cross-org list can hold same-named projects, and
        // display names carry no unique constraint, so the slug disambiguates.
        option_label: '{name} ({org} · {slug})',
        add_btn: 'Add read-only reference',
        processing: 'Processing…',
        unknown_project: '(unknown project)',
        mounted_pre: 'Mounted at ',
        mounted_post: ' (read-only)',
        remove_btn: 'Remove',
        empty:
          'No projects referenced yet. Only Docker-family projects support mounting.',
        err_duplicate: 'This project is already referenced.',
        err_cross_org:
          'That organization has not granted this organization access to its projects.',
        err_bad_request:
          'Cannot add: a project cannot reference itself, this project must be Docker-family to mount references, and a cross-org reference also requires both projects on the same host.',
        err_not_found: 'The source project does not exist or has been deleted.',
        dept_section_label: 'Who can access this data?',
        dept_all_label: 'Everyone (default)',
        dept_all_desc: 'Agents of every department and member in this project can see this data source',
        dept_specific_label: 'Specific departments or members',
        dept_specific_desc: 'Only agents of members in the selected departments, or of the selected people, will see this path',
        pick_depts_label: 'Departments',
        pick_users_label: 'Members',
        no_members: 'No members available',
        specific_or_note: 'Visible if EITHER applies: the member\'s department is ticked, or the member is ticked. References are always read-only.',
        chip_nobody: 'Nobody',
        dept_no_depts: 'No departments available; configure departments in org members first',
        dept_chip_all: 'Everyone',
        edit_dept_btn: 'Edit Access',
        save_dept_btn: 'Save',
        cancel_dept_btn: 'Cancel',
        scope_label: 'Reference content scope',
        scope_whole: 'Whole project',
        scope_whole_desc: 'Mount everything in the source project (read-only)',
        scope_subpaths: 'Specific folders',
        scope_subpaths_desc: 'Mount only the folders listed below; nothing else is visible',
        scope_textarea_label: 'Folders (one per line, relative to the source project root)',
        scope_hint: 'e.g. domains/<department>/',
        scope_summary_whole: 'Scope: whole project',
        scope_summary_n: 'Scope: {n} folders',
        scope_err_empty: 'Enter at least one folder, or choose "Whole project"',
        scope_err_invalid: 'Folders must be relative paths without a leading / or "..": {line}',
      },
      branches: {
        title: 'Branch Management',
        subtitle: 'Retiring deletes the worktree immediately; the branch is kept {days} days and can be restored, then permanently deleted',
        tab_active: 'Active {n}',
        tab_retired: 'Retired {n}',
        col_branch: 'Branch',
        col_commit: 'Last commit',
        col_merged: 'Merged',
        col_size: 'Size',
        col_conversations: 'Conversations',
        col_actions: 'Tags & actions',
        merged_yes: 'Merged',
        merged_no: 'Unmerged {n}',
        suggested: 'Suggest retire',
        retire_btn: 'Retire',
        restore_btn: 'Restore',
        retired_badge_days: 'Deleted in {n} d',
        retired_badge_hours: 'Deleted in {n} h',
        retired_badge_due: 'Deleting soon',
        retired_badge_unknown: 'Retired',
        reason: {
          default_branch: 'Default/base branch',
          inflight: 'Task running',
          test_slot: 'Test slot in use',
          dirty: '{n} uncommitted changes',
          checked_out_remote: 'Checked out',
          codex_admission: 'Codex execution in use',
        },
        modal_title: 'Retire branch {name}',
        modal_body: 'The worktree directory is deleted immediately; the branch is kept {days} days, then permanently deleted. You can restore it any time before then.',
        modal_conversations: '{n} linked conversation(s): they cannot dispatch on this branch while it is retired; restoring resumes them.',
        modal_unmerged_ack: 'I understand there are {n} unmerged commits',
        confirm_btn: 'Retire',
        retiring: 'Retiring…',
        cancel_btn: 'Cancel',
        conflict: {
          protected: 'This branch is protected and cannot be retired:',
          unmerged: 'This branch has unmerged commits — tick the confirmation and retry.',
          already_retired: 'This branch is already retired.',
          other: 'Retirement refused:',
        },
        empty_active: 'No active branches',
        empty_retired: 'No retired branches',
        loading: 'Loading branches…',
        degraded: 'Some branch details could not be fetched (remote unreachable or timed out); the list may be incomplete.',
        error_load: 'Failed to load branches.',
        error_generic: 'Operation failed, please retry.',
        error_not_found: 'This branch can no longer be restored (missing or past the retention window).',
        retry_btn: 'Retry',
      },
    },
    accessYaml: {
      tier: {
        read_only: 'Read only (read_only)',
        testing: 'Testing (testing) · legacy, retired',
        code_changes: 'Code changes (code_changes)',
        deploy: 'Deploy (deploy) · legacy, retired',
        full_autonomy: 'Full autonomy (full_autonomy)',
        inherit: 'Follow department (inherit)',
      },
      collabTier: {
        read_only: 'Read only',
        code_changes: 'Editable',
        full_autonomy: 'Full access',
        inherit: 'Follow department',
      },
      fullAutonomyDeptWarning:
        'On the CLI backend this tier bypasses all path restrictions, so cross-department isolation is lost; owners / admins get full autonomy automatically via their identity — do not grant it to a department here. If you only need to open up writes, use the "Writable directories" whitelist.',
      section: {
        departments_title: 'Departments',
        departments_subject: 'Department',
        departments_add: '+ Add Department',
        users_title: 'Individual Users',
        users_subject: 'User',
        users_add: '+ Add User',
      },
      col: {
        tier: 'Permission Tier',
        allow: 'Writable Dirs',
        deny: 'Denied Dirs',
        source: 'Source',
        deny_access: 'Deny Access',
        read_scope: 'Read Scope',
      },
      chip: {
        add: '+ Add',
        done: 'Done',
        manual_glob: 'Advanced: manual glob',
        manual_glob_aria: 'Manual glob',
        remove_aria: 'Remove {item}',
        dir_aria: 'Directory {dir}',
      },
      row: {
        empty: 'No {subject} permissions configured yet',
        // P-COND-CDD515B7ACD4 — most of these rows are AUTO-SEEDED from the
        // org department list by `_maybe_reconcile_collab_departments`
        // (opt-out semantics). Calling them "explicitly configured" made an
        // admin believe he had already granted access. The wording must
        // reflect the real source.
        explicit_source: 'Inherited from org',
        inherited_source: 'Inherited default',
        // Collab: a department with no access.yaml entry does NOT inherit the
        // project default tier — it always lands on the collab floor
        // (read_only + the three shared dirs), so never say "inherited".
        unconfigured_source: 'Not configured',
        unauthorized_tier: 'Not authorized · read-only (shared area only)',
        floor_hint: 'Writable only: ',
        revoked_source: 'Authorization removed',
        revoked_title:
          'This department is explicitly un-authorized (access.yaml keeps an empty entry so the background reconcile cannot re-add it). To re-authorize, change the tier above or add writable directories.',
        tier_aria: '{key} permission tier',
        delete_aria: 'Delete {key}',
        delete_btn: 'Del',
        revoke_aria: 'Remove authorization for {key}',
        revoke_btn: 'Remove access',
        no_departments: 'The organization has no departments yet; set member departments on the "Members" page first, then configure here',
        select_subject_placeholder: 'Select {subject}…',
        select_subject_aria: 'Select {subject}',
        input_placeholder: 'Type a {subject} name and press Enter',
        // AISS-464: the access.yaml `users:` key must be the user UUID
        // (`users.id`) — the backend looks it up via `spec.users.get(user_id)`,
        // so a username key matches nobody. The add-row is now PICK-ONLY from
        // the org roster, so the UUID can no longer be mistyped as a name.
        no_users:
          'No selectable members (the org member list failed to load, or every org member is already configured)',
        unresolved_badge: 'Invalid entry',
        unresolved_title:
          'This key does not match any org member. An access.yaml `users:` key must be the user UUID (users.id); a username or display name saves fine and validates fine but grants nobody, ever. Delete this row and re-add the person with "+ Add User".',
        deny_access_aria: 'Deny access for {key}',
        deny_access_title:
          'Checked = explicitly deny this member access to the project. Highest precedence: applied before department grants and before an org admin\u2019s implicit permission.',
        allow_placeholder: 'e.g. src/ or domains/eng/**',
        deny_placeholder: 'e.g. secrets/**',
      },
      // collab-perm ST2: writable-scope tri-state (same mapping as the department table).
      scope: {
        floor: 'Shared areas + own folder',
        all: 'All writable',
        specific: 'Specific directories',
        floor_note: 'Shared areas + own folder (domains/shared, workflows and your own folder under domains/_personal are writable)',
        all_note:
          "All writable (the entire collab workspace, incl. every department directory and org-knowledge.md; other people's personal folders, agent rules, permission notes and audit records excepted)",
        specific_empty: 'No directory picked = nothing writable (fail-closed); pick "Shared areas + own folder" to restore the shared-area floor',
      },
      // P-COND-1CF189CE8EFA: per-person read scope (absent = follow template).
      readScope: {
        template: 'Follow template (hidden departments not visible)',
        all: 'All departments',
        hint: 'Default is "Follow template". "All departments" makes hidden departments readable, but other people\'s personal folders stay hidden.',
        aria: '{key} read scope',
      },
      note: 'Permissions take effect for sub-agents after the project enables a "Collab / Dev" workspace mode; until then this config is saved only.',
      policy: {
        summary: 'Permission notes (click to expand)',
        rw_intro: 'First distinguish read from write:',
        read: 'Read',
        read_desc: ' — the whole workspace is readable by default; only "Denied directories" block reads, "Writable directories" do not affect reads.',
        write: 'Write',
        write_desc: ' — first set the baseline by "Permission tier", then narrowed by "Writable directories".',
        tier_intro: 'Permission tier',
        tier_intro_desc: ' (default applying to all directories):',
        tier_readonly: 'Read only',
        tier_readonly_desc: ' — globally readable, writes denied everywhere by default; to allow writes, list directories under "Writable directories".',
        tier_editable: 'Editable',
        tier_editable_desc:
          ' — the writable scope is decided by "Writable directories": on a dev project, leaving it empty makes the whole workspace writable; on a collab project pick one of three — "Shared areas only" writes only domains/shared/, workflows/, domains/_personal/; "All writable" = the entire collab workspace (incl. every department directory and org-knowledge.md; agent rules, permission notes and audit records excepted); "Specific directories" writes only the listed ones.',
        tier_full: 'Full access',
        tier_full_desc: ' — not limited by "Writable / Denied directories" (the CLI backend bypasses the path whitelist; use only for special cases like owners / admins).',
        allow_intro: 'Writable directories',
        allow_intro_desc: ' (affect writes only, not reads):',
        allow_filled: ' Filled = writes are limited to these directories; ',
        allow_filled_strong: 'directories not listed remain readable but not editable',
        allow_empty: ' Empty = no extra narrowing, per permission tier: ',
        allow_empty_strong: 'the read-only tier still cannot write',
        allow_empty_tail: '; only the editable tier makes the whole workspace writable (collab projects excepted — see below).',
        deny_intro: 'Denied directories',
        deny_intro_desc: ' (highest priority): listed directories are denied for both read and write — useful to hide secret directories.',
        no_config: 'No departments configured and no individual entries: a dev project falls back to its default permission tier; a collab project always lands on the collab floor — the whole workspace read-only, only the shared area writable.',
        collab_unconfigured: 'Collab project: a department not authorized below is NOT authorized — the whole workspace is read-only and only these three directories are writable: ',
        collab_unconfigured_tail:
          '. The project default permission tier does not apply to collab dispatches, and a department\'s own domains/<dept>/ must be listed explicitly under "Writable directories" to be writable.',
        default_tier_prefix: 'Project default permission tier: ',
        default_tier_suffix: '. Departments not explicitly configured below are pre-filled as greyed "Inherited default" rows; they join the save only after being edited.',
      },
      form_unavailable: 'The current access.yaml uses syntax the form does not yet support ({error}). Please use the "Advanced" raw YAML editor below; the backend still validates on save.',
      parse: {
        expect_list: 'Expected a [ ] list',
        cannot_parse_line: 'Cannot parse line: {line}',
        must_be_group: '{key} must be a group',
        unknown_top_field: 'Unknown top-level field: {key}',
        missing_section: 'Missing parent section',
        cannot_parse_entry: 'Cannot parse entry: {entry}',
        missing_entry: 'Missing parent entry',
        cannot_parse_list_item: 'Cannot parse list item: {item}',
        unknown_tier: 'Unknown permission tier: {tier}',
        unknown_field: 'Unknown field: {key}',
        unknown_read_scope: 'Unknown read scope: {value}',
        unindent_unrecognized: 'Unrecognized indentation: {line}',
      },
      tier_hint: 'When multiple rules match: the permission tier takes the most specific (user > department > project default); writable directories take the smallest scope; denied directories stack (any deny wins).',
      advanced_toggle: '⚙ Advanced (raw YAML)',
      reset_btn: 'Reset (reload)',
      advanced_hint: 'Edit the raw YAML directly (for advanced users; equivalent to the form above).',
    },
  },

  // ── Dispatch Approval Page ──────────────────────────────────────
  dispatch: {
    header: 'Dispatch Approvals',
    nav_chat: 'Chat',
    tabs: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      needs_human: 'Needs human',
      closed: 'Closed',
      my_proposals: 'My Proposals',
      all: 'All',
    },
    empty: 'No proposals match this filter.',
    table: {
      proposal_id: 'Proposal ID',
      title: 'Title',
      project: 'Project',
      submitter: 'Submitter',
      status: 'Status',
      submitted: 'Submitted',
    },
    project_filter: 'Project:',
    all_projects: 'All Projects',
    date_filter: 'Date:',
    clear_dates: 'Clear',
    submitter_filter: 'Submitter:',
    submitter_placeholder: 'Search submitter',
    id_filter: 'Proposal ID:',
    clear_all: 'Clear All',
    sections: {
      submitted_by: 'Submitted By',
      original_task: 'Original Task',
      proposal: 'Proposal',
      source: 'Source',
      expires: 'Expires',
      review: 'Review',
      execution_result: 'Execution Result',
    },
    reviewer_note_placeholder: 'Reviewer note (optional)',
    adopt_recommendations_note: 'Approving adopts every recommendation under "Decisions Needed" in the proposal',
    start_implementation: 'Start Implementation',
    starting: 'Starting...',
    re_execute: 'Re-execute',
    executing: 'Executing...',
    execution_complete: 'Execution Complete',
    reset: 'Reset',
    reviewed_by: 'Reviewed by',
    project_prefix: 'Project:',
    badges: {
      long_running: 'long-running',
    },
    operation_failed: 'Operation failed',
    stats_unavailable: 'Statistics unavailable (insufficient permissions)',
    rollback_button: 'Rollback (disabled)',
    rollback_disabled_reason: 'The legacy rollback reverts every commit made in the shared workspace during that period and cannot guarantee it undoes only this proposal, so it has been disabled. To undo changes, submit a manual revert proposal.',
  },

  // ── Onboarding (M1: OnboardingPage / OnboardingSetupWizard / AcceptInvitePage) ──
  onboarding: {
    // OnboardingPage — three-pane chooser
    chooser: {
      brand_loading: 'Loading',
      welcome_title: 'Welcome to Conductor',
      welcome_desc:
        'You do not belong to any organization yet. Choose one of the ways below to begin — invite code, whitelist matching, or create your own workspace.',
      // Pane 1: invite code
      invite_icon_label: 'Invite code',
      invite_title: 'Join with an invite code',
      invite_desc: 'Got an invite code from an org admin? Enter it to join.',
      invite_label: 'Invite code',
      invite_placeholder: 'Paste invite code',
      invite_redeeming: 'Redeeming…',
      invite_submit: 'Join organization',
      // Pane 2: whitelist (placeholder)
      whitelist_title: 'Whitelist matching',
      whitelist_badge: 'Coming soon',
      whitelist_desc:
        'If your email belongs to a registered organization domain, you will be added automatically after admin review. Please ask your org admin to invite you.',
      whitelist_learn_more: 'Learn more →',
      // Pane 3: self-serve
      self_serve_title: 'Create your workspace',
      self_serve_desc:
        'Start a 14-day free trial now, with the Claude model connected by default — no credit card required.',
      self_serve_slug_label: 'Workspace identifier (slug)',
      self_serve_slug_placeholder: 'my-workspace',
      self_serve_slug_hint:
        'Lowercase letters / digits / hyphens, 1-50 chars (e.g. mayfair-hq); cannot be changed after creation',
      self_serve_name_label: 'Display name (optional)',
      self_serve_name_placeholder: 'Defaults to "Your name\'s workspace"',
      self_serve_creating: 'Creating…',
      self_serve_submit: 'Create workspace',
      self_serve_note:
        'Trial accounts may create only 1 workspace with a 14-day free trial; paid accounts (Standard / Enterprise) may create multiple workspaces — additional workspaces have no trial and must be paid for and billed separately before activation. Up to 3 workspaces can be created within 24 hours.',
      // Slug validation (computed in component)
      slug_too_long: 'Maximum 50 characters',
      slug_invalid:
        'Lowercase letters / digits / hyphens only; first character must be a letter or digit',
      // Toast / result messages (parameterized)
      joined: 'Joined "{org}" ({role})',
      role_admin: 'Admin',
      role_member: 'Member',
      created_pending_payment:
        '"{name}" created. New workspaces have no trial and are independent subscription entities — payment is required to activate.',
      created_with_trial: '"{name}" created (trial until {date})',
      created_no_trial: '"{name}" created',
      // Redeem error messages
      err_redeem_generic: 'Failed to redeem invite code, please try again',
      err_redeem_404: 'Invite code does not exist',
      err_redeem_410: 'Invite code has expired',
      err_redeem_409: 'Invite code is used up, or you are already a member',
      err_redeem_403: 'This invite code does not match your account (email / WeCom)',
      // Create error messages
      err_create_generic: 'Failed to create workspace, please try again',
      err_create_429: 'You can create at most 3 workspaces within 24 hours, please try again later',
      err_create_quota: 'Trial accounts may create only 1 workspace; upgrade to a paid account to create more',
      err_create_409: 'Slug is already taken, please choose another',
      err_create_400: 'Invalid slug format',
    },
    // OnboardingSetupWizard — post-registration guided setup
    wizard: {
      loading: 'Loading',
      progress_nav: 'Wizard progress',
      step_invite: '1. Invite colleagues',
      step_project: '2. Create first project',
      step_done: '3. Done',
      invite_title: 'Invite colleagues',
      invite_desc:
        'You can invite team members to the workspace now, or invite them later in organization settings. This step is skippable.',
      invite_skip: 'Skip this step',
      project_title: 'Create first project',
      project_desc:
        'Create your first project and start using Conductor for AI-assisted development or team collaboration.',
      done_title: 'Setup complete',
      done_desc: 'Your workspace is configured. You can now start using Conductor.',
      done_enter: 'Enter workspace',
    },
    // AcceptInvitePage — public invite redemption form
    accept: {
      // QR landing section
      landing_title: 'You are invited to join',
      landing_subtitle_prefix: 'The inviter',
      landing_subtitle_suffix:
        'invites you to join their team organization and use Conductor together.',
      highlight_multi: 'One conversation, unified command of multi-project agents',
      highlight_secure: 'Org-level permissions and quotas, secure and controllable',
      highlight_fast: 'No install, scan-to-use, get started in minutes',
      landing_cta: 'Join now →',
      landing_self_serve_subtitle:
        'Create your dedicated workspace and get started with Conductor in minutes.',
      landing_self_serve_cta: 'Create now →',
      // FORK A: self-serve workspace creation
      self_serve_title: 'Create your workspace',
      self_serve_subtitle:
        'Fill in the information below to create your account and start your dedicated workspace.',
      self_serve_submitting: 'Creating…',
      self_serve_submit: 'Create account and start',
      // FORK A: create-own-workspace dual entry (choose / login)
      self_serve_choose_subtitle:
        'Create a brand-new workspace — pick the way that works for you.',
      self_serve_choose_register_title: 'Register a new account',
      self_serve_choose_register_desc:
        'No account yet? Sign up and create your first workspace.',
      self_serve_choose_login_title: 'Use an existing account',
      self_serve_choose_login_desc:
        'Already have an account? Log in and create another workspace.',
      self_serve_choose_footnote:
        'Each workspace is billed independently with its own members and projects.',
      self_serve_login_title: 'Log in and create a workspace',
      self_serve_login_subtitle:
        'Log in with your existing account to create a new workspace for it.',
      self_serve_name_label: 'Workspace name',
      self_serve_name_placeholder: 'e.g. My Team',
      self_serve_slug_label: 'Workspace identifier',
      self_serve_slug_placeholder:
        'e.g. my-team (lowercase letters, digits, hyphens)',
      self_serve_login_submitting: 'Logging in and creating…',
      self_serve_login_submit: 'Log in and create workspace',
      self_serve_login_err_slug:
        'Invalid workspace identifier — use lowercase letters, digits, or hyphens (max 50 characters).',
      self_serve_login_err_409:
        'That workspace identifier is already taken — please choose another.',
      self_serve_login_err_429:
        'You have created too many workspaces in the last 24 hours — please try again later.',
      self_serve_login_err_generic:
        'Failed to log in or create the workspace — please try again later.',
      // FORK B: bound-channel join
      bound_title: 'Join organization',
      bound_subtitle:
        'You were invited through a corporate channel — fill in the basic information to complete registration.',
      bound_hint_prefix: 'This invitation was sent via the',
      bound_hint_suffix: 'channel; no email or phone verification code is required.',
      bound_submitting: 'Submitting…',
      bound_submit: 'Create account and join',
      // FORK C: legacy OTP form
      title: 'Join organization',
      subtitle: 'Fill in the information below to create an account and join the organization.',
      bound_email_hint: 'This invitation is bound to an email; please use the invited email to register.',
      bound_sms_hint: 'This invitation is bound to a phone number; please use the invited phone to register.',
      // Shared form fields
      username_label: 'Username *',
      username_placeholder: 'Lowercase letters/digits, at least 8 chars',
      password_label: 'Password *',
      password_placeholder: 'At least 8 chars, must include letters and digits',
      display_name_label: 'Name (optional)',
      display_name_placeholder: 'Real name / nickname',
      email_label: 'Email',
      email_label_required: 'Email *',
      email_label_optional: 'Email (optional)',
      email_placeholder: 'name@example.com',
      phone_label: 'Phone',
      phone_label_required: 'Phone *',
      phone_placeholder: 'Phone number',
      channel_label: 'Verification channel',
      channel_locked_email: 'Email (invitation bound)',
      channel_locked_sms: 'SMS (invitation bound)',
      channel_email: 'Email',
      channel_sms: 'SMS',
      otp_label: 'Verification code',
      otp_placeholder: 'Enter the code you received',
      otp_sending: 'Sending…',
      otp_resend: 'Resend',
      otp_resend_countdown: 'Resend ({seconds}s)',
      otp_send: 'Send code',
      submitting: 'Submitting…',
      submit: 'Create account and join',
      // Validation hints
      hint_username_empty: 'Please enter a username',
      hint_username_space: 'Username cannot contain spaces',
      hint_username_format:
        'Username must be 8-50 characters, only lowercase letters, digits and . _ @ + - symbols',
      hint_username_checking: 'Checking availability…',
      hint_username_available: 'This username is available',
      hint_username_taken: 'This username is already taken, please choose another',
      hint_password_short: 'Password must be at least 8 characters',
      hint_password_format: 'Password must include both letters and digits',
      err_no_contact: 'Please fill in at least an email or phone number',
      err_invalid_email: 'Please enter a valid email address',
      err_invalid_phone: 'Please enter a valid phone number',
      otp_sent_email: 'A verification code has been sent to your email, please check.',
      otp_sent_sms: 'A verification code has been sent to your phone, please check.',
      err_send_otp: 'Failed to send verification code, please try again',
      // Error helper (extractError)
      err_missing_code: 'Invalid invite link: missing invite code.',
      err_404: 'Invite code does not exist or is no longer valid',
      err_410: 'Invite code has expired',
      err_409: 'Invite code has been used, or this account already exists',
      err_403: 'Verification failed — please confirm the code and contact are correct',
      err_422: 'Request fields do not meet requirements',
      err_500: 'Service temporarily unavailable, please try again later',
      err_join_fallback: 'Failed to join, please try again',
      // FORK C dual-CTA — register new / log in with existing (P-COND-8D5436930B90)
      choose_subtitle: 'Choose how you want to join this organization.',
      choose_register_title: 'Register a new account',
      choose_register_desc: "I'm new here — create an account and join.",
      choose_login_title: 'Log in with an existing account',
      choose_login_desc: 'I already have a Conductor account — log in and join.',
      choose_footnote:
        'Either way, your email/phone is verified against the invitation binding when you join.',
      back_to_choose: '← Back — choose a different way to join',
      // FORK C login (log in with existing account)
      login_title: 'Log in to join',
      login_subtitle:
        'Log in with your existing account to join this organization.',
      login_username_label: 'Username *',
      login_username_placeholder: 'Enter your username',
      login_password_label: 'Password *',
      login_password_placeholder: 'Enter your password',
      login_submitting: 'Logging in and joining…',
      login_submit: 'Log in and join',
      login_err_username: 'Please enter your username',
      login_err_password: 'Please enter your password',
    },
  },

  // ── Sub-Agent (M2: SubAgentDrawer / SubAgentCard) ────────────────
  subagent: {
    drawer_title: 'Sub-Agents',
    empty: 'No sub-agent activity',
    status_running: 'Running',
    status_completed: 'Completed',
    status_failed: 'Failed',
    status_pending: 'Pending',
    turns: 'Turns',
    duration: 'Duration',
    tokens: 'Tokens',
    cost: 'Cost',
    view_trace: 'View trace',
    tool_calls: 'Tool calls',
    started_at: 'Started',
    model: 'Model',
    strip_title: 'Sub-agents',
    state_running: 'Running',
    state_finished: 'Done',
    roster_title: 'Sub-agents',
    roster_aria: 'Sub-agent roster',
    roster_list_aria: 'Sub-agent list',
    close: 'Close',
    roster_empty: '(No sub-agents)',
    placeholder: 'Select a sub-agent to view its process',
    no_text_output: '(No text output)',
  },

  // ── Trace View (M2: TraceView) ───────────────────────────────────
  trace: {
    title: 'Execution Trace',
    empty: 'No trace events',
    tool: 'Tool',
    input: 'Input',
    output: 'Output',
    result: 'Result',
    error: 'Error',
    thinking: 'Thinking',
    expand: 'Expand',
    collapse: 'Collapse',
    copy: 'Copy',
    copied: 'Copied!',
    step: 'Step',
    steps_title: 'Execution Steps',
    stream_reconnecting: 'Network issue — live output paused, reconnecting…',
    stream_interrupted: 'Live connection interrupted. Execution status is unconfirmed.',
    stream_recovering: 'Restoring live connection…',
    stream_recover: 'Restore live connection',
    stream_content_error: 'Some live content could not be processed. Still receiving updates.',
    view_process: 'View research process',
    collapse_process: 'Collapse research process',
    loading_process: 'Loading research process',
    load_failed: 'Failed to load research process',
    segment_suffix: ' process segment (read-only replay)',
    empty_replay: 'No replayable process (may have expired)',
    footnote: 'The final answer is shown separately in the chat bubble above (not in this timeline)',
    main_text: 'Main agent · Text',
    sub_prefix: 'Sub-agent · ',
    tool_call: 'Tool call',
    status_done: 'Done',
    status_error: 'Failed',
    status_running: 'Running',
    main_done: 'Main agent · Finished',
    sub_done_suffix: ' · Finished',
    expired: 'Expired',
    not_recorded: 'No process recorded',
    load_more: 'Load more',
    head_truncated: 'The earlier part of this turn was too long and has been omitted',
  },

  // ── Live stream (ST3: StreamingMessage truncation notice) ─────────
  stream: {
    truncated_notice: 'Earlier process omitted (too long)',
    view_full: 'View full process',
  },

  // ── Stage (M3: StageProgressBar / StageAdvanceCard) ──────────────
  stage: {
    advance_title: 'Advance to next stage',
    advance_confirm: 'Confirm advance',
    advancing: 'Advancing…',
    cancel: 'Cancel',
    current_stage: 'Current stage',
    next_stage: 'Next stage',
    advance_hint:
      'Advancing the stage requires full-autonomy approval and can only be done by the project owner.',
    labels: {
      planning: 'Planning',
      requirements: 'Requirements',
      design: 'Design',
      implementation: 'Implementation',
      testing: 'Testing',
      review: 'Review',
      done: 'Done',
      stage0: 'Stage 0 (Reverse understanding)',
      stage1: 'Stage 1',
      stage2: 'Stage 2',
    },
    short_labels: {
      planning: 'Plan',
      requirements: 'Req',
      design: 'Design',
      implementation: 'Impl',
      testing: 'Test',
      review: 'Review',
      done: 'Done',
    },
    // StageProgressBar (M3) — S0-S4 short segment labels keyed by backend
    // planning-stage enum, plus the bar's aria-label.
    progress_aria: 'Project stage progress',
    progress: {
      planning_comprehension: 'S0 Understand',
      planning_requirements: 'S1 Requirements',
      planning_product: 'S2 Product',
      planning_technical: 'S3 Technical',
      planning_implementation: 'S4 Implementation',
    },
    // StageAdvanceCard (M3) — full per-stage prose keyed by backend
    // planning-stage enum (mirrors new_project.py::STAGE_LABELS_ZH).
    descriptions: {
      planning_comprehension: 'Stage 0: Understand the existing project',
      planning_requirements: 'Stage 1: User requirements',
      planning_product: 'Stage 2: Product solution',
      planning_technical: 'Stage 3: Technical solution',
      planning_implementation: 'Stage 4: Implementation plan',
    },
    // StageAdvanceCard (M3) — advance-confirm card controls. {label} is
    // interpolated with the localized current/next stage label.
    advance: {
      ready_title: '{label} is complete — ready to advance',
      summary_hint: 'Summary collected this stage:',
      confirm_label: 'Advance to "{label}"',
      decline_label: 'Keep refining this stage',
      note: 'Confirm is equivalent to typing yes; the card hides automatically once you continue. Shown only while the backend stage_confirm signal is pending.',
    },
  },

  // ── Modals (M5a project/member modals + M5b org modals) ──────────
  modals: {
    // Shared error messages (reused across modals)
    err_field_invalid: 'Request fields do not meet requirements',
    err_service_unavailable: 'Service temporarily unavailable, please try again later',

    // P-COND-4E646C4E5E39 Sub-Task 4 — "Request access to another organization"
    // two-step dialog. The Chinese side is the design contract's verbatim copy;
    // this is its faithful English rendering (same key set, same placeholders).
    orgGrantApply: {
      title: 'Request access to another organization',
      hint: 'Enter the other organization\u2019s unique identifier (slug), provided by their admin. Display names can repeat, so the system matches on slug only.',
      slug_label: 'Organization slug',
      slug_placeholder: 'e.g. the-mayfair',
      resolving: 'Looking up…',
      confirm_label: 'Found — please confirm the target organization:',
      confirm_meta: 'slug: {slug} · status: {status}',
      not_found:
        'No organization matches that slug. Please check the exact spelling with their admin.',
      reason_label: 'Reason for request',
      reason_placeholder:
        'Explain the intended use so their admin can decide',
      cancel: 'Cancel',
      submit: 'Send request',
      submitting: 'Sending…',
      // Lookup-stage failures other than 404: unrelated to spelling, so they
      // get their own copy rather than sending the user hunting for a typo.
      err_403: 'Only organization admins can request cross-org access.',
      err_resolve_generic: 'Lookup failed, please try again later.',
      // Submit-stage failures, branched on orgs.py::create_org_grant statuses.
      err_400_self: 'An organization cannot request access to itself.',
      err_409:
        'A pending or active grant already exists — no need to request again.',
      err_404_submit:
        'The target organization does not exist, or you are not an admin of this organization.',
      err_422: 'Request fields do not meet requirements.',
      err_500: 'Service temporarily unavailable, please try again later.',
      err_generic: 'Failed to send the request, please try again later.',
    },

    // M5a — CreateProjectModal
    createProject: {
      title: 'New project',
      footer_cancel: 'Cancel',
      footer_creating: 'Creating…',
      footer_submit: 'Create and start onboarding',
      name_label: 'Project name *',
      name_placeholder: 'e.g. Acme Group',
      name_hint:
        'Chinese supported, ≤100 chars; unique within the org. The system auto-generates an English identifier used for dispatch routing and file paths.',
      name_invalid: 'Project name is required and must be ≤100 chars',
      desc_label: 'Project description',
      desc_placeholder: 'One sentence describing the project purpose',
      type_label: 'Project type *',
      type_aria: 'Project type',
      type_hint_collab: 'Collab project: for cross-department knowledge work — upload standards/SOPs, build a knowledge base, draft department docs; no code branches, ready to use.',
      type_hint_dev: 'Dev project: for code development — git branch lifecycle is off by default; enable it in project settings.',
      collab_tier_label: 'Collaboration complexity',
      collab_tier_aria: 'Collaboration complexity',
      departments_label: 'Participating departments',
      departments_aria: 'Participating departments',
      departments_hint:
        'Each checked department gets its own working directory and write access. Leave untouched to include all of them.',
      origin_label: 'Project origin *',
      origin_aria: 'Project origin',
      origin_hint:
        'Taking over an existing project will first enter the Stage-0 reverse-understanding phase to absorb the existing code and materials.',
      ingest_label: 'Asset ingestion method',
      ingest_aria: 'Asset ingestion method',
      git_url_label: 'Git repository URL *',
      git_url_placeholder: 'e.g. git@codeup...',
      git_url_required: 'Git clone requires a repository URL',
      ingest_hint:
        'Asset ingestion is only supported for local projects; for remote projects use the project agent to ingest after creation.',
      access_method_remote_hint:
        'Access method defaults to ssh_docker; platform admins can change it in project settings.',
      access_method_label: 'Access method (access_method) *',
      access_method_aria: 'Access method',
      path_label: 'Project path *',
      path_placeholder_local: 'e.g. /home/ec2-user/myproject',
      path_placeholder_remote: 'Project path on the remote host...',
      path_hint: 'Project root directory path (required by the backend)',
      ssh_host_label: 'SSH host *',
      ssh_host_placeholder: 'e.g. 18.142.225.133',
      ssh_port_label: 'SSH port',
      ssh_port_placeholder: 'Default 22 (leave blank for default)',
      ssh_user_label: 'SSH user *',
      ssh_user_placeholder: 'e.g. ec2-user',
      ssh_key_label: 'SSH private key path',
      ssh_key_placeholder: 'e.g. /home/ec2-user/.ssh/id_ed25519 (optional)',
      api_host_label: 'API tunnel host *',
      api_host_placeholder: 'e.g. 18.138.213.6 (SSH entry of the Windows host)',
      api_user_label: 'API tunnel user *',
      api_user_placeholder: 'e.g. Administrator',
      api_key_label: 'API tunnel private key path',
      api_key_placeholder: 'Optional — leave blank for default',
      api_inner_host_label: 'API host (inside tunnel)',
      api_inner_host_placeholder: 'HTTP service address inside the tunnel, e.g. 127.0.0.1 (optional)',
      api_inner_port_label: 'API port (inside tunnel)',
      api_inner_port_placeholder: 'e.g. 8081 (Windows Flask API port, optional)',
      ssh_docker_port_hint: 'SSH port of the remote host; remaining connection info can be filled in on the project detail page after creation',
      need_approval_label: 'Require admin approval before changes are applied',
      need_approval_yes: 'Yes — admin must approve the plan before work begins (recommended)',
      need_approval_no: 'No — execute directly (low-risk projects only)',
      need_approval_hint_collab:
        'Collaboration docs can be rolled back with one click, so prior approval is off by default (can be switched back on).',
      need_approval_hint_dev:
        'Recommended: draft a plan → admin approves → then implement.',
      default_tier_label: 'Default org member permission',
      tier_deny: 'Deny (not visible to org members)',
      tier_read_only: 'Read-only (org members can view content)',
      tier_code_changes: 'Editable (org members can modify code)',
      default_tier_hint:
        'Controls default access for org members who are not explicitly invited. Can be changed any time in project settings.',
      // ACCESS_METHODS const
      access_local_title: 'local',
      access_local_desc: 'Conductor local project (e.g. conductor itself)',
      access_ssh_agent_title: 'ssh_agent',
      access_ssh_agent_desc: 'Remote Linux host (SSH key auth)',
      access_api_title: 'api',
      access_api_desc: 'Windows / third-party HTTP API integration (via SSH tunnel)',
      access_docker_title: 'docker',
      access_docker_desc: 'Local Docker container project',
      access_ssh_docker_title: 'ssh_docker',
      access_ssh_docker_desc: 'Remote Linux + Docker container (most common)',
      access_desktop_title: 'desktop',
      access_desktop_desc: 'My computer (Conductor Desktop daemon — tools run locally)',
      // PROJECT_TYPES const
      type_collab: 'Collaboration',
      type_dev: 'Development',
      // ORIGINS const
      origin_new_title: 'Brand new',
      origin_new_desc: 'A brand-new project, starting from scratch',
      origin_takeover_title: 'Take over existing',
      origin_takeover_desc: 'Take over existing code / materials, with reverse understanding first',
      // COLLAB_TIERS const
      tier_simple_title: 'Simple',
      tier_simple_desc: 'Stage 1 only, usable right after creation',
      tier_general_title: 'General',
      tier_general_desc: 'Stage 1-2 collaborative onboarding',
      // INGEST_METHODS const
      ingest_existing_title: 'Select existing path',
      ingest_existing_desc: 'Understand existing assets in place (no copy)',
      ingest_git_title: 'Git clone',
      ingest_git_desc: 'One-time clone of a remote repository to the project path',
      // M1–M3 step-by-step wizard (P-COND-6FF093311B58)
      wizard: {
        // Step titles / subtitles
        step_type_title: 'What will this project be used for?',
        step_type_desc:
          'Pick the way of working that fits your team — AI capabilities and the guided flow adapt to your choice.',
        step_collab_config_title: 'Set up your collaboration space',
        step_collab_config_desc: 'Fill in the basics — that is all it takes to create the project.',
        step_dev_git_title: 'Git version control',
        step_dev_git_desc:
          'Git records the history of every change and supports branch workflows and approvals. No repository yet? Skip it — you can turn it on any time after creation.',
        step_dev_env_title: 'Development environment',
        step_dev_env_desc:
          'Choose the server environment your code runs on. The Conductor platform needs no setup and is the fastest way to start a new project.',
        step_dev_confirm_title: 'Confirm and create',
        step_dev_confirm_desc:
          'Review the configuration below. Everything can still be changed in project settings after creation.',
        // Step bar + navigation buttons
        bar_step_of: 'Step {current} of {total}',
        nav_back: '← Back',
        nav_next: 'Next →',
        nav_create: '✓ Create project',
        // Project type cards
        card_collab_title: 'Collaboration space',
        card_collab_desc:
          'The AI reads and writes your content directly — ideal for knowledge management, design work, legal and compliance, and other non-code scenarios.',
        card_collab_badge: 'No code required',
        card_collab_chip_1: 'Knowledge base',
        card_collab_chip_2: 'Design work',
        card_collab_chip_3: 'Legal & compliance',
        card_collab_chip_4: 'Industry templates',
        card_dev_title: 'Code development',
        card_dev_desc:
          'A complete engineering management system — built for team development and full-lifecycle project control.',
        card_dev_badge: 'Engineering workflow',
        card_dev_chip_1: 'Version control',
        card_dev_chip_2: 'Multi-agent',
        card_dev_chip_3: 'Full lifecycle',
        card_dev_chip_4: 'Ready to use',
        // Highlights disclosure panel
        feats_expand: 'See highlights',
        feats_collapse: 'Hide highlights',
        feats_collab_1: 'Direct AI read/write — content syncs instantly, no manual import',
        feats_collab_2: 'Industry templates — roles and skill sets preconfigured, ready to use',
        feats_collab_3: 'Permission isolation — each department gets its own knowledge space',
        feats_collab_4: 'Version snapshots — every change is recorded automatically',
        feats_dev_1: 'Full lifecycle — development, testing and operations in one place',
        feats_dev_2: 'Engineering discipline — pre-read checks and multi-agent collaboration',
        feats_dev_3: 'Complete audit trail — logs, history and lessons archived automatically',
        feats_dev_4: 'Beginner friendly — guided step by step, usable out of the box',
        // devConfirm read-only summary
        confirm_section_basic: 'Basics',
        confirm_section_git: 'Version control',
        confirm_section_env: 'Development environment',
        confirm_type: 'Way of working',
        confirm_git: 'Git source',
        confirm_env: 'Environment type',
        confirm_access: 'Access method',
        confirm_approval: 'Approval requirement',
        confirm_note:
          'Every setting can be changed later on the project Settings page without affecting existing work.',
        confirm_name_label: 'Project name *',
        confirm_desc_label: 'Project description',
        // #s2c template selection step (P-COND-F165C3ECC9FC)
        step_template_select_title: 'Choose a project template',
        step_template_select_desc: 'Templates come pre-configured with AI roles and skill sets — or pick the generic template for full customisation.',
        tpl_generic_title: 'General collaboration space',
        tpl_generic_desc: 'Core AI assistant capabilities with no preset industry role. Great for teams with existing workflows or those who want full control.',
        tpl_generic_badge: 'General',
        tpl_generic_chip_1: 'Fully customisable',
        tpl_generic_chip_2: 'Generic skill set',
        tpl_generic_chip_3: 'Flexible config',
        tpl_overseas_title: 'Cross-border enterprise template',
        tpl_overseas_desc: 'Pre-configured for SMEs expanding into SE Asia / Middle East / Europe: market research, multilingual content, compliance triage — all in one.',
        tpl_overseas_badge: 'Industry template',
        tpl_overseas_chip_1: '✓ 8 pre-built skills',
        tpl_overseas_chip_2: '✓ 3 shared knowledge bases',
        tpl_overseas_chip_3: '✓ Overseas advisor role',
        tpl_overseas_coming_soon: 'Coming soon',
        // #s2d Git version control (toggle + hosting choice)
        git_toggle_label: 'Enable Git version control',
        git_conductor_title: 'Conductor-managed',
        git_conductor_badge: 'Recommended for new projects',
        git_conductor_desc: 'The platform manages the repository automatically — no repository URL or key to configure. Ready to use out of the box.',
        git_external_title: 'External repository',
        git_external_badge: 'Requires setup',
        git_external_desc: 'Works with GitHub, GitLab, Gitee, Codeup, and any Git host.',
        git_off_info: 'You can still use AI-assisted collaboration without Git. Enable it any time from project Settings after creation.',
        git_url_label_ext: 'Repository URL (SSH or HTTPS)',
        // #s3d development environment (three options)
        env_conductor_title: 'Conductor platform',
        env_conductor_badge: 'Recommended',
        env_conductor_desc: 'The platform allocates server resources automatically — no SSH setup or server admin experience required.',
        env_linux_title: 'Your own Linux server',
        env_linux_desc: 'Connect via SSH to a Linux host you manage (all major distributions supported).',
        env_desktop_title: 'My computer (Conductor Desktop)',
        env_desktop_desc: 'Run the Conductor Desktop daemon on your own machine and let it dial the platform — your code and files never leave it.',
        env_desktop_path_placeholder: 'C:\\Users\\me\\projects\\myapp or /Users/me/projects/myapp',
        env_desktop_path_hint: 'The project directory on your computer. What is actually readable / writable is narrowed again by the Desktop-side policy.',
        env_windows_title: 'Windows server',
        env_windows_badge: 'Coming soon',
        env_windows_desc: 'Windows environment support is under development — stay tuned.',
        env_nonadmin_hint: 'Contact a platform administrator to configure the server connection.',
        // #s4d confirm step additional copy
        confirm_git_conductor: '✓ Conductor-managed',
        confirm_git_external: '✓ External repository',
        confirm_git_off: 'Not enabled',
        confirm_env_conductor: '✓ Conductor platform',
        confirm_env_linux: '✓ Own Linux server',
        confirm_env_desktop: '✓ My computer (Conductor Desktop)',
        confirm_note_ibox: '💡 Every setting can be changed later on the project Settings page without affecting existing work.',
        // Git auth fields (P-COND-6AA599A6D939)
        git_auth_method_label: 'Authentication',
        git_auth_ssh_key: 'SSH Key',
        git_auth_ssh_key_badge: 'Recommended',
        git_auth_token: 'Token',
        git_auth_token_badge: 'HTTPS access',
        git_auth_password: 'Password',
        git_auth_password_badge: 'Username & password',
        git_key_label: 'SSH Private Key',
        git_key_types: 'RSA · Ed25519 · ECDSA supported',
        git_key_upload: 'Upload file',
        git_key_paste: 'Paste content',
        git_key_path: 'File path',
        git_key_upload_hint: 'Click to upload private key file (id_rsa · id_ed25519 · *.pem)',
        git_key_paste_placeholder: '-----BEGIN OPENSSH PRIVATE KEY-----\n(Paste full private key content)\n-----END OPENSSH PRIVATE KEY-----',
        git_key_path_placeholder: '~/.ssh/id_ed25519',
        git_key_path_hint: 'Absolute path to the private key file on the Conductor server',
        git_key_detected: 'Private key detected',
        git_token_placeholder: 'GitHub: ghp_xxxx · GitLab: glpat-xxxx · Codeup: ...',
        git_username_label: 'Username',
        git_password_label: 'Password',
        git_pwd_warning: '⚠️ GitHub · GitLab · Gitee and most platforms have deprecated password login. Use a Token instead.',
        git_proto_ssh: '🔒 SSH protocol',
        git_proto_https: '🔗 HTTPS protocol',
        // Dev env SSH key (P-COND-6AA599A6D939)
        env_ssh_key_label: 'SSH Private Key',
        env_ssh_key_types: 'RSA · Ed25519 · ECDSA supported',
        env_key_path_placeholder: '~/.ssh/id_ed25519',
      },
    },

    // M5a — AddMemberModal
    addMember: {
      title: 'Invite Members to the Organization',
      context_line: 'You are inviting new members as an organization admin',
      footer_done: 'Done',
      footer_later: 'Maybe later',
      footer_cancel: 'Cancel',
      footer_next: 'Next · Generate links',
      footer_generating: 'Generating…',
      footer_sending: 'Sending…',
      // step indicator (P-COND-C7C2FA6D84CD 3-step)
      step_fill: 'Fill invitees',
      step_links: 'Copy links',
      step_notify: 'Send notice',
      // links stage (Requirement 3)
      links_banner:
        '✅ Generated {n} invitations. Copy the links to forward now; click "Send notice" to also send SMS / email.',
      links_hint:
        'Recipients open the link to register and join; links are valid for 7 days. Even if the notification fails to send, the link still works.',
      links_back: '← Back to edit invitees',
      footer_later_close: 'Send later, close',
      footer_send_notify: 'Send notice →',
      // done stage (Requirement 4)
      done_msg: 'Notifications sent',
      done_sub: 'Sent SMS / email notifications to {n} invitees. They can click the link to register and join the organization.',
      done_sub_fallback: 'If a notification is not delivered, the invitee can still join using the link you copied in the previous step.',
      done_notify_failed:
        'Some notifications failed to send, but the invitation links remain valid — forward the links you copied in the previous step manually.',
      // extractApiError
      err_403: 'Insufficient permissions — only org admins can invite',
      err_404: 'Organization does not exist or is deactivated',
      err_409: 'Organization member count has reached its limit; please increase the quota first or contact a platform admin',
      err_generic: 'Failed to send invitation, please try again',
      // validateRow
      err_contact_empty: 'Contact cannot be empty',
      err_email_invalid: 'Invalid email format',
      // links stage copy buttons
      success_copy_link: 'Copy link',
      success_copied: 'Copied ✓',
      // input view
      contact_required_hint:
        'Only the emails / phones you fill in can accept the invitation — they are the bound, restricted invitation targets. Each invitee receives their own independent link, so fill every row and leave none blank.',
      row_switch_hint:
        '📱 Defaults to phone invite. Click the icon on the left of each row to switch to email (the ⇄ badge means it is switchable). On the right, optionally pick the invitee\u2019s department (can be left blank).',
      row_channel_to_email: 'Currently phone — click to switch to email',
      row_channel_to_phone: 'Currently email — click to switch to phone',
      channel_label: 'Invitation method',
      channel_aria: 'Invitation method',
      channel_email: 'Email invite',
      channel_phone: 'Phone invite',
      channel_hint: 'The system sends a separate invitation link to each invitee; recipients click to join on their own.',
      grant_months_label: 'Gift subscription months',
      grant_months_hint: 'When an invitee accepts and joins, the org receives a paid subscription for this many months; enter 0 to gift nothing (stays on trial).',
      invitees_label: 'Invitees',
      invitees_count: '{n} added',
      invitee_email_placeholder: 'e.g. wangwu@mayfair.cn',
      invitee_phone_placeholder: 'e.g. 13800138000',
      contact_aria: 'Contact {n}',
      remove_row_aria: 'Remove row {n}',
      add_more: '＋ Add another',
      send_failed: 'Failed to send invitation to {contact}: {message}',
      // 3-step flow: step 1 only CREATES the invitation (delivery deferred to
      // step-3 send), so a step-1 failure is a CREATE failure, not a send
      // failure — wording must not mislead (P-COND-C7C2FA6D84CD).
      create_failed: 'Failed to create invitation for {contact}: {message}',
      // already an active member of this org (P-COND-B7B74DECF7AA)
      already_member: '{contact} is already a member of this org — no invite needed',
      // per-row optional department (P-COND-C7C2FA6D84CD)
      row_dept_none: 'None',
      row_dept_aria: 'Department {n}',
    },

    // M5a — AddProjectMemberModal
    addProjectMember: {
      title: 'Add project member',
      footer_cancel: 'Cancel',
      footer_adding: 'Adding…',
      footer_add: 'Add',
      err_409: 'This user is already an active member of this project.',
      err_400: 'This user is not an active member of this organization and cannot be added. Please invite them on the "Organization members" page first.',
      select_label: 'Select from organization members *',
      select_placeholder: '— Select a user —',
      select_hint: 'You may only add from current organization members. To add a new member, invite them on the "Organization members" page first.',
      select_unavailable_hint: '(The organization member list is temporarily unavailable; switched to manual user_id entry.)',
      manual_placeholder: 'Enter user_id',
      role_label: 'Project role *',
      role_member: 'Member (member)',
      role_owner: 'Owner (owner) — only when there is no current owner',
      tier_label: 'Permission tier (permission_tier) *',
      tier_read_only: 'read_only — read-only, Planning mode only (may write planning/docs/testing)',
      tier_testing: 'testing — legacy tier (retired, effective as read_only)',
      tier_code_changes: 'code_changes — can modify source code (requires need_approval two-phase approval)',
      tier_deploy: 'deploy — legacy tier (retired, effective as read_only)',
      tier_full_autonomy: 'full_autonomy — fully autonomous, skips approval',
      tier_hint: 'Controls the highest operation level the Claude agent may execute in this project (Blueprint §5.1)',
      // P-COND-CAAB7244BAE1 Sub-Task 2 — legacy dev-tier badge on collab projects
      tier_legacy_dev_badge: 'Advanced tier (dev-project tier)',
      denyAccess: 'Deny access (access_denied) — explicitly refuse this user on this project',
      denyAccessHint:
        'When checked, this user is explicitly denied on this project: access no longer falls back to the department gate, the org-admin grant, or the project default permission tier. This is NOT the same as removing the member — removal falls through to the org default tier and re-admits them. Platform admins are exempt.',
      note_label: 'Note (stored locally, not sent)',
      note_placeholder: 'e.g. responsible for the frontend of project {projectId}',
    },

    // M5a — EditProjectMemberModal
    editProjectMember: {
      title: 'Edit project member',
      footer_cancel: 'Cancel',
      footer_saving: 'Saving…',
      footer_save: 'Save',
      member_label: 'Member',
      role_label: 'Project role *',
      role_aria: 'Project role',
      role_member: 'Member (member)',
      role_owner: 'Owner (owner)',
      role_hint: 'Downgrading the project owner will be rejected by the backend (409); transfer ownership first.',
      tier_label: 'Permission tier *',
      tier_hint: 'The permission tier controls the operations the Claude agent may execute (read_only → code_changes → full_autonomy). read_only may only use Planning mode.',
      // P-COND-CAAB7244BAE1 Sub-Task 2 — legacy dev-tier badge on collab projects
      tier_legacy_dev_badge: 'Advanced tier (dev-project tier)',
      // TIER_OPTIONS const
      tier_read_only: 'read_only — read-only, Planning mode only',
      tier_testing: 'testing — legacy tier (retired, effective as read_only)',
      tier_code_changes: 'code_changes — can modify source code',
      tier_deploy: 'deploy — legacy tier (retired, effective as read_only)',
      tier_full_autonomy: 'full_autonomy — fully autonomous',
      // P-COND-FACB825338E0 — explicit personal deny (standalone boolean, NOT a tier value)
      denyAccess: 'Deny access (access_denied) — explicitly refuse this user on this project',
      denyAccessHint:
        'When checked, this user is explicitly denied on this project: access no longer falls back to the department gate, the org-admin grant, or the project default permission tier. This is NOT the same as removing the member — removal falls through to the org default tier and re-admits them. Platform admins are exempt.',
      // extractApiError
      err_400: 'Invalid role or permission tier value',
      err_404: 'Project or member does not exist / is deactivated',
      err_409: 'Cannot downgrade the project owner — transfer ownership first',
      err_generic: 'Failed to save, please try again',
    },

    // M5a — EditMemberRoleModal
    editMemberRole: {
      title: 'Change member role',
      footer_cancel: 'Cancel',
      footer_saving: 'Saving…',
      footer_save: 'Save',
      member_label: 'Member',
      owner_hint: 'The owner role cannot be changed — to change the owner, use the "Transfer owner" flow.',
      role_label: 'Organization role *',
      role_aria: 'Organization role',
      role_member: 'Member (member)',
      role_admin: 'Admin (admin)',
      role_hint: 'Admins can invite/remove members, edit member info, and transfer the owner. Downgrading the last admin will be rejected by the backend (409).',
      // extractApiError
      err_403: 'Insufficient permissions — only org admins can change roles',
      err_404: 'Organization or member does not exist / is deactivated',
      err_409: 'Cannot downgrade the last admin',
      err_generic: 'Failed to save, please try again',
    },

    // M5a — EditMemberMetadataModal
    editMemberMetadata: {
      title: 'Edit member info',
      footer_cancel: 'Cancel',
      footer_saving: 'Saving…',
      footer_save: 'Save',
      member_label: 'Member',
      department_label: 'Department',
      title_label: 'Job title',
      title_placeholder: 'e.g. Senior Engineer',
      title_hint: 'Leave blank to clear this field.',
      manager_label: 'Direct manager',
      manager_none: 'None',
      manager_hint: 'Only active members of this organization can be a manager. Selecting "None" clears this field.',
      // extractApiError
      err_403: 'Insufficient permissions — only org admins can edit member info',
      err_404: 'Member does not exist or has been removed',
      err_400: 'The direct manager must be an active member of this organization',
      err_generic: 'Failed to save, please try again',
    },

    // M5b — CreateOrgModal
    createOrg: {
      title: 'New organization',
      footer_cancel: 'Cancel',
      footer_creating: 'Creating…',
      footer_submit: 'Create',
      name_label: 'Organization name *',
      name_placeholder: 'e.g. New Business Unit',
      slug_label: 'Slug (URL) *',
      slug_placeholder: 'e.g. new-bu (lowercase, hyphens)',
      slug_hint: 'Lowercase letters, digits, hyphens; first character must be a letter or digit (1-50 chars)',
      slug_auto_hint: 'Auto-suggested from the organization name; you can override manually',
      slug_invalid: 'Invalid slug format — must match ^[a-z0-9][a-z0-9-]{0,49}$',
      owner_label: 'Owner (auto-added and granted)',
      owner_fallback: 'Current logged-in user',
      owner_hint: 'Defaults to the creator. Can be changed via "Transfer owner" after creation.',
      banner: '📌 After creation you will be automatically added as the sole owner, and prompted to add at least 1 admin to avoid a single point of lockout (Blueprint §6.6).',
      // extractApiError
      err_400_slug: 'Invalid slug format — needs lowercase letters, digits, hyphens (1-50 chars)',
      err_409: 'Slug is already taken, please choose another',
      err_403: 'Only platform admins can create organizations',
      err_422: 'Request fields do not meet the requirements',
      err_500: 'Service temporarily unavailable, please try again later',
      err_generic: 'Failed to create, please try again',
    },

    // M5b — TransferOrgModal
    transferOrg: {
      title: 'Transfer organization owner — {name}',
      footer_cancel: 'Cancel',
      footer_submitting: 'Submitting…',
      footer_send_code: 'Send verification code',
      footer_later: 'Handle later',
      footer_next: 'Next',
      footer_done: 'Done',
      // step rows
      step_select_title: 'Select target',
      step_select_selected: 'Selected: {id}',
      step_select_desc: 'Please select a current admin of this organization as the new owner',
      step_verify_title: 'WeCom IM two-factor verification',
      step_verify_generated: 'Verification token generated; the new owner must confirm within 12 hours',
      step_verify_waiting: 'Waiting for you to initiate — the backend will generate a transfer token valid for 12h',
      step_confirm_title: 'Target user confirms acceptance',
      step_confirm_desc: 'The new owner must accept within 12 hours via WeCom (POST /api/v1/orgs/{id}/transfer/confirm)',
      // step 1
      warn: '⚠️ After a successful transfer you will be automatically downgraded to admin; this operation cannot be undone.',
      eligibility_owner: 'Only the current owner can initiate a transfer.',
      eligibility_admin: 'Only the current owner or a platform admin can initiate a transfer.',
      target_label: 'Target new owner *',
      target_placeholder: 'Enter the target user_id',
      target_hint: 'Member list temporarily unavailable — please enter the user_id directly (a current admin of this organization).',
      loading: 'Loading…',
      select_placeholder: 'Please select',
      admin_suffix: '(admin)',
      target_admin_hint: 'Can only transfer to a current admin. A regular member must be promoted to admin first.',
      reason_label: 'Reason (optional)',
      reason_placeholder: 'Explain the reason for transfer (at least 10 chars, for audit traceability)',
      reason_too_short: 'Reason must be at least 10 characters',
      // step 2
      token_generated: 'A 12h transfer token has been generated. Forward it to the new owner — they confirm acceptance in WeCom.',
      token_label: 'Transfer token',
      copy_token_aria: 'Copy token',
      copied: '✓ Copied',
      copy: 'Copy',
      expires_label: 'Expires at',
      // step 3
      submitted: 'Transfer submitted. After the new owner receives the token and accepts in WeCom, the transfer completes.',
      // extractApiError
      err_403: 'Insufficient permissions — only the current owner can initiate a transfer',
      err_404: 'Organization does not exist or you do not have access',
      err_400: 'The target user must be an active admin of this organization',
      err_500: 'Service temporarily unavailable, please try again later',
      err_generic: 'Operation failed, please try again',
    },

    // M5b — DeactivateOrgModal
    deactivateOrg: {
      title: 'Deactivate organization',
      footer_cancel: 'Cancel',
      footer_submitting: 'Deactivating…',
      footer_submit: 'Deactivate organization',
      banner: 'After deactivation the organization enters an isolated state and can be restored by a platform admin.',
      name_label: 'Organization name',
      impact: 'Will deactivate {projectLabel} projects and {memberLabel} members.',
      confirm_label: 'Enter the organization name to confirm *',
      confirm_hint: 'The "Deactivate organization" button is enabled only when the entered name exactly matches the one above.',
      // extractApiError
      err_403: 'Insufficient permissions — only the org owner or a platform admin can deactivate the organization',
      err_404: 'Organization does not exist or is already deactivated',
      err_409: 'Organization is already deactivated',
      err_500: 'Service temporarily unavailable, please try again later',
      err_generic: 'Failed to deactivate, please try again',
    },

    // M5b — ForceSetOwnerModal
    forceSetOwner: {
      title: 'Change organization owner — {name}',
      title_no_name: 'Change organization owner',
      footer_cancel: 'Cancel',
      footer_submitting: 'Changing…',
      footer_submit: 'Confirm change',
      hint: 'Forcibly transfer ownership to the target user, who will automatically become an admin of the organization.',
      owner_label: 'New owner *',
      loading: 'Loading users…',
      select_placeholder: '— Select a new owner —',
      current_suffix: '(current)',
      load_failed: 'Failed to load user list: {error}',
      reason_label: 'Reason for change (required, {min}-{max} chars) *',
      reason_placeholder: 'e.g. the original owner has left; emergency takeover by platform admin',
      reason_hint: 'Will be recorded in the audit log platform_admin_force_action (entered {n}/{max} chars)',
      reason_invalid: 'Reason must be {min}-{max} characters',
      banner: '⚠️ This operation bypasses the normal transfer confirmation flow and is for platform-admin emergency use only.',
    },

    // M5b — RemoveMemberConfirmModal
    removeMember: {
      title: 'Remove organization member',
      footer_cancel: 'Cancel',
      footer_submitting: 'Removing…',
      footer_submit: 'Confirm removal',
      owner_hint: 'The owner cannot be removed — use the "Transfer owner" flow to hand over ownership first.',
      member_label: 'Member',
      role_label: 'Role',
      warn: 'This operation cannot be undone, but the member can be re-invited.',
      // roleLabel
      role_owner: 'Owner',
      role_admin: 'Admin',
      role_member: 'Member',
      // extractApiError
      err_403: 'Insufficient permissions — only org admins can remove members',
      err_404: 'Member no longer exists',
      err_409: 'Cannot remove: the owner, or a member who still has active project ownership',
      err_500: 'Service temporarily unavailable, please try again later',
      err_generic: 'Failed to remove, please try again',
    },

    // M5b — EditOrgQuotaModal
    editOrgQuota: {
      title: 'Edit organization quota',
      footer_cancel: 'Cancel',
      footer_saving: 'Saving…',
      footer_save: 'Save',
      banner: 'Leave blank to mean "unlimited". Only platform admins can perform this operation.',
      project_limit_label: 'Project limit',
      project_limit_placeholder: 'Unlimited',
      project_limit_invalid: 'Must be a non-negative integer (or leave blank for unlimited).',
      member_limit_label: 'Member limit',
      token_budget_label: 'Monthly token budget (USD)',
      token_budget_invalid: 'Must be a non-negative number (or leave blank for unlimited).',
      derived_label: 'Derived allowance (auto-computed from monthly)',
      derived_hint: 'Daily = monthly / 10; Weekly = monthly / 2.',
      derived_hint_live: 'Daily = monthly / 10 = {daily} · Weekly = monthly / 2 = {weekly}',
      subscribe_title: 'Subscribe / Upgrade',
      current_tier: 'Current plan: {tier}',
      tier_label: 'Plan tier',
      period_label: 'Subscription period',
      subscribed: 'Subscribed',
      upgraded: 'Upgraded',
      submitting: 'Submitting…',
      submit: 'Confirm subscription / upgrade',
      price_prefix: ' · Price ',
      diff_prefix: ' · Difference ',
      expiry_prefix: ' · Expires ',
      expiry_dash: '—',
      // SUBSCRIBE_PERIODS const
      period_monthly: 'Monthly',
      period_quarterly: 'Quarterly',
      period_half_year: 'Half-year',
      period_yearly: 'Yearly',
      // extractApiError
      err_403: 'Insufficient permissions — only platform admins can edit quotas',
      err_404: 'Organization does not exist',
      err_400: 'Invalid input value (cannot be negative or non-numeric)',
      err_500: 'Service temporarily unavailable, please try again later',
      err_generic: 'Failed to save, please try again',
    },
  },

  // ── Skill Picker Panel ───────────────────────────────────────────
  skillPicker: {
    title: 'Skill Management',
    sheetTitle: 'Manage skills',
    openCenter: "Open Skill Center",
    footerHint: "Tap a skill · one at a time",
    plusTitle: "Manage skills",
    plusAria: "Open the skill panel",
    movedHint: "Creating mandatory skills moved to Project details → Project skills",
    installMore: "Install more skills in the Skill Center",
    emptyInstalled: "No skills available yet — install some in the Skill Center",
    source: {
      mandatory: "Required",
      project: "Project skill",
      mine: "My skill",
      installed: "{source} · Installed",
    },
    close: 'Close',
    tabs: {
      inPicker: "Available",
      platform: 'Platform Skills',
      orgProject: 'Org / Project',
      favorites: '★ Favorites',
    },
    search: {
      placeholder: 'Search skill name or description...',
      ariaLabel: 'Search skills',
    },
    loading: 'Loading...',
    groups: {
      defaultActive: 'Default · Active',
      optional: 'Optional · Install',
      noMatch: 'No matching skills',
      admin: 'Admin · Commands',
      commands: 'Commands',
    },
    badges: {
      activated: 'Active',
      selected: 'Selected',
      mandatory: 'Mandatory',
      default: 'Default',
    },
    mandatory: {
      empty: 'No mandatory skills',
      create: '＋ Create Mandatory Skill',
      slugPlaceholder: 'slug (alphanumeric and hyphens)',
      descPlaceholder: 'Description (optional)',
      contentPlaceholder: 'SKILL.md content',
      creating: 'Creating...',
      createBtn: 'Create',
      errConflict: 'This name is reserved by a platform skill or system command, or already exists. Please choose another name.',
      errInvalid: 'Invalid name: use lowercase letters, digits and hyphens only',
      errGeneric: 'Operation failed, please try again later',
      errPlatformDelete: 'Platform skills cannot be deleted here; deactivate them instead',
      cancel: 'Cancel',
      delete: 'Delete',
    },
    favorites: {
      empty: 'Pinned skills appear in the toolbar',
      unpinAriaLabel: 'Unpin {slug}',
      pinAriaLabel: 'Pin {slug}',
    },
    install: {
      required: 'Required',
      installed: '✓ Installed',
      install: '＋ Install',
    },
    tip: 'Click row = use (this send) · ★ = pin to toolbar',
    legend: {
      install: 'Install = write to project files',
      mandatory: '🔒 = admin mandatory',
      favorites: '★ = pin to toolbar',
    },
    removeFromToolbar: 'Remove /{cmd} from toolbar',
    addToToolbar: 'Add /{cmd} to toolbar',
    clickToDeselect: 'Click to deselect /{slug}',
    clickToSelect: 'Click to use /{slug} (this send)',
  },

  // ── Shared helper error fallbacks (hooks/services) ───────────────
  errors: {
    conversation: {
      load_failed: 'Failed to load conversation.',
      timeout: 'Request timed out, please retry.',
      network: 'Network connection lost, please check your connection.',
      server: 'Server error, please try again later.',
      forbidden: 'Access denied.',
      not_found: 'Conversation not found.',
    },
    project: {
      invalid: 'Invalid request parameters',
      forbidden_admin_only: 'Only organization admins or platform admins can perform this action',
      not_found: 'Resource does not exist or you do not have access',
      quota_exceeded: 'Project limit reached, please contact a platform admin to raise the quota',
      name_conflict: 'Project name already exists (must be unique within the organization)',
      invalid_fields: 'Request fields do not meet requirements (check required fields)',
      unavailable: 'Service temporarily unavailable, please try again later',
      generic: 'Operation failed, please retry',
      create_pending: 'Project may have been created — close and refresh to confirm',
    },
  },
  // ── Proposal layered view (P-COND-DA0631525AC2 ST2) ─────────────
  proposalView: {
    key_points: 'Key points',
    current: 'Background',
    plan: 'Solution',
    outcome: 'Expected outcome',
    expand_to_view: 'Expand to view',
    design: {
      title: 'Design preview',
      open: 'Open in new window',
      count: '{count} design(s)',
    },
    // P-COND-69912D91E992 — Decisions Needed block
    decisions: {
      title: 'Decisions for you · {count}',
      recommended: 'Recommended: ',
      reason: 'Recommendation rationale',
      alternatives: 'Other options',
      from_review: 'Raised by reviewer',
      no_recommendation: 'Raised by reviewer, no recommendation',
      see_above: 'see the decisions above',
      overview_hint: 'See all questions and recommendations; open an item for details.',
      suggested_option: 'Recommended option',
      missing_recommendation: 'No recommendation provided',
      risk_note: 'Risk noted',
      untitled: 'Untitled decision',
      open_item: 'View item {index}: {question}',
      back: 'Back to all {count} items',
      previous: 'Previous',
      next: 'Next',
      read_only_note: 'Blue marks a recommendation. Viewing does not select or approve it. Approval adopts the proposal’s recommendations.',
      live_note: 'Tap an option to change it; you will check the draft before sending. Blue marks the recommendation.',
      choice_group: 'Choose the recommendation or the alternative',
      alternatives_selected: 'Alternatives · selected',
      changed: 'Changed',
    },
    review: {
      approved: 'Independent review: approved',
      approved_sub: 'another AI checked every code reference',
      issues: 'Independent review: {count} issues found',
      issues_one: 'Independent review: 1 issue found',
      issues_sub: 'please read before approving',
      business_decision: 'Independent review: business decision to confirm',
      business_decision_sub: 'the plan contains a business choice for you to make',
      unknown: 'Independent review: inconclusive',
      unknown_sub: 'the review reached no pass verdict, read the details before approving',
      unreviewed: 'Independent review: not completed',
      unreviewed_sub: 'this revision was not independently reviewed, please judge it yourself',
      cancelled: 'Independent review: stopped by you',
      cancelled_sub: 'you stopped the independent review; this revision was not reviewed, please judge it yourself',
      user_decision: 'Independent review: no blockers, your decision needed',
      user_decision_sub: 'contains a business decision for you to make',
      notes: 'Independent review: passed with notes',
      notes_sub: 'no blocking issues, worth a quick look',
      none: 'No independent review',
      details: 'Details',
      hide_details: 'Hide details',
      source: 'Source: {origin}',
      source_inline: 'Source: {origin} · {model} · revision {rev} · {resumed}',
      origin_inline: 'inline',
      origin_posthoc: 'post-hoc',
      resumed_yes: 'resumed',
      resumed_no: 'not resumed',
      source_posthoc: 'Source: {origin} · {model}',
      source_mode: 'Review mode: {mode}',
      mode_A: 'author self-check',
      mode_B: 'cross-model review',
      mode_source_project: ' · per project setting',
      mode_source_collab: ' · collab project',
      mode_source_conversation: ' · per conversation setting',
      disabled_project: 'Not reviewed (project setting)',
      disabled_project_sub: 'automatic review is turned off for this project, please judge it yourself',
      disabled_collab: 'Collab projects are not reviewed',
      disabled_collab_sub: 'collab projects skip automatic review, please judge it yourself',
      disabled_conversation: 'Not reviewed per conversation setting',
      disabled_conversation_sub: 'automatic review is off for this conversation, please judge it yourself',
      pinned_failed: 'Pinned model {model} did not finish the review',
      pinned_failed_nomodel: 'Pinned model did not finish the review',
      pinned_failed_sub: 'not reviewed; retry with manual multi-model review or pick another model',
    },
    risk: {
      title: 'Risk assessment',
      low: 'Low risk',
      mid: 'Medium risk',
      high: 'High risk',
      reversal: 'How to roll back',
    },
    lessons: {
      title: 'Past lessons',
      referenced: '{count} referenced',
      avoided: 'all avoided',
      na: 'Not applicable',
      skipped: 'skipped',
    },
    checklist: {
      title: 'Checklist',
      pass: '{count} pass',
      skip: '{count} skipped',
      na: '{count} n/a',
      blocker: '{count} blocking',
      freeform: 'Filled in (free-form)',
      show_raw: 'Show original self-check text',
      hide_raw: 'Hide original self-check text',
      item: {
        code_refs: 'Code references verified',
        identifiers: 'Identifiers verified',
        numbers: 'Numbers checked against source',
        paths: 'File paths verified',
        minimal: 'Minimal change principle applied',
        lessons: 'Past lessons checked',
        flow_impact: 'Flow impact declared',
        user_flow: 'User flow reproduced',
        recurrence: 'Compared with the previous proposal',
        scope: 'Scope assessment provided',
        design_persist: 'Design assets persisted',
        frontend_design: 'Frontend design provided',
        multi_page: 'Multi-page coverage checked',
        content_grep: 'Cited content grep-verified',
        plan_pack: 'Plan Pack path declared',
      },
    },
    approach: {
      title: 'Implementation steps',
      subtasks: '{count} sub-tasks',
      flows: '{count} flow impacts',
      fallback: 'Expand to view',
      understanding: 'Understanding',
      steps: 'Proposed approach',
      flow_impact: 'Flow impact',
    },
    technical: {
      title: 'Technical details (for developers)',
      sections: '{count} sections',
    },
    guidance: {
      pending: '↓ Use the approval card below',
      done: 'This proposal has been handled or has expired',
      neutral: 'To act on it, use the approval card below or type a command',
    },
    raw_commands: 'Text commands (you can still type them)',
  },
  // ── Proposal approval card (strings for ST3 ProposalApprovalCard) ─
  approvalCard: {
    aria_label: 'Proposal approval card',
    title: 'Proposal awaiting your approval',
    title_need_approval: 'Proposal ready to submit for approval',
    revised: 'revised {count} times',
    expiry: 'valid for {hours} h',
    help: 'What is this?',
    help_text: 'This is the implementation plan the agent proposed for your task. It will not change any code until you approve it; you can also implement with notes, ask for a revision, ask a question or run tests first. Each button sends the same text command you could type yourself.',
    help_text_need_approval: 'This project needs an approver before implementation. After "Submit for approval", a reviewer with full autonomy will review the plan; you can still ask for a revision or ask a question first.',
    approve: 'Approve & implement',
    submit: 'Submit for approval',
    approve_recommended: 'Go with recommendations',
    approve_selected: 'Go with my choices…',
    submit_selected: 'Revise with my choices…',
    override_count: '{count} changed',
    restore_recommended: 'Restore recommendations',
    override_check_hint: 'check it, then press Send',
    override_review_running: 'Review in progress — go with your choices once it finishes',
    override_stale: 'The decisions or your choices changed, so this draft may be stale. Collapse it and press the choices button again to re-check.',
    submit_recommended: 'Submit with recommendations',
    decisions_hint_proceed: 'Disagree with a recommendation? Say so under "More → Implement with notes", e.g. Decision 1: option B',
    decisions_hint_revise: 'Disagree with a recommendation? Use "Revise", e.g. Decision 1: option B',
    placeholder_proceed_decisions: 'e.g. Decision 1: option B (rest as recommended)',
    placeholder_revise_decisions: 'e.g. Decision 1: option B',
    proceed_with_notes: 'Implement with notes',
    revise: 'Revise',
    ask: 'Ask',
    run_tests: 'Run tests first',
    full_proposal: 'Full proposal',
    cancel: 'Cancel proposal',
    // P-COND-368144D76579 — More menu, editor hint/label, cancel dialog
    more: 'More',
    multi_review: 'Multi-model review',
    menu_proceed: 'Implement with notes',
    hint_proceed: 'Your notes are submitted together with the current proposal and implementation starts. To change the proposal itself, choose "Revise".',
    label_proceed: 'Implementation notes (required)',
    hint_test: 'Verify the current proposal first; you still decide whether to implement after the tests finish. The test scope may be left empty.',
    label_test: 'Test scope (optional)',
    cancel_dialog_title: 'Cancel this proposal?',
    cancel_dialog_body: 'Once cancelled, this proposal no longer waits for approval. The conversation history is kept.',
    cancel_dialog_review_note: 'Cancelling the proposal does not stop the review; to stop it, use “Stop review”.',
    cancel_dialog_confirm: 'Cancel proposal',
    cancel_dialog_back: 'Back',
    placeholder_proceed: 'What should be adjusted during implementation…',
    placeholder_revise: 'How should the proposal change…',
    placeholder_ask: 'Your question…',
    send_proceed: 'Implement with these notes',
    send_revise: 'Send revision notes',
    send_ask: 'Send question',
    collapse: 'Collapse',
    notes_required: 'Please enter your notes first',
    question_required: 'Please enter your question first',
    send_hint: 'Sending is the same as typing {command} followed by your text',
    footer_prefix: 'Buttons send the same commands as typing',
    footer_revise_example: '/revise feedback',
    footer_suffix: 'etc. You can still type in the message box.',
    sending: 'Sending…',
    // P-COND-6E08B543DEA8 — card states (idle / filling / running / expired)
    reveal_composer: 'Use the message box',
    // P-COND-42D22E812062 — composer mode: card collapses to one line
    mini_title: 'Proposal awaiting your approval',
    mini_hint: 'What you type below is sent as a question about the proposal',
    // in-card review-running notice
    review_panel_pointer: 'Review in progress — progress and stop are in the review panel above',
    review_panel_ended: 'Review ended — results are in the conversation',
    review_running_short: 'Reviewing',
    back_to_panel: 'Back to approval panel',
    placeholder_test: 'Optional: what the tests should focus on (leave empty to just run them)…',
    send_test: 'Start tests',
    keys_hint: 'Ctrl+Enter to send · Esc to collapse',
    placeholder_with_keys: '{text} ({keys})',
    running_approve: '✓ Approved, implementing…',
    running_revise: 'Revising the proposal…',
    running_ask: 'Answering your question…',
    running_proceed: 'Notes attached, implementing',
    running_test: 'Testing — the proposal will wait for your approval afterwards',
    running_cancel: 'Cancelling…',
    running_other: 'A task is running',
    running_resume: 'you can continue the approval when it finishes',
    expires_in_hours: 'expires in about {hours} h',
    expires_in_minutes: 'expires in about {minutes} min',
    expired_notice: 'Proposal {id} has expired — start the task again if you still need it',
    dismiss: 'Dismiss',
  },
  // ── PreflightBar ──────────────────────────────────────────────────
  preflight: {
    bar_label: 'Pre-send settings',
    mode_branch_dev: 'Branch Dev',
    mode_operation: 'Operation',
    lock_hint: 'Locked after send',
  },

  // ── Conductor Desktop download page (/desktop/download) ───────────
  desktop_download: {
    title: 'Conductor Desktop',
    subtitle:
      'A small daemon that runs on your own PC. Once installed and paired, a Conductor agent can read and write files on this machine under the permissions you grant.',
    back: 'Back to chat',
    loading: 'Loading installers…',
    load_failed: 'Could not load the installer list',
    retry: 'Retry',
    current_version: 'Current version',
    platform_windows: 'Windows',
    platform_macos: 'macOS',
    file_label: 'File',
    size_label: 'Size',
    sha256_label: 'SHA-256',
    platform_note_macos:
      'Apple Silicon (M-series) Macs only. Intel Macs are not supported.',
    download: 'Download',
    downloading: 'Downloading…',
    unavailable: 'Not published yet',
    unavailable_hint:
      'This installer has not been built and uploaded yet. Nothing is wrong on your side — check back later.',
    download_failed: 'Download failed. Please retry.',
    downloading_pct: 'Downloading… {pct}%',
    downloading_bytes: 'Downloading… {mb} MB',
    download_stalled: 'Download stalled (no data for {seconds}s) and was stopped. Check your connection and retry.',
    verify_title: 'Verify what you downloaded',
    verify_body:
      'Optional: compare the SHA-256 above with the file you downloaded. A match means the file is complete and unaltered.',
    verify_cmd_windows: 'certutil -hashfile <file> SHA256',
    verify_cmd_macos: 'shasum -a 256 <file>',
    unsigned_title: 'The installers are unsigned — and the two platforms behave differently',
    unsigned_intro:
      'The installers are not code-signed. Windows only WARNS; macOS REFUSES to open the app and calls it "damaged". Read the section for your platform before installing.',
    unsigned_windows_title: 'Windows — SmartScreen warns; you can click through',
    unsigned_windows_body:
      'A blue "Windows protected your PC" window appears on first run. Click More info → Run anyway. Installation then proceeds normally; nothing else is needed.',
    unsigned_macos_title: 'macOS — Gatekeeper hard-refuses and calls the file "damaged"',
    unsigned_macos_body:
      'macOS marks a file downloaded in a browser as quarantined. An unsigned, quarantined app is reported as "damaged and can\'t be opened". The file is NOT damaged — this is a policy refusal.',
    unsigned_macos_way1:
      'Before the first launch, drag the app into Applications, then run this command once in Terminal:',
    unsigned_macos_cmd: 'xattr -dr com.apple.quarantine /Applications/ConductorDesktop.app',
    unsigned_macos_arch:
      'Apple Silicon (M-series) Macs only.',
    unsigned_macos_keychain:
      'On the first launch after an upgrade, macOS asks for this Mac\'s login password so ConductorDesktop can read its saved sign-in from the keychain. Enter it and click "Always Allow"; clicking "Deny" leaves the client unable to sign in.',
    autostart_title: 'Autostart fires at LOGIN, not at power-on',
    autostart_body:
      'The client starts automatically once you log in to your computer account. After a reboot, while the machine sits at the lock screen, it is not running yet; it connects after you log in. Your sign-in is stored in your personal keychain/credential vault, which cannot be read without a login session.',
    next_title: 'After installing',
    next_body:
      'Once paired, the device appears on the My Devices page in Web Chat. Pairing needs someone at the computer to confirm it; an unattended machine cannot complete it.',
  },
  relay: {
    title: 'Personal relay',
    subtitle: 'Only you · deleted after 24h',
    notice: '🔒 Only visible to you · never sent to the agent · every text and file is permanently deleted 24 hours after it is sent',
    empty: 'Text and files you send here\nshow up instantly on your other signed-in devices.\nEach item is deleted automatically after 24 hours;\nyou can also delete it any time via ⋯.',
    placeholder: 'Type text, or drop/paste files',
    delete: 'Delete',
    delete_confirm: 'Permanently delete this item (including attachments)? This cannot be undone.',
    delete_failed: 'Delete failed, please try again',
    clear: 'Clear',
    clear_title: 'Delete all text and files in the relay',
    clear_confirm: 'Delete ALL text and files in the relay? This cannot be undone.',
    clear_failed: 'Clear failed, please try again',
    expires_in_hours: 'Deleted in {n}h',
    expires_in_minutes: 'Deleted in {n} min',
    download_only: 'Relay files can only be downloaded',
    pick_local: '📁 Local files',
    pick_local_mobile: '📁 Local files / camera',
    pick_from_relay: '📥 Pick from personal relay',
    pick_title: 'Add attachments',
    pick_hint: 'Once added, files follow this conversation\'s rules and are no longer deleted after 24h',
    pick_empty: 'No files in your relay yet',
    pick_loading: 'Loading…',
    pick_load_failed: 'Could not load relay files, please try again',
    pick_add: 'Add {n}',
    pick_cancel: 'Cancel',
    pick_skipped: '{n} file(s) expired or unreadable, skipped',
    pick_unavailable: 'Your personal relay could not be found',
    save_to_relay: 'Save to relay',
    saved: 'Saved to relay, deleted after 24h',
    save_failed: 'Save to relay failed',
  },
  // ── Skill Center (SKC-T1.3 skeleton; placeholder pages) ──────────
  skillCenter: {
    title: 'Skill Center',
    placeholder: 'Skill Center is coming soon.',
    tabs: {
      library: 'Skill Library',
      mine: 'My Skills',
      guide: 'Guide',
      project: 'Project skills',
      org: 'Org admin',
    },
    create: {
      title: 'Create Skill',
      steps: {
        s1: 'Describe',
        s2: 'Refine in chat',
        s3: 'Preview & confirm',
      },
      heading: 'Have an idea but not sure how to write a skill?',
      sub: 'One sentence is enough. An agent will help you complete it in a conversation only you can see.',
      problem: 'What problem should it solve',
      problemPlaceholder: 'e.g. Every Friday, summarize this week\'s conversations into a report grouped by project',
      required: 'required',
      project: 'Project to refine it in',
      projectFromConv: '(from the conversation)',
      noProject: 'You have no available project, so a refine conversation cannot start',
      optional: 'Optional: name, reference material, advanced',
      name: 'Skill name (optional)',
      namePlaceholder: 'e.g. Weekly report',
      materials: 'Reference material (optional, paste text)',
      materialsPlaceholder: 'Paste templates, examples or requirements for the agent to use',
      advanced: 'Advanced',
      slug: 'Invocation name',
      slugHint: 'Generated automatically; you can edit it. Lowercase letters, digits and hyphens only, e.g. weekly-report.',
      slugInvalid: 'Use lowercase letters, digits and hyphens (2-48 chars, starting with a letter or digit)',
      backToCenter: '← Back to Skill Center',
      backToOrigin: 'Back to original conversation',
      leaveConfirm: 'You have unsubmitted input. Leave anyway?',
      start: 'Next: open the conversation',
      starting: 'Creating conversation…',
      saveOnly: 'Write it myself (no conversation)',
      startHint: 'Your request is sent to the assistant automatically; it asks a few questions and then turns it into a skill. Leaving with unsubmitted input asks for confirmation.',
      tooLarge: 'Content exceeds the limit ({size} / {max}); please shorten it',
      failed: 'Could not create the draft, please try again later',
      off: 'My Skills is not available yet',
      offBody: 'Your organization has not enabled personal skills. You can still use platform and organization skills from the Skill Library.',
      materialsHeading: 'Reference material:',
      skeleton: {
        purpose: '## Purpose',
        when: '## When to use',
        inputs: '## Inputs',
        outputs: '## Output',
        example: '## Example',
      },
    },
    mine: {
      newSkill: '+ New skill',
      intro: 'Your skills belong to you only, and work in any project you have access to.',
      inProgress: 'In progress (unsaved drafts)',
      inProgressNote: 'Drafts are stored on the server, so you can continue after a refresh or on another device. Abandoning a draft keeps its refine conversation.',
      saved: 'Saved',
      untitled: 'Untitled draft',
      kindNew: 'New skill · draft',
      kindRevision: 'Editing v{n} · draft',
      size: '{size} / limit {max}',
      updated: 'Draft last updated {time}',
      privateNote: 'Only you can see this',
      continue: 'Continue refining',
      edit: 'Edit manually',
      editSave: 'Save changes',
      editCancel: 'Cancel',
      abandon: 'Abandon draft',
      abandonConfirm: 'Abandon this draft? The refine conversation is kept.',
      convLink: 'Conversation: {title} →',
      convUntitled: 'Refine conversation',
      conflict: 'This skill was updated elsewhere to v{n}. Your draft is kept; review the differences before saving.',
      viewDiff: 'View differences (v{n} ↔ your draft)',
      savedTag: 'Saved · v{n}',
      conflictTag: 'Name conflict',
      conflictNote: 'This name is already used by another skill. Rename it before using or publishing it.',
      useHere: 'Use in current conversation',
      useElsewhere: 'Use in a conversation',
      revise: 'Edit content',
      revisePending: 'I want to change the skill “{title}” — please ask me what to change first',
      history: 'Version history ({n} versions)',
      historyLoad: 'Version history',
      historyNote: 'Versions are append-only; editing creates a new version.',
      versionTag: 'v{n}',
      current: 'current',
      view: 'View',
      hide: 'Hide',
      emptyTitle: 'You have no skills of your own yet',
      emptyBody: 'Describe the problem in one sentence and an agent will help you complete it in a conversation only you can see.',
      loadFailed: 'Failed to load',
      retry: 'Retry',
      actionFailed: 'Action failed, please try again later',
      publishBtn: 'Publish to org',
      copyBtn: 'Copy to org library',
      republishBtn: 'Request again',
      withdrawBtn: 'Withdraw request',
      withdrawConfirm: 'Withdraw this publish request?',
      pubPending: 'Publish request: pending · v{n}',
      pubRejected: 'Publish request: rejected',
      pubWithdrawn: 'Publish request: withdrawn',
      pubApproved: 'Published to org · v{n}',
      pubTarget: 'Requested for: {target} · waiting for another admin',
      pubStale: 'The request is for v{n}, but v{m} now exists; to publish the new version, withdraw and request again.',
      pubRejectNote: 'Rejection note: {note}',
      pubWithdrawnNote: 'You withdrew the request on {time}; you can request again any time.',
      revisionConflict: 'The draft was updated elsewhere; the latest content is loaded, please edit again',
    },
    refine: {
      banner: 'Skill refine conversation · only you can see it',
      backToOrigin: 'Back to original conversation',
      backToCenter: '← Back to Skill Center',
      cardTitleRevision: 'editing v{n}',
      untitled: 'Untitled skill',
      status: {
        preparing: 'The assistant is preparing the first question…',
        refining: 'Refining the request · just answer the assistant',
        refiningOr: 'Answer the assistant’s questions, or',
        candidate: 'Draft generated — next step →',
        ready: 'Draft saved {time} · review it, then create',
        readyVersion: 'Draft saved {time} · review it, then save a new version',
        adoptConflict: 'Not saved (the draft was changed) · candidate kept',
        adoptTooLarge: 'Not saved (over the {max} limit) · candidate kept',
        adoptFailed: 'Not saved · candidate kept',
        createdNow: 'Created v{n} · {title} /{slug}',
        savedNow: 'Saved as v{n}',
        createdCurrent: 'Created · skill is at v{n}',
        savedCurrent: 'Saved · skill is at v{n}',
      },
      adopt: 'Use & preview',
      adopting: 'Writing…',
      quickDraft: 'Please generate the skill draft now from the information so far',
      quickDraftBtn: 'Generate draft now',
      previewConfirm: 'Preview & confirm',
      previewConfirmVersion: 'Preview & save new version',
      useInConversation: 'Use /{slug} in a conversation',
      viewPreview: 'View preview',
      hidePreview: 'Hide preview',
      viewDiff: 'View differences',
      hideDiff: 'Hide differences',
      adoptDiffTitle: 'Differences (current draft ↔ candidate)',
      continueEdit: 'Keep refining',
      slugLabel: 'Command',
      previewNote: '“Keep refining” closes the preview and returns to the input box; a trial never creates a version; re-tick after the content changes.',
      empty: 'The agent has not written a draft yet. It will appear here after you answer its questions.',
      loading: 'Loading draft…',
      loadFailed: 'Failed to load the draft',
      retry: 'Retry',
      edit: 'Edit manually',
      editAria: 'Edit the draft manually',
      editHint: 'Saving updates the draft; you still need to tick “I have checked the content” before saving as a skill.',
      editSave: 'Update draft',
      editCancel: 'Cancel',
      trial: 'Try with an example',
      trialPlaceholder: 'Example input, e.g. /weekly-report this week for the conductor project',
      trialAria: 'Example input for the trial',
      trialHint: 'Runs once in this conversation with your example input. Nothing is saved and no version is created; keep refining if you are not happy.',
      trialSend: 'Send trial',
      confirm: 'I have checked the content',
      saveNew: 'Create skill',
      saveVersion: 'Save new version',
      saving: 'Saving…',
      openMine: 'View my skills',
      conflict: 'This skill was updated elsewhere to v{n}. Your draft is kept; review the differences before saving.',
      diffTitle: 'Differences (v{n} ↔ your draft)',
      rebase: 'Save on top of v{n}',
      revisionConflict: 'The draft was just updated by the agent or another device; the latest content is loaded and your edit is still in the editor.',
      tooLarge: 'The draft exceeds the limit ({size} / {max}); please shorten it before saving',
      saveFailed: 'Save failed, please try again later',
      size: '{size} / limit {max}',
    },
    slash: {
      aria: 'Skill suggestions',
      hintNav: '↑↓ select',
      hintEnter: 'Enter confirm',
      hintEsc: 'Esc close',
      empty: 'No matching skills',
      src: {
        platform: 'Platform',
        team: 'Team',
        personal: 'Personal',
        command: 'Approval',
      },
    },
    org: {
      title: 'Org skills',
      closedTitle: 'Org skills are not available yet',
      closedBody: 'Skill publishing has not been enabled for this organization.',
      tabs: {
        library: 'Org skill library',
        approvals: 'Publish approvals',
      },
      adminNote: 'Admins create and edit org skills directly without approval; every change is audited.',
      memberNote: 'As an org member you can browse the org skill library. To share one of your own skills, submit a publish request from Skill Center → My skills.',
      newSkill: '＋ New org skill',
      empty: 'The org skill library is empty.',
      loadFailed: 'Failed to load',
      retry: 'Retry',
      scopeOrg: 'Whole org',
      scopeDept: 'Department',
      mandatoryTag: 'Mandatory · {n} projects',
      versionTag: 'v{n}',
      archivedTag: 'Archived',
      archivedOn: 'v{n} · archived {time}',
      edit: 'Edit (new version)',
      archive: 'Archive',
      unarchive: 'Re-enable',
      archiveConfirm: 'Archive /{slug}? Delivered files are removed from projects and the library shows "Archived".',
      mandatoryLock: 'Mandatory in {n} projects, cannot be archived. Remove the mandatory setting in project details first.',
      mandatoryConflict: 'This skill is mandatory in a project and cannot be archived. Remove the mandatory setting in project details first.',
      archivedNote: 'Archived: delivered files are removed from projects and the library shows "Archived"',
      defaultInstall: 'Installed by default in the org',
      defaultInstallHint: 'Members get it automatically and can uninstall it themselves',
      form: {
        createTitle: 'New org skill',
        slug: 'Invocation name',
        slugHint: 'Lowercase letters, digits and dashes, e.g. weekly-report',
        name: 'Name',
        description: 'One-line description',
        scope: 'Available in',
        scopeOrg: 'Whole organization',
        body: 'Skill body',
        bodyNoPrefill: 'The list did not include the current body; paste the complete new content.',
        create: 'Create',
        saveVersion: 'Save as v{n}',
        cancel: 'Cancel',
        required: 'Invocation name and body are required',
        slugInvalid: 'Invalid invocation name',
        conflict: 'This skill was updated elsewhere to v{n}. Your text is still in the editor; review the diff before saving.',
        failed: 'Save failed: {msg}',
      },
      sync: {
        ok: 'Delivered to {n} projects',
        failed: '{n} projects failed: {list}',
        none: 'Saved (not assigned to any project yet)',
      },
      approvals: {
        empty: 'No publish requests',
        memberEmpty: 'You have not submitted any publish request',
        status: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', withdrawn: 'Withdrawn' },
        meta: 'Requested version: v{n} · Requester: {who}',
        metaTarget: ' · Target: {target}',
        you: 'you',
        unknownVersion: '?',
        lockNote: 'Approval applies only to v{n}, locked at submission; later versions by the requester are not part of this request. The requester\'s refine conversation is not shared.',
        viewBody: 'View the full content of this version',
        noBody: 'Failed to load this version, please retry later.',
        notePlaceholder: 'A note is required to reject',
        noteRequired: 'A note is required to reject',
        approve: 'Approve',
        reject: 'Reject',
        withdraw: 'Withdraw request',
        selfReason: 'You cannot approve your own request; another admin must handle it',
        decidedNote: 'Rejection note: {note}',
        alreadyDecided: 'This request was already handled; the list is refreshed.',
        failed: 'Action failed: {msg}',
      },
    },
    publish: {
      title: 'Request to move /{slug}',
      copyTitle: 'Move /{slug}',
      intro: 'An org admin (or, for a project skill, a full-autonomy member of that project) reviews the request; once approved the skill becomes a project or organization skill.',
      copyIntro: 'As an admin you move it directly (audited) — no approval needed, and you cannot approve yourself. An organization skill is usable in every project of the org; a project skill by every project member.',
      copyIntroProject: 'You have full autonomy on this project, so you move it directly into the project (audited) — no approval needed. Every project member can then use it.',
      versionTag: 'v{n}',
      onlyVersion: 'Only v{n} is published.',
      lockNote: 'This version is locked when you submit; if you save a newer version later, approval is refused — withdraw and resubmit. Your refine conversation is not shared.',
      copyLockNote: 'The whole skill moves with all its versions; the current one is v{n}.',
      target: 'Move to',
      targetProject: 'Project skill',
      projectPick: 'Choose a project',
      projectNone: 'No project available',
      targetOrg: 'Whole organization: {org}',
      targetOrgPlain: 'Whole organization',
      reviewNote: 'A full-autonomy member of the project or an org admin must approve (organization skills: org admins only). You cannot approve your own request; a rejection always carries a reason. You can withdraw any time before it is decided.',
      cancel: 'Cancel',
      submit: 'Submit request',
      copySubmit: 'Move now',
      submitting: 'Submitting…',
      noOrg: 'Select an organization in the top bar first',
      noVersion: 'This skill has no saved version yet and cannot be published',
      failed: 'Submit failed: {msg}',
      done: 'Publish request submitted',
      copied: 'Moved — it left My skills; version history and creator are kept',
    },
    tabsAria: "Skill Center tabs",
    loading: "Loading…",
    closed: {
      title: "Skill Center is not open yet",
      body: "The feature is being prepared. Once open, the skill library appears here. You can still use installed skills in conversations as usual.",
    },
    back: "← Back to conversation",
    backWithTitle: "← Back to conversation: {title}",
    context: {
      project: "Project",
      mode: "Mode",
      fromConv: "from your conversation, switchable",
      noProject: "You have no projects yet",
      modeDev: "dev mode",
      modeCollab: "collab mode",
    },
    intro: {
      title: "Three steps",
      s1: "1 Search or filter",
      s2: "2 Expand the details to check fit",
      s3: "3 Click \"Use in this conversation\"",
      guide: "Read the guide",
      close: "Dismiss",
    },
    library: {
      search: "Search skill name or purpose…",
      searchAria: "Search skills",
      statusAria: "Filter",
      status: {
        all: "All",
        available: "Available",
        favorites: "★ Favorites",
      },
      sourceAria: "Source",
      source: {
        all: "All sources",
        platform: "Platform skills",
        team: "Team skills",
        mine: "My skills",
      },
      teamHint: "Team skills = org and project skills; the small tag shows where each one is available.",
      useHere: "Use in this conversation",
      useElsewhere: "Use in a conversation",
      pin: "Favorite /{slug}",
      unpin: "Unfavorite /{slug}",
      mandatory: "Mandatory",
      expand: "Show details",
      collapse: "Hide details",
      empty: "No matching skills",
      error: "Failed to load skills",
      retry: "Retry",
      attachOnly: "Used on this project by attaching it to the dispatch",
      install: "Install",
      where: {
        org: "Whole org",
        department: "Department",
        project: "This project",
        personal: "Available in your projects",
      },
      fields: {
        purpose: "Purpose",
        when: "When to use",
        inputs: "You provide",
        outputs: "Output",
        example: "Example",
        note: "Note",
      },
      exampleText: "/{slug} followed by your request",
      mandatoryNote: "Mandatory only means it cannot be removed; it never runs automatically",
      deliveryFailed: "Version {n} failed to deliver; the project still uses version {prev}",
      retrySync: "Retry",
      retrying: "Retrying…",
      retryFailed: "Retry failed, please try again later",
      reasons: {
        not_installed: "This project skill is not enabled here · an admin enables it in project details",
        name_conflict: "Name conflict · another skill has the same name",
        mode_mismatch: "Mode mismatch · not available in this mode",
        permission_denied: "No permission · you may not use this skill",
        archived: "Archived · an org admin archived this skill",
        not_delivered: "Not delivered · not yet delivered to this project; an admin can retry in project details",
        unknown: "Unavailable",
      },
    },
    drawer: {
      title: "Skill details",
      close: "Close",
      notFound: "Skill not found",
    },
    handoff: {
      dialogTitle: "Use /{slug} in a conversation",
      dialogBody: "You opened the Skill Center from navigation; choose the conversation to use it in.",
      step1: "1 Choose a project",
      step2: "2 Choose a conversation",
      recent: "Recent: {title}",
      untitled: "Untitled conversation",
      newConv: "＋ New conversation (in this project)",
      loading: "Loading conversations…",
      loadFailed: "Failed to load conversations",
      cancel: "Cancel",
      confirm: "Go and select the skill",
      failed: "Something went wrong, please retry",
      writeFailed: "Could not save the handoff (browser storage unavailable)",
    },
    composer: {
      selected: "/{slug} selected from the Skill Center — review, then send",
      unavailable: "/{slug} is unavailable in this conversation's project: {reason}",
      notFound: "/{slug} was not found in this conversation's project; not selected",
      checkFailed: "Could not verify /{slug}; not selected. Pick it from the ＋ panel later",
      replaceAsk: "/{current} is selected. Replace it with /{slug}?",
      replace: "Replace",
      keep: "Keep current",
      dismiss: "Dismiss",
    },
    guide: {
      intro: "A skill is a written-down way of doing a task. Select it and the agent follows that method for you.",
      use: {
        title: "Use a skill in a conversation",
        sub: "Find the right skill and take it back to your conversation.",
        s1t: "Find a skill",
        s1d: "Search the library, or filter by Available / Favorites and source.",
        s2t: "Read the details",
        s2d: "Expand a card to see its purpose, when to use it, what to provide and what you get.",
        s3t: "Use it",
        s3d: "Click \"Use in this conversation\" to go back; the skill is selected and nothing is sent automatically.",
        cta: "Open the library",
      },
      make: {
        title: "Make your own skill",
        sub: "No need to know how — the agent asks you step by step.",
        s1t: "Write one sentence",
        s1d: "Say what problem it solves; everything else is optional.",
        s2t: "Answer a few questions",
        s2d: "In a conversation only you can see, the agent asks for details. Pick \"not sure yet\" when unsure.",
        s3t: "Review and save",
        s3d: "Review the draft, tick \"I have checked it\" and save. To share it, request publishing to your org.",
        cta: "New skill",
        off: "Personal skills are not enabled for your org yet. Ask your org admin if you need them.",
      },
      faqTitle: "FAQ",
      faq: {
        q1: "Do mandatory skills run automatically?",
        a1: "No. Mandatory only means it is always installed and cannot be removed. You still select it in a conversation for it to take effect.",
        q2: "Why are some skills grey?",
        a2: "The card says why: not installed yet, not enabled on this project, a name conflict, the mode does not apply, or no permission. Install platform and org skills yourself from the library; a project skill is enabled by an admin in project details.",
        q3: "How many skills per message?",
        a3: "One at a time. Selecting another replaces the current one.",
        q4: "Can others see my skills?",
        a4: "By default only you. After an org admin approves your publish request, colleagues in the org can see it. Only the version you submitted is published.",
        q5: "Who can see the skill-refining conversation?",
        a5: "Only you. Neither org admins nor platform admins can see it.",
        q6: "My publish request was rejected — now what?",
        a6: "\"My skills\" shows the admin's note. Revise accordingly and submit again.",
      },
      manualText: "Want to learn more, e.g. how to write better skills?",
      manualLink: "Open the user manual →",
    },
    project: {
      title: "Project skills",
      sub: "Decide which skills are installed on this project and which are optional. Saving delivers immediately; failures show the reason and can be retried at once; a nightly run fills any gaps.",
      add: "＋ Add skill",
      addPick: "Choose a skill to add",
      addConfirm: "Add",
      addNone: "Nothing to add",
      legend: {
        mandatory: "Always installed, cannot be deactivated (does not run automatically)",
        default: "Installed, can be deactivated",
        available: "Visible to members; usable once an admin installs it",
      },
      modes: {
        mandatory: "Mandatory",
        default: "Default",
        available: "Optional",
      },
      modeAria: "Install mode",
      win: "This project runs on a Windows PC: installs are append-only. A deactivated skill leaves the list, but delivered files are not deleted.",
      readOnly: "You can view this project's skills. Ask an org admin to install or change them.",
      sources: {
        platform: "Platform skill",
        org: "Org skill",
        department: "Department skill",
        project: "Project skill",
        personal: "Personal skill",
      },
      version: "v{n}",
      status: {
        delivered: "✓ Delivered",
        installed: "✓ Installed",
        notInstalled: "Not installed",
        inactive: "Deactivated",
        filesKept: "(files kept)",
        notDelivered: "Not delivered (never delivered; not selectable yet)",
        failed: "Version {n} failed to deliver; the project still uses version {prev}",
        attachOnly: "Attached to the dispatch on this project",
        archived: "Archived by an org admin",
        conflict: "Name conflict",
        unavailable: "Unavailable",
      },
      deactivate: "Deactivate",
      reactivate: "Reactivate",
      install: "Install",
      retry: "Retry",
      mandatoryLocked: "A mandatory skill cannot be deactivated. Change it to Default first.",
      saving: "Saving…",
      saveFailed: "Save failed, please retry",
      loadFailed: "Failed to load project skills",
      empty: "No skills installed on this project yet",
      result: {
        title: "Delivery results",
        delivered: "✓ {project}: delivered",
        failed: "✕ {project}: delivery failed ({error})",
        attach: "{project}: attached to the dispatch when selected",
        windows: "{project}: Windows append-only (deactivating keeps delivered files)",
        unknownError: "unknown reason",
      },
      create: {
        summary: "＋ New mandatory skill",
        hint: "The new skill belongs to this project only and is set to mandatory.",
        slug: "Name",
        desc: "One-line description",
        descPlaceholder: "e.g. checklist before a site inspection",
        body: "Body",
        bodyHint: "Use the fixed subheadings; the library shows them when expanded. The name must not clash with a platform skill.",
        submit: "Create as mandatory",
        submitting: "Creating…",
        cancel: "Cancel",
      },
    },
    help: "Help",
    helpTitle: "Skill Center help",
    newSkill: "＋ New skill",
    currentProject: "Current project",
    modeLabel: "Conversation mode",
    projectsLoadFailed: "Failed to load projects",
    projectType: {
      dev: "Dev project",
      collab: "Collab project",
      both: "Both modes",
      unset: "Type not set",
    },
    projectUnsetHint: "This project has no type set; showing all skills",
    orgTab: {
      library: "Org skills",
      approvals: "Publish approvals",
    },
    status: {
      ready: "Ready",
      readyMandatory: "Installed · Mandatory",
      installable: "Installable",
      pending: "Installing / pending",
      failed: "Install failed",
      stale: "New version pending",
      versionUnknown: "Running version unverified",
      attach: "Ready",
      notInstalled: "Not installed",
      userInstalled: "Installed",
      mandatoryProject: "Required here",
      projectSkill: "Project skill",
    },
    card: {
      expand: "Show details",
      learnMore: "Learn more →",
      learnMoreShort: "Learn more",
      use: "Use in conversation",
      install: "Install to project",
      installPersonal: "Install",
      uninstall: "Uninstall",
      installed: "Installed",
      retry: "Retry install",
      manage: "Manage",
      favorite: "Favorite",
      unfavorite: "Unfavorite",
      guidePending: "Guide coming soon",
      authorUnrestricted: "Author did not restrict type",
      when: "When to use",
      inputs: "What to prepare",
      output: "What you get",
      failReason: "Reason: {msg}",
      scope: {
        dev: "For dev projects",
        collab: "For collab projects",
        both: "For dev and collab",
      },
    },
    guideModal: {
      what: "What it does",
      when: "When to use",
      inputs: "Before you start",
      how: "How to use",
      howDefault: "Select this skill in a conversation, add your request, review, then send.",
      output: "What you get",
      example: "Try saying",
      notes: "Notes",
      version: "Guide for version {n}",
      pendingVersion: "Version {n} pending",
      close: "Close",
      loadFailed: "Failed to load guide",
    },
    errors: {
      host_unconfigured: "This project has no server connection configured yet, so the skill can't be installed. Please contact an admin.",
      write_failed: "The skill file couldn't be written to the project. Please try again; contact an admin if it keeps failing.",
      remove_failed: "The skill file couldn't be removed from the project. Please try again; contact an admin if it keeps failing.",
      check_failed: "Couldn't check the project's skill files right now. Please try again later.",
      file_ops_unavailable: "Couldn't reach the project's server right now. Please try again later; contact an admin if it keeps failing.",
      unmanaged_file_exists: "The project already has a skill file with this name that the Skill Center didn't install, so it wasn't overwritten. Ask an admin to deal with that file first.",
      template_missing: "This platform skill's files are missing. Please contact an admin.",
      not_installed: "Install this skill first, then favorite it or use it in a conversation.",
      mandatory: "This skill is required in this project and can't be uninstalled.",
      version_changed: "The skill was updated to a new version. Please refresh and try again.",
      not_found: "This skill can't be found, or you don't have permission to use it.",
      bad_target: "Something was wrong with the request. Please refresh the page and try again.",
      mandatory_only: "Org skills can only be set as mandatory for this project.",
      default_install_org_only: "Only org skills can be installed by default across the org",
      department_retired: "Department skills are retired; resubmit as an org or project skill",
      generic: "Service temporarily unavailable. Please try again later.",
    },
    source: {
      platform: "Platform",
      conductor: "Organization skill",
      org: "Organization skill",
      orgDefault: "Org default",
      project: "Project skill",
      department: "Department skill",
      personal: "My skill",
    },
    transfer: {
      toProject: "Move to project skills",
      toOrg: "Move to organization skills",
      toDepartment: "Move to department skills",
      confirm: "Version {n} will be moved. It leaves My skills; version history and creator are kept.",
      done: "Moved",
      removedHint: "Once approved it leaves My skills; version history and creator are kept",
      errors: {
        forbidden: "Only org admins can move a skill directly — submit a request instead",
        use_copy_to_org: "You are an admin — move it directly, no request needed",
        name_conflict: "A skill with the same name already exists there — rename yours first",
        slug_taken: "A skill with the same name already exists there — rename yours first",
        project_skill_conflict: "This project already has a project skill with the same name — rename yours first",
        already_transferred: "This skill has already been moved — refresh the page",
        project_gone: "The target project no longer exists — choose another one",
        pending_exists: "This skill already has a pending request — withdraw it first",
        project_required: "Choose a project",
        project_not_found: "Project not found, or you have no access to it",
        project_not_allowed: "This project is not in the current organization",
        version_changed: "The skill has a newer version — refresh and try again",
        check_failed: "Could not check the project's skill files right now — try again later",
      },
    },
    projectTab: {
      notice: "Only this project's skills are shown here: project skills and skills made mandatory for this project. Install platform and org skills yourself from the library.",
      addFromLibrary: "＋ Add from library",
      mandatorySection: "Required in this project",
      manageTitle: "Manage /{slug}",
      setMandatory: "Set mandatory",
      setDefault: "Set default",
      setAvailable: "Set optional",
      disable: "Disable",
      enable: "Enable",
      summary: "{n} installed, {m} need attention",
      manageLink: "Manage skills →",
      assignedSection: "Project skills",
      mandatoryEmpty: "No required skills in this project yet",
      addMandatory: "＋ Make required in this project",
      addMandatoryTitle: "Make required in this project",
      addMandatoryHint: "A required skill is used automatically by every member of this project and can't be uninstalled.",
      addMandatoryEmpty: "No skills can be made required",
      addMandatoryAction: "Make required",
      saveFailed: "Save failed: {msg}",
    },
    empty: {
      noMatch: "No matching skills",
      noSkills: "No applicable skills",
      noPermission: "No access to this project",
      loadFailed: "Failed to load",
      retry: "Retry",
      createSkill: "Create skill",
    },
    copy: {
      confirmVersion: "Version {n} will be copied",
      versionChanged: "The skill has a newer version; refresh and try again",
    },
    favoriteFailed: "Favorite failed; reverted",
  },
  // ── MCP Center (M3-3 / P-COND-ACDA06F07BFD) — mcpCenter.handoff.* is Tier 4 (M3-5) ──
  mcpCenter: {
    picker: {
      title: "Connections (MCP)",
      openAria: "Choose MCP connections for this message",
      modeAuto: "Auto: allow any usable connection",
      modeSelected: "Only the connections I pick",
      modeNone: "No MCP Center connections this time",
      shared: "Shared connection",
      personal: "Personal connection",
      usable: "Usable",
      go: "Handle",
      manage: "Manage connections",
      apply: "Apply to this message",
      applyHint: "Pick at least one connection",
      maxHint: "At most {n} connections per message",
      loading: "Loading connections…",
      loadFailed: "Could not load connections. Try again later.",
      empty: "No selectable connections in this project",
      summary: {
        auto: "MCP · Auto",
        selected: "MCP · {n} selected",
        none: "MCP off",
      },
      chipSelected: "Only: {names}",
      chipNone: "No MCP connections",
      chipReset: "Back to auto",
      sendBlocked: "\"Only the connections I pick\" has nothing picked: pick a connection or switch to auto before sending",
    },
    title: "MCP Center",
    subtitle: "Hand external services (MCP connections) to projects safely: requests, approvals and account connections live here.",
    loading: "Loading…",
    closed: {
      title: "MCP Center is not open yet",
      body: "The platform has not enabled the MCP Center. Contact a platform admin.",
    },
    tabsAria: "MCP Center sections",
    tabs: {
      effective: "Project connections",
      requests: "Requests & approvals",
      org: "Org connections",
      catalog: "Platform catalog",
      inbox: "To-do",
      hosted: "Hosted apps",
    },
    project: {
      label: "Project",
      none: "No accessible projects in this organization",
      loadFailed: "Failed to load projects",
    },
    retry: "Retry",
    refresh: "Refresh",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    loadFailed: "Load failed: {msg}",
    scope: {
      platform: "Platform catalog",
      org: "Org connection",
      project: "Project-private",
    },
    mode: {
      none: "No account",
      shared: "Shared org account",
      personal: "Each member signs in",
    },
    auth: {
      none: "No auth",
      api_key: "API key",
      oauth: "OAuth sign-in",
    },
    connection: {
      not_required: "No account needed",
      not_connected: "Not connected",
      connecting: "Connecting",
      valid: "Connected",
      unverified: "Unverified",
      expired: "Expired",
      reauth_required: "Sign-in required",
      disconnected: "Disconnected",
    },
    health: {
      unchecked: "Unchecked",
      ok: "Healthy",
      unavailable: "Unavailable",
      incompatible: "Incompatible",
    },
    lifecycle: {
      draft: "Draft",
      published: "Published",
      install_frozen: "No new installs",
      disabled: "Disabled",
    },
    requestStatus: {
      draft: "Draft",
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
      superseded: "Superseded",
      none: "No request",
    },
    usable: "Ready to use",
    unusable: "Not available",
    reasons: {
      not_granted: "You have not been granted this connection in this project; you can request it",
      project_disabled: "This project has not enabled the connection; a project owner or org admin must enable it",
      request_pending: "Your request is waiting for approval",
      account_not_connected: "Account not connected (shared accounts are connected by the maintainer or a project manager; personal ones by you)",
      account_expired: "Account sign-in expired; reconnect it",
      account_unverified: "Account saved but not verified yet, so it is not active; click Verify to retry or reconnect",
      consent_required: "Personal-account results are written into the conversation; confirm result visibility once first",
      runtime_unsupported: "This project's runtime does not support MCP yet (local and Docker only in this release)",
      unhealthy: "The external service is unavailable; contact the maintainer",
      version_pending: "A new service version awaits approval",
      definition_disabled: "The platform disabled this service",
      local_runtime_restricted: "External connections are disabled by default for locally-run projects",
    },
    reasonUnknown: "Other reason ({code})",
    versionNo: "v{n}",
    actions: {
      request: "Request access",
      withdraw: "Withdraw",
      approve: "Approve",
      reject: "Reject",
      enable: "Enable for project",
      connect: "Connect account",
      reconnect: "Reconnect",
      disconnect: "Disconnect",
      verify: "Verify",
      manage: "Manage",
      review: "Review",
      revoke: "Revoke project grant",
      grantEnable: "Grant & enable for project",
      deleteConnection: "Delete connection",
      resolve: "Mark done",
      open: "Open",
    },
    consentHint: "Before first use, confirm result visibility for a personal connection once on this page.",
    effective: {
      empty: "No MCP connections are visible to this project.",
      rowMeta: "{scope} · {mode}",
      manageHint: "Grants all active project members every enabled tool of the connection.",
      revokeHint: "Further calls from this project stop immediately; content already in conversations stays.",
      revokeUnavailable: "No grant record found (only org admins see grant ids); handle it under Org connections.",
      enabled: "Enabled for this project",
      revoked: "Project grant revoked",
      grantEnableScopeHint: "Grants all members access to all tools",
      deleteConfirm: "This deletes the connection with its grants, credentials and pending requests. This cannot be undone.",
      deleteConfirmBtn: "Confirm delete",
      deleted: "Connection deleted",
      deleteOrgOnly: "Manage org-level connections under Org connections",
      addPrivate: "Add project-private connection",
    },
    requestForm: {
      title: "Request access: {name}",
      purpose: "Purpose",
      purposePh: "e.g. read this project's approved documents",
      tools: "Tools needed (optional, comma separated)",
      toolsPh: "Leave empty to let the approver decide",
      submit: "Submit request",
      submitted: "Request submitted; waiting for approval",
      duplicate: "An identical pending request already exists; nothing new was submitted",
      noSecret: "Requests never collect secrets.",
    },
    requests: {
      empty: "No MCP requests in this project.",
      applicantMe: "Me",
      applicantOther: "Another member",
      applicant: "Applicant",
      created: "Submitted",
      decided: "Decided",
      purpose: "Purpose",
      tools: "Tools",
      toolsAll: "Approver decides",
      rejectNote: "Rejection reason",
      target: "Target",
      targetCatalog: "Catalog: {name}",
      unknownTarget: "(connection no longer visible)",
      filterPending: "Pending only",
    },
    approval: {
      title: "Review request",
      approveHint: "Approval grants the applicant access; existing grants only widen, never narrow members or tools.",
      scopeAria: "Open to",
      scope: { none: "As requested", all_active: "All active members", departments: "By department" },
      deptPick: "Pick departments (multiple allowed)",
      deptNone: "The organization has no departments to pick.",
      deptPaused: "(paused)",
      deptRequired: "Pick at least one department",
      deptLoadFailed: "Could not load departments, please retry later",
      rejectNote: "Rejection reason (required, visible to the applicant)",
      rejectNotePh: "e.g. purpose unclear; list the resources you need",
      rejectNoteRequired: "Please enter a rejection reason",
      withdrawHint: "You can only withdraw your own pending request in this project.",
      stale: "The request changed; refreshed to the latest state. Please review again.",
      approved: "Approved",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
      handoffShared: "Next: the maintainer connects the shared org account.",
      handoffPersonal: "Next: each approved member signs in with their own account.",
      readOnly: "You can view this request but have no actions on it.",
    },
    hosted: {
      installation: "Hosted app",
      none: "No hosted apps to show.",
      empty: "This connection is not deployed to any node yet.",
      offTitle: "Hosted apps are not enabled",
      offBody: "The platform has not enabled hosted apps yet.",
      online: "Node online",
      offline: "Node offline",
      sessions: "Account sessions",
      noSessions: "No account sessions yet.",
      expectedIdentity: "Expected identity: {id}",
      login: { logged_in: "Signed in", not_logged_in: "Not signed in", expired: "Expired", identity_mismatch: "Identity mismatch", unknown: "Unknown", uncertain: "Unconfirmed" },
      lease: { none: "Idle", task: "In use by a task", maintenance: "Taken over by maintainer" },
      takeover: "Take over",
      release: "Release",
      tookOver: "Takeover requested; it is yours once the running task stops",
      released: "Released; tasks can use this account again",
      genericAccess: "Generic access",
      genericAccessHint: "Only maintainers can operate the signed-in account directly on the node, to explore and build capabilities; members can only call the accepted capabilities below.",
      acceptedCaps: "Accepted capabilities",
      noAccepted: "No accepted capabilities yet.",
      otherCaps: "Pending / disabled",
      capStatus: { draft: "Pending acceptance", accepted: "Accepted", disabled: "Disabled" },
      opClass: { query: "Query", export: "Export", modify: "Modify" },
      versionState: { draft: "Draft version", accepted: "Accepted version" },
      current: "current",
      history: "Versions ({n})",
      accept: "Accept this version",
      acceptNote: "Once accepted, granted members can call this version; only query capabilities can be accepted for now.",
      acceptQueryOnly: "Only query capabilities can be accepted for now; this one cannot.",
      accepted: "Accepted",
      disable: "Disable",
      disabled: "Disabled",
    },
    org: {
      needOrg: "Select an organization at the top first.",
      noPermission: "Only org admins can manage org connections.",
      empty: "The organization has no MCP connections yet.",
      newInstallation: "New org connection",
      grants: "Granted projects",
      noGrants: "Not granted to any project",
      allMembers: "All project members",
      listedMembers: "{n} listed members",
      departmentMembers: "{n} departments",
      tools: "Tools: {list}",
      toolsAll: "All enabled tools",
      maintainer: "Account maintainer",
      revokeConfirm: "Revoke this project's grant?",
    },
    catalog: {
      empty: "The platform catalog is empty.",
      noVersion: "No installable version yet",
      requestForProject: "Request for current project",
      newDefinition: "New catalog service",
      newVersion: "Publish new version",
      endpoint: "Endpoint",
      tools: "{n} tools",
    },
    editor: {
      orgTitle: "New org connection",
      projectTitle: "New project-private connection",
      catalogTitle: "New catalog service",
      versionTitle: "New version: {name}",
      definition: "Catalog service",
      pickDefinition: "Choose a catalog service",
      displayName: "Display name (optional)",
      credentialMode: "Account mode",
      grantProjects: "Also grant to these projects (all active members)",
      noProjects: "No projects in this organization",
      source: "Source",
      sourceCatalog: "From the platform catalog",
      sourceSelf: "Self-built remote service (HTTP)",
      name: "Service name",
      endpoint: "Endpoint (https)",
      authKind: "Auth",
      purpose: "Purpose",
      lifecycle: "Lifecycle",
      tools: "Tools (JSON array; each needs a name)",
      authCaps: "Auth parameters (JSON object; OAuth needs authorize_url / token_url / client_id)",
      invalidJson: "Invalid JSON: {field}",
      required: "Fill in the required fields",
      create: "Create",
      created: "Created",
      selfNote: "A self-built service is visible to this project only; enable it for the project before use.",
    },
    credential: {
      sharedKey: "Shared org API key",
      sharedKeyPh: "Paste API key (usually just the token itself, no Bearer prefix)",
      saveKey: "Save key",
      keySaved: "Shared account connected; the key will never be shown again",
      keySavedVerified: "Saved and verified as reachable (handshake and tool list succeeded; this does not guarantee repository read access)",
      keySavedUnverified: "Saved, but not verified yet, so it is not active (reason: {reason})",
      verifyOk: "Verified; the account is now active",
      verifyPending: "Could not complete verification; the account state is unchanged (reason: {reason})",
      verifyRejected: "The external service rejected this credential; reconnect it",
      verifyReasonGeneric: "verification did not complete; try it in a conversation later",
      verifyReason: {
        org_paused: "the organization's external connections are paused, so it was not verified",
        upstream_bad_request: "the external service rejected the request (credential format or request may be incompatible)",
        upstream_not_found: "the external service address was not found (404)",
        upstream_timeout: "the external service timed out",
        upstream_unavailable: "the external service is currently unreachable",
        ssrf_blocked: "the service address is blocked by security policy",
        upstream_protocol_error: "the external service returned an unrecognized response",
      },
      keyHint: "The key is write-only and stored encrypted on the server; no page ever shows it again. It is test-connected to the external service before saving.",
      oauth: "Sign in with the provider",
      redirecting: "Redirecting to the provider…",
      disconnectConfirm: "Disconnect? Only this connection in this project is affected; content already in conversations stays.",
      disconnected: "Account disconnected",
      noBinding: "No disconnectable binding found (only the maintainer or the owner sees it); refresh and retry.",
      authUnknown: "The connection's auth method is unknown; choose one:",
    },
    oauthResult: {
      connected: "Account connected; you can use it in conversations now.",
      unverified: "Signed in and saved, but reachability could not be verified, so it is not active yet. Click Verify on this page to retry.",
      cancelled: "You cancelled the provider sign-in; nothing was connected.",
      expired: "The sign-in timed out (10 minutes); connect again.",
      user_mismatch: "The Conductor account that returned differs from the one that started; nothing was connected. Retry with the starting account.",
      login_required: "Could not confirm the sign-in started in this browser (different browser or cleared cookies); nothing was connected. Click connect again on this page.",
      forbidden: "You are no longer allowed to sign in for this connection (access changed); nothing was connected.",
      failed: "Connection failed; retry later or contact the maintainer.",
    },
    inbox: {
      empty: "Nothing to do.",
      open: "Open",
      resolved: "Done",
      showResolved: "Show done",
      kind: {
        mcp_request: "MCP request",
        mcp_enable: "MCP enable",
        mcp_account_setup: "Account setup needed",
      },
    },
    errors: {
      mcp_not_found: "Not found or not visible to you",
      mcp_forbidden: "You are not allowed to do this",
      mcp_stale_revision: "Data changed and was refreshed; review it before acting",
      mcp_invalid_state: "Not allowed in the current state",
      mcp_runtime_unsupported: "This runtime does not support MCP",
      mcp_config_conflict: "MCP configuration name conflict",
      mcp_consent_required: "Confirm personal result visibility first",
      mcp_ssrf_blocked: "Endpoint not allowed (private address or redirect)",
      generic: "Operation failed; retry later",
      withMessage: "{base}: {msg}",
    },
    // M3-5 (§9.8): personal-result consent (D3) + conversation strip / handoff
    consent: {
      action: "Confirm result visibility",
      title: "Confirm result visibility for personal connection \"{name}\"",
      body: "Results fetched with your personal account are written into the conversation; project members who can see the conversation and platform admins can see them",
      scope: "Confirm once and every conversation in this project can use it. Disconnecting or reconnecting your account, or revoking the project grant, requires confirming again. Group chats and WeCom conversations never use personal connections.",
      confirm: "I understand, confirm",
      done: "Confirmed. Personal connection \"{name}\" can now be used in this project's conversations",
    },
    handoff: {
      label: "MCP",
      usable: "Usable: {names}",
      noneUsable: "No usable connections",
      needs: "{n} need action",
      hideNeeds: "Hide",
      go: "Handle",
      goAll: "Open MCP Center",
      back: "Back to conversation",
      need: {
        group: "Personal connections are off in group chats",
        runtime: "Runtime not supported",
        pending: "Awaiting approval",
        connect: "Connect your account",
        consent: "Confirm result visibility",
        request: "You can request access",
        unavailable: "Unavailable",
      },
      ready: "\"{name}\" is ready. Your draft is kept and the connection is included when you send.",
      still: "\"{name}\" is not ready yet: {need}. Your draft is kept.",
      summary: "Back in the conversation: {usable} usable, {needs} need action. Your draft is kept.",
      notFound: "The connection you were handling is no longer in this project. Your draft is kept.",
      dismiss: "Dismiss",
      stripAria: "MCP connection status for this conversation",
    },
  },
  // ── Observability center · Auto-fix (AF-T8) ──────────────────────
  autofix: {
    tab: 'Auto-fix',
    title: 'Auto-fix',
    subtitle: 'Nightly auto-fix batches, per-issue results and live status; revert or restore per issue',
    date: 'Date',
    refresh: '↻ Refresh',
    loading: 'Loading…',
    loadError: 'Failed to load. Please try again later.',
    empty: 'No auto-fix batches on this date.',
    batchTitle: 'Batch {date}',
    releaseStagesTitle: 'Release stages',
    noRuns: 'No runs in this batch.',
    scopeNote: 'A revert only guarantees no other commit is reverted; fixes that functionally depend on this one get a dependency check after the revert.',
    col: {
      issue: 'Issue',
      result: 'Result',
      live: 'Live status',
      action: 'Action',
    },
    fixTargetLabel: 'Fix target',
    stageLabel: 'Stage',
    handoffLabel: 'Handoff reason',
    labelSep: ': ',
    listSep: ', ',
    handoffDetail: {
      label: 'Why not auto-fixed',
      missingInput: 'Missing input ({field})',
      reason: {
        authorization: 'Authorization not passed',
        interrupted: 'Interrupted (restart or crash)',
        budget: 'Budget exhausted',
        per_issue_budget: 'Per-issue budget exhausted',
        post_diff_violation: 'Actual change exceeded authorization',
        failure_limit: 'Too many recent failures',
        rules_unavailable: 'Authorization rules unavailable',
        no_conductor_context: 'Platform context missing',
        arm_unavailable: 'Execution arm unavailable',
        arm_mismatch: 'Execution arm mismatch',
        root_touched: 'Main repository was modified',
        no_commit: 'No commit produced',
        workspace: 'Workspace setup failed',
        past_cutoff: 'Past the nightly cutoff',
        phase1_wrote: 'Research phase wrote files',
        no_proposal: 'No proposal produced',
        claim_lost: 'Proposal claim lost',
        phase2_failed: 'Implementation phase failed',
        acceptance: 'Acceptance check failed',
        internal_error: 'Internal error',
        ledger_unavailable: 'Cost ledger unavailable',
        post_claim_env: 'Environment problem after claim',
        test_report_invalid: 'Invalid test report',
        test_regression: 'Test regression',
        baseline_red: 'Baseline tests already failing',
      },
      code: {
        path_outside_verified_dirs: 'Change outside verified directories',
        denylist_path: 'Touches a denylisted path',
        maintenance_non_test_path: 'Test maintenance touches a non-test file',
        review_business_decision: 'Involves a business decision',
        review_blockers: 'Independent review has blockers',
        hard_lock_too_many_files: 'Too many files changed',
        hard_lock_core_path: 'Touches a core path',
        hard_lock_core_dimension: 'Touches a core risk dimension',
        review_not_approved: 'Independent review not approved',
        no_source_change: 'No source change',
        diff_outside_proposal: 'Change outside the proposal scope',
        empty_diff: 'Empty diff',
        added_files_not_subset: 'Added files outside the proposal',
        new_paths_not_subset: 'New files not within proposal files',
        rules_version_mismatch: 'Rules version mismatch',
        migration_path: 'Contains a database migration',
        test_suite_modification: 'Modifies an existing test',
        no_new_test: 'No new test',
        policy_not_covering: 'Not covered by a policy',
        policy_no_system_approval: 'Policy does not allow system approval',
        fix_target_not_platform: 'Fix target is not the platform',
        risk_tier_unconfirmed: 'Risk tier unconfirmed',
        tier_above_cap: 'Risk tier above the cap',
        locked: 'Issue is locked',
        f1_violation: 'Safety audit failed',
        diff_policy_not_covered: 'Change not covered by the policy',
      },
    },
    chip: {
      merged: 'In master',
      frontendLive: 'Frontend live',
      backendLive: 'Backend live',
    },
    mergedValue: {
      effective: 'Yes',
      reverted: 'Reverted',
      not_merged: 'No',
    },
    liveValue: {
      live: 'Yes',
      not_live: 'No',
      unknown: 'Cannot confirm',
    },
    liveText: {
      live: 'Live',
      not_live: 'Not live',
      unknown: 'Cannot confirm (treated as live)',
    },
    action: {
      revert: 'Revert',
      restore: 'Restore',
      unavailable: 'Revert unavailable',
      checking: 'Checking…',
      detailError: 'Could not read revert conditions',
    },
    reason: {
      not_implemented: 'Revert is not enabled yet',
      not_merged: 'Not merged into master yet',
      not_effective: 'Not currently effective',
      not_reverted: 'Not currently reverted',
      in_flight: 'A revert or restore is already in progress',
      needs_reconcile: 'Needs reconciliation; check git history manually',
      migration_refused: 'Contains a database migration; needs manual handling',
      live_unprovable: 'Cannot confirm whether it is live; check manually',
      stale_preview: 'Content changed; preview again',
      policy_revoked: 'Authorization changed',
      not_on_master: 'Target commit is not on master',
      member_busy: 'A selected issue already has a revert in progress',
      already_reverted: 'A selected issue is already reverted',
      busy: 'Another operation is in progress; retry shortly',
      wrong_batch: 'A selected issue does not belong to this batch',
      requires_confirm: 'This handoff reason needs an explicit confirmation',
      candidate_invalidated: 'The candidate is no longer valid; refresh and retry',
      wrong_state: 'The batch state does not allow this action',
      sha_mismatch: 'The candidate has changed; refresh and retry',
      other: 'Unavailable ({code})',
    },
    batchState: {
      open: 'Open',
      integrating: 'Integrating',
      merged: 'Merged into master',
      failed: 'Failed',
      skipped: 'Observe mode (no merge)',
    },
    releaseStage: {
      frontend_sg: 'Frontend (Singapore entry)',
      frontend_cn: 'Frontend (China entry)',
      backend_stage: 'Backend staging',
      backend_promote: 'Backend promotion',
    },
    releaseResult: {
      running: 'Running',
      succeeded: 'Succeeded',
      failed: 'Failed',
      skipped: 'Skipped',
      none: 'No record',
    },
    fixTarget: {
      conductor_platform: 'Platform code',
      project_env: 'Project environment',
      project_content: 'Project content',
      none: 'No fix needed',
      undetermined: 'Undetermined',
    },
    stage: {
      queued: 'Queued',
      investigating: 'Investigating',
      deciding: 'Deciding',
      implementing: 'Implementing',
      verifying: 'Verifying',
      awaiting_integration: 'Awaiting integration',
      integrated: 'Integrated',
      closed: 'Closed',
    },
    outcome: {
      pending: 'Pending',
      fixed: 'Fixed',
      no_change: 'No change needed',
      needs_human: 'Needs human',
      failed: 'Failed',
      skipped_budget: 'Skipped (budget)',
      observed_only: 'Observed only',
    },
    handoff: {
      none: 'None',
      migration: 'Database migration',
      frontend_change: 'Frontend change (never auto-merged)',
      revert_conflict: 'Revert conflict',
      root_conflict: 'Repo root conflict',
      test_failed: 'Tests failed',
      budget: 'Over budget',
      project_env_change: 'Project environment change order',
      needs_reconcile: 'Needs reconciliation',
      admin_excluded: 'Excluded by admin',
      reverted: 'Reverted',
    },
    execState: {
      pending: 'Queued',
      checking: 'Impact check',
      reverting: 'Reverting',
      testing: 'Core tests',
      merging: 'Merging into master',
      succeeded: 'Done',
      conflict: 'Conflict; handed to a human',
      test_failed: 'Tests failed; master unchanged',
      migration_refused: 'Database migration; refused',
      root_conflict: 'Repo root conflict; handed to a human',
      stale_preview: 'State changed; preview again',
      refused: 'Refused',
      needs_reconcile: 'Needs reconciliation',
      failed: 'Failed',
    },
    dialog: {
      previewNote: 'Impact preview (re-checked at execution time)',
      titleRevert: 'Revert the fix for {id}',
      titleRestore: 'Restore the fix for {id}',
      targetSha: 'Target commit',
      files: 'Touched files',
      filesCount: '{n} (frontend: {fe})',
      migration: 'Database migration',
      migrationYes: 'Yes (cannot auto-revert)',
      migrationNo: 'No',
      later: 'Later commits touching the same files',
      laterCount: '{n}, may conflict',
      laterNone: 'None',
      frontend: 'Frontend',
      backend: 'Backend',
      shipTitle: 'When to ship afterwards',
      shipNext: 'With the next 03:00 release (frontend goes live directly, backend waits for your switch)',
      shipImmediate: 'Ship now',
      immediateWarn: 'Shipping now rebuilds both frontends immediately and stages the backend (you still switch it), and it ships ALL changes currently merged into master, not only this revert.',
      staleRefreshed: 'Content changed; the preview has been refreshed',
      notAllowed: 'Cannot run now: {reason}',
      failNote: 'On conflict, test failure or state change master stays unchanged and the item moves to "Needs human" or asks for a new preview.',
      cancel: 'Cancel',
      close: 'Close',
      confirmRevert: 'Confirm revert',
      confirmRestore: 'Confirm restore',
      submitting: 'Submitting…',
      progress: 'Progress',
      stepShip: 'Ship',
      opId: 'Operation {id}',
      done: 'Done, result commit {sha}',
      failed: 'Not completed: {state}',
      errorDetail: 'Error: {error}',
      requestError: 'Submit failed: {message}',
      previewError: 'Failed to load the preview. Please try again later.',
      loadingPreview: 'Building preview…',
      pollError: 'Progress temporarily unavailable; retrying',
      pollGaveUp: 'Unable to get progress. Please refresh later.',
      pollRetry: 'Retry',
      releaseTitle: 'Release records',
      releaseNext: 'Ships with the next 03:00 release',
    },
    handoffs: {
      title: 'Auto-fix handoffs',
      sub: 'Items auto-fix handed to humans, including project environment change orders for admins to execute',
      empty: 'No auto-fix handoffs.',
      loadError: 'Failed to load handoffs. Please try again later.',
      createdAt: 'Handed off',
      changeOrder: 'Change order',
      project: 'Project',
      summary: 'Summary',
      steps: 'Steps',
    },
    batchRevert: {
      title: 'Revert {n} items',
      order: 'Revert order (newest first)',
      tests: 'Tests after revert',
      testsPaired: 'Related tests only: {n} + {r} red-line tests',
      testsFull: 'Full test suite',
      testsMore: '… {n} more',
      afterNote: 'After the revert these issues go to manual handling (reason: Reverted) and are not auto-fixed again.',
      allOrNothing: 'All or nothing: either every item is reverted or none is; any failure abandons the whole group.',
      confirm: 'Revert {n} items',
      blocked: {
        busy: 'Operation in progress',
        migration: 'Database migration',
        already_reverted: 'Already reverted',
        wrong_batch: 'Not in this batch',
      },
    },
    requeue: {
      button: 'Requeue',
      confirmText: 'Register this issue for one more try in the next nightly run (one time only). It is still subject to the authorization policy, failure limit and daily count limit, so it may not run. Requeue now?',
      confirm: 'Confirm requeue',
      adminConfirmText: 'This issue was handed off by an admin action ({reason}). Requeuing lets the next nightly run auto-fix it again, which may re-merge the change that was just excluded or reverted. Requeue anyway?',
      cancel: 'Cancel',
      sending: 'Submitting…',
      pending: 'Requeued, waiting for the next nightly run (still subject to failure and daily count limits)',
      consumed: 'Last requeue has been used',
      success: 'Requeued; it will be retried in the next nightly run',
      error: {
        already_requeued: 'This issue is already requeued and waiting for the next nightly run',
        issue_not_open: 'This issue is no longer open and cannot be requeued',
        no_handoff: 'This issue has no handoff to requeue',
        requires_confirm: 'This issue was excluded or reverted by an admin; confirm again to requeue it',
        generic: 'Requeue failed. Please try again later.',
      },
    },
    riskTier: {
      '1': 'Low',
      '2': 'Medium-low',
      '3': 'Needs review',
      '4': 'Human only',
    },
    status: {
      title: 'Pipeline status',
      loadError: 'Failed to load pipeline status. Please try again later.',
      enabled: 'Master switch',
      on: 'On',
      off: 'Off',
      dailyLimit: 'Daily limit',
      riskCap: 'System risk cap',
      riskCapEffective: 'Risk cap',
      riskCapLine: 'policy {policy} · ceiling R{ceiling} · effective {effective}',
      riskCapNoPolicy: 'no active policy (ceiling R{ceiling})',
      breaker: 'Breaker',
      breakerOff: 'Tripped (paused)',
      breakerOk: 'Normal',
      activePolicy: 'Active policy',
      noPolicy: 'None',
      policyRef: 'v{version} · risk ≤ R{tier}',
      lastRun: 'Last run',
      noHistory: 'No runs recorded yet.',
      observed: 'Observed {n}',
      skipped: 'Skipped: {list}',
      skipItem: '{label} {n}',
      hintBreaker: 'The runtime breaker is tripped; nothing will be processed tonight.',
      hintLimit: 'The daily limit is 0; nothing will be processed tonight.',
      hintDisabled: 'The master switch (AUTO_EXEC_ENABLED) is off; nothing will be processed tonight.',
      hintNoPolicy: 'No authorization policy is in force; nothing will be processed tonight. Create a version under "Authorization policies" below.',
      observeNote: 'Observe mode: only records which issues would be processed — no code changes, no model calls, zero cost.',
      historyTitle: 'Recent runs (up to 14)',
      col: {
        ranAt: 'Ran at',
        trigger: 'Trigger',
        batch: 'Batch date',
        result: 'Result',
      },
    },
    trigger: {
      scheduled: 'Scheduled',
      catch_up: 'Catch-up',
    },
    stopReason: {
      paused: 'Paused (master switch off)',
      redis_unavailable: 'Stopped: Redis unavailable',
      breaker: 'Stopped: breaker tripped',
      ledger_unavailable: 'Stopped: cost ledger unavailable',
      no_policy: 'Not run: no active policy',
      outside_window: 'Outside the nightly window',
      past_cutoff: 'Stopped: past cutoff',
      budget: 'Stopped: budget exhausted',
      batch_closed: "Tonight's batch already closed",
      gate_parked_slot: 'Not run: inactive slot',
      gate_not_production: 'Not run: not production',
      gate_already_applied: 'Not run: already ran tonight',
      assistant_daily_limit: 'Nightly assistant limit reached',
      assistant_pass_locked: 'Another assistant pass holds the lock',
      rules_unavailable: 'Rules file unavailable',
      no_conductor_context: 'Conductor project context missing',
      arm_unavailable: 'Codex arm unavailable',
      gate_draining_slot: 'Draining slot',
      exception: 'Run failed with an error',
      other: 'Stopped ({code})',
    },
    skipReason: {
      fix_target_unknown: 'Unknown fix target',
      fix_target_undetermined: 'Fix target undetermined',
      fix_target_none: 'No fix needed',
      fix_target_project_content: 'Project content',
      fix_target_out_of_policy_scope: 'Outside policy scope',
      risk_tier_above_policy: 'Risk tier above cap',
      review_missing: 'Not reviewed by strong model',
      review_stale: 'Review stale',
      review_not_actionable: 'Review verdict not actionable',
      review_tier_above_cap: 'Review tier above cap',
    },
    review: {
      title: 'Strong-model review',
      none: 'Not reviewed yet',
      tier: 'Review tier',
      modelTier: 'Model proposed R{tier}',
      floorApplied: 'Raised by rule',
      floorTitle: 'A rule floor is above the model judgment; the review tier was raised',
      verdictLabel: 'Verdict',
      valueScore: 'Value score',
      duplicateOf: 'Duplicate of',
      verifiedPaths: 'Verified paths',
      rationale: 'Rationale',
      dimensions: 'Risk dimensions',
      model: 'Model',
      reviewedAt: 'Reviewed at',
      reference: 'Strong-model review',
      referenceNote: 'reference only, never auto-submitted',
      filter: 'Review verdict',
      filterAll: 'All review verdicts',
      filterNone: 'Not reviewed',
      sortValue: 'By value score',
      sortValueTitle: 'Sort by strong-model value score (unreviewed last); click again to flip',
      verdict: {
        actionable: 'Actionable',
        already_fixed: 'Already fixed',
        duplicate: 'Duplicate',
        need_info: 'Needs info',
        out_of_scope: 'Out of scope',
      },
    },
    reviewStatus: {
      title: 'Strong-model review',
      on: 'On',
      off: 'Off',
      gate: 'Execution gate',
      gateOn: 'Review required',
      gateOff: 'Not required',
      lastRun: 'Last run',
      noRun: 'No review runs yet',
      runSummary: '{date} · wrote {written}/{selected}',
      cost: 'cost ${cost}',
      costUnknown: '{n} batch(es) cost unknown',
      queue: 'Executable queue',
      byVerdict: 'Verdicts (last {days} days)',
      agreeConfirmed: 'Agreement with formal confirmation',
      agreeSuggestion: 'Suggestion vs review agreement',
      agreeByKind: 'Agreement by confirmation source',
      agreeKindItem: '{kind} {rate}',
      autoLocked: 'machine-confirmed {auto} · locked {locked}',
      rate: '{rate} (n={total})',
      hintGateOnly: 'Review is off: the nightly assistant will receive no candidates (gate only tightens)',
      stopReason: {
        review_cost_ceiling: 'Review cost ceiling reached, stopped',
        budget_unavailable: 'Budget unavailable, stopped',
        disabled: 'Review disabled',
        parked: 'Inactive slot, not run',
        no_rows: 'Nothing to review',
        other: 'Stopped ({code})',
      },
    },
    nightly: {
      title: 'Nightly assistant',
      on: 'On',
      off: 'Off (default)',
      dailyLimit: 'Daily limit',
      window: 'Last {days} days',
      counts: 'proposals {proposals} · system-approved {approved} · merged {merged} · handed to human {needsHuman}',
      degraded: 'Counts unavailable',
    },
    policy: {
      title: 'Authorization policies',
      sub: 'Each create issues a new version; the newest version in force decides what runs at night.',
      loadError: 'Failed to load policies. Please try again later.',
      empty: 'No authorization policies yet.',
      version: 'v{version}',
      targets: 'Fix targets',
      maxTier: 'Risk cap',
      globs: 'Path scope',
      globsAll: 'Any',
      validRange: 'Valid',
      budget: 'Budget',
      budgetValue: 'Daily ${daily} · per issue ${perIssue}',
      budgetGlobal: 'Global (system constant)',
      budgetSnapshot: 'Budget (recorded at creation; the global budget applies)',
      budgetBreaker: 'Auto-pause after {days} consecutive days each spending over ${spend} with no successful fix',
      budgetTodayLocked: "Today's limit ${limit} (locked; the new limit applies from the next night)",
      budgetNote: 'Budgets are global system constants and do not change per policy. They are checked against reservations before each step starts: once spent plus reserved reaches the limit, no NEW step starts, but a started step is not stopped by the budget, so the final settled cost can exceed the limit (not a hard cap). An interrupted step is charged its full reservation.',
      active: 'Active',
      revoked: 'Revoked',
      inactive: 'Not in force',
      revoke: 'Revoke',
      revokeTitle: 'Revoke policy v{version}?',
      revokeBody: 'The version stops immediately; older versions are not revived. Create a new version if needed.',
      revokeConfirm: 'Confirm revoke',
      create: 'New policy',
      createTitle: 'New authorization policy',
      fixTargets: 'Allowed fix targets (at least one)',
      maxRiskTier: 'Risk cap',
      tierAuthority: 'This policy decides (system ceiling R{ceiling})',
      tierAboveCeiling: 'Above the system ceiling; R{ceiling} applies',
      pathGlobs: 'Path scope (one per line, optional, up to {max})',
      validDays: 'Valid days ({min}–{max})',
      submit: 'Next: confirm',
      confirmTitle: 'Confirm new policy',
      confirmBody: 'Creates v{version}: fix targets {targets}, risk ≤ R{tier}, paths {globs}, valid for {days} days. Older versions stop once it is in force.',
      confirmSubmit: 'Confirm create',
      cancel: 'Cancel',
      submitting: 'Submitting…',
      errTargets: 'Select at least one fix target.',
      errGlobs: 'At most {max} path entries.',
      errDays: 'Valid days must be between {min} and {max}.',
      requestError: 'Submit failed: {message}',
      systemApproval: 'System approval',
      allowed: 'allowed',
      notAllowed: 'not allowed',
      systemApprovalOn: 'system approval allowed',
      allowSystemApproval: 'Allow system approval (nightly assistant)',
      allowSystemApprovalNote: 'When checked, the nightly assistant may approve and run proposals within this policy on its own; unchecked, every proposal goes to a human.',
      confirmSystemApproval: 'This version lets the nightly assistant system-approve.',
      decisionAutonomy: 'Autonomous decisions',
      allowDecisionAutonomy: 'Allow autonomous decisions (nightly assistant adopts business trade-offs)',
      allowDecisionAutonomyNote: 'When checked with decision types selected, the nightly assistant may adopt those business trade-offs within this policy on its own; unchecked, every business trade-off goes to a human. After an admin revert or exclusion, the same issue + type is never auto-adopted again.',
      decisionOps: 'Decision types it may adopt',
      decisionOp: { ui_text: 'UI text' },
      confirmDecisionAutonomy: 'This version lets the nightly assistant adopt the selected business trade-offs on its own.',
    },
    risk: {
      title: 'Risk tier',
      formal: 'Formal tier',
      unset: 'Not set',
      suggested: 'Suggested tier',
      noSuggestion: 'No automatic suggestion yet (generated by nightly triage).',
      rationale: 'Rationale',
      dimensions: 'Risk dimensions',
      likelyPaths: 'Likely paths',
      floorApplied: 'Raised by rule floor',
      floorTitle: 'A rule floor was above the model suggestion, so the tier was raised',
      fixTarget: 'Fix target',
      confirmed: 'Confirmed: {by} · {at}',
      notConfirmed: 'Not confirmed',
      byMachine: 'strong-model review',
      kind: {
        human: 'Human-confirmed',
        machine: 'Machine-confirmed',
        legacy_agent: 'Legacy confirm',
      },
      kindShort: {
        human: 'H',
        machine: 'M',
        legacy_agent: 'L',
      },
      kindFilter: 'Confirmation source',
      kindFilterAll: 'All confirmation sources',
      kindFilterNone: 'Not confirmed',
      lockFilter: 'Locked',
      lockFilterAll: 'All lock states',
      lockFilterYes: 'Locked',
      lockFilterNo: 'Not locked',
      lockLabel: 'Hard lock',
      lockBadge: 'Hard-locked R4',
      lockShort: 'Lock',
      lockNote: 'Hard-lock rule hit: can only be confirmed as R4.',
      lockError: 'This issue is hard-locked to R4 ({reasons}); a lower tier cannot be confirmed.',
      lockReason: {
        core_path: 'core path',
        core_dimension: 'core risk dimension',
        too_many_files: 'too many files changed',
      },
      confirmSuggested: 'Confirm suggested R{tier}',
      chooseOther: 'or choose',
      confirming: 'Submitting…',
      confirmError: 'Confirm failed. Please try again later.',
      bulkButton: 'Confirm risk tier',
      bulkTitle: 'Bulk confirm risk tier',
      bulkBody: "Each row defaults to its suggested tier; rows without a suggestion must be picked manually. Nothing is sent until you confirm.",
      bulkPick: 'Pick',
      bulkNoSuggestion: 'No suggestion',
      bulkSubmit: 'Confirm ({n})',
      bulkMissing: '{n} rows have no tier selected.',
      bulkTooMany: 'At most {max} rows per confirm; reduce the selection.',
      bulkResult: 'Confirmed {updated} · failed {failed}',
      bulkFailed: 'Failed: {list}',
      bulkError: 'Bulk confirm failed. Please try again later.',
      failItem: '{id} ({reason})',
      close: 'Close',
      failReason: {
        not_found: 'not found',
        error: 'error',
        risk_tier_locked: 'hard-locked R4, cannot lower',
      },
    },
  },
} as const;

export default en;
